/* Carry Hope Bulk & Custom Orders builder.
 * All prices, tiers, surcharges, minimums, and the custom-quote threshold come from ONE
 * config object (shop metafield carry_hope.bulk_pricing, serialized into the page).
 * Never add a price number to this file.
 * Size prices (Amber, Oct 3 2026): Step 4 shows each garment size's price per piece under its box (tier rate of that
 * category + size_surcharges, one print location included), plus the average estimated price and the order total.
 * Placement add-ons are explained as flat per-piece amounts. The internal request email keeps the full breakdown.
 * Artwork (Step 5 "Placement & Artwork" since round 2, Oct 2026) is chosen per print location: Upload (PNG/JPG, stored privately in Carry Hope's
 * Google Drive through the upload helper whose /exec URL is a data attribute on the section), Canva
 * (upload the export or paste a share link), or Help Me Design It. Uploads are tied to the request
 * number, which is created before the first upload and reused at submit.
 * Colors (Oct 2026): an order can mix several garment colors (Step 3, multi-select + typed "Another color"s), each with
 * its own size breakdown (Step 4). The price tier always comes from the COMBINED quantity across all colors.
 * Rush (config v4+): a need-by date within config.rush_business_days business days (weekends + config.rush_closed_dates
 * excluded) adds the flat config.rush_fees fee for the combined quantity; custom-quote orders get manual rush review.
 * A config without rush keys simply has no rush fee.
 * Automatic print sizing (config.auto_print_sizing + products[].print_size_group, Amber Oct 2 2026): the customer never
 * chooses a print size. Each garment size gets its main (front/back) and left-chest print size from the config, and each
 * size's pieces are priced at their own print size (main surcharge = config.print_sizes[price_as].surcharge; left-chest
 * sizes carry no surcharge). Bulk products/configs without those keys keep the customer-chosen print sizes. The 1–9
 * page always shows the sizing note only (its config copy carries auto_print_sizing with every print-size charge zeroed).
 * Product families (config v9+, products[].categories, Amber Oct 3 2026): one product card (e.g. "Gildan Tees") holds
 * categories (Adult / Youth / Toddler). Step 2 takes a quantity per category; the COMBINED family quantity sets the tier,
 * the minimum, Custom Volume Pricing and the rush fee; each category is priced at its own rate in that tier and keeps
 * its own colors (+ its own "Another color"), sizes and print-size group. Keys: first category = plain color name /
 * 'custom:<id>' (exactly as before families), other categories '<catId>|<name>' / '<catId>|custom:<id>'.
 * Modes: data-mode="bulk" (10+ pieces, priced) or data-mode="custom" (1–9 pieces, page /pages/custom-orders):
 * same steps, families, automatic sizing and artwork uploads. With config.custom_estimate (sent by the 1–9 section,
 * Amber Oct 3 2026) it shows the same live estimate as Bulk at ONE 1–9 price per product/category (custom_page_price)
 * + the Bulk add-ons and size prices + the flat 1–9 rush fee — never tiers, ladders, nudges or Custom Volume Pricing.
 * Without the flag the 1–9 page shows no prices (quote by email). The COMBINED total is capped at 9.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Pricing engine: pure functions over the config.                     */
  /* ------------------------------------------------------------------ */
  var Engine = (function () {
    function toCents(dollars) { return Math.round(Number(dollars || 0) * 100); }
    function findById(list, id) {
      for (var i = 0; i < (list || []).length; i++) { if (list[i].id === id) return list[i]; }
      return null;
    }
    function product(config, id) { return findById(config.products, id); }
    function placement(config, id) { return findById(config.placements, id); }
    function printSize(config, id) { return findById(config.print_sizes, id); }

    // Sizes shown for a garment in a given color. A listed color may narrow the product's size
    // list to its commonly available sizes (e.g. some crewneck colors usually stop at 3XL).
    // "__other" (customer-typed color) ALWAYS gets the full list so the customer's real sizes are
    // preserved; availability is confirmed at review. With no color chosen, the full list is returned.
    function sizesFor(prod, colorName) {
      if (!prod) return [];
      var all = prod.sizes || [];
      if (!all.length) return [];
      var list = null;
      if (colorName && colorName !== '__other') {
        for (var i = 0; i < (prod.colors || []).length; i++) {
          if (prod.colors[i].name === colorName) { list = prod.colors[i].sizes; break; }
        }
      }
      if (!list || !list.length) return all.slice();
      return all.filter(function (s) { return list.indexOf(s) !== -1; });
    }

    function sortedTiers(prod) {
      return (prod.tiers || []).slice().sort(function (a, b) { return a.min - b.min; });
    }

    function tierFor(config, prod, qty) {
      if (!prod) return { status: 'no_product' };
      if (!Number.isInteger(qty) || qty < config.minimum_quantity) return { status: 'below_minimum' };
      if (qty >= config.custom_quote_threshold) return { status: 'custom_quote' };
      var tiers = sortedTiers(prod);
      for (var i = 0; i < tiers.length; i++) {
        var t = tiers[i];
        if (qty >= t.min && (t.max === null || t.max === undefined || qty <= t.max)) {
          return { status: 'priced', tier: t, index: i };
        }
      }
      return { status: 'unpriced' };
    }

    function nextBreak(config, prod, qty) {
      if (!prod || !Number.isInteger(qty) || qty < config.minimum_quantity || qty >= config.custom_quote_threshold) return null;
      var tiers = sortedTiers(prod);
      var target = null;
      for (var i = 0; i < tiers.length; i++) {
        if (tiers[i].min > qty) { target = { type: 'tier', min: tiers[i].min, price: tiers[i].price }; break; }
      }
      if (!target) target = { type: 'custom_quote', min: config.custom_quote_threshold };
      target.remaining = target.min - qty;
      var window_ = Math.max(1, Math.floor(target.min * Number(config.next_tier_nudge_ratio || 0)));
      target.nudge = target.remaining <= window_;
      return target;
    }

    function startingPrice(config, prod) {
      var min = null;
      (prod.tiers || []).forEach(function (t) {
        if (t.min < config.custom_quote_threshold && (min === null || t.price < min)) min = t.price;
      });
      return min;
    }

    // {cfg, group} when the product's print size is set automatically from its garment sizes, else null.
    function autoSizing(config, prod) {
      var a = config && config.auto_print_sizing;
      if (!a || !prod || !prod.print_size_group || !a.groups || !a.groups[prod.print_size_group]) return null;
      return { cfg: a, group: a.groups[prod.print_size_group] };
    }
    // Main (front/back) or left-chest print size for one garment size: {id, label, eachCents} or null when unmapped.
    // `loc` (optional, 'Front' / 'Back'): a group may map that location separately (hoodies: the front sits above the
    // pouch pocket, so `front` differs from `back`); groups without a per-location map use `main` for every location.
    function autoDimension(config, prod, kind, size, loc) {
      var auto = autoSizing(config, prod);
      if (!auto) return null;
      var mapKey = kind;
      if (kind === 'main' && loc && auto.group[String(loc).toLowerCase()]) mapKey = String(loc).toLowerCase();
      var dimId = (auto.group[mapKey] || {})[size];
      var dim = findById(kind === 'main' ? auto.cfg.main_dimensions : auto.cfg.left_chest_dimensions, dimId);
      if (!dim) return null;
      var ps = kind === 'main' ? printSize(config, dim.price_as) : null;
      return { id: dim.id, label: dim.label, eachCents: ps ? toCents(ps.surcharge) : 0 };
    }

    function printCharges(config, placementId, printSizes, prod) {
      var pl = placement(config, placementId);
      if (!pl) return { valid: false, perItemCents: 0, lines: [], locations: [], sizes: [] };
      var extra = config.additional_location_charges || {};
      var auto = autoSizing(config, prod);
      var lines = [];
      var sizes = [];
      var valid = true;
      (pl.full_prints || []).forEach(function (loc, i) {
        if (auto) {
          // Print size comes from each garment size (priced per size in quote()); only the location charge is per item.
          sizes.push({ location: loc, id: 'auto', label: 'Sized to each garment', auto: true });
          if (i > 0) lines.push({ label: loc + ' — second full-size print', cents: toCents(extra.second_full_print) });
          return;
        }
        var sizeId = (printSizes && printSizes[loc]) || config.print_sizes[0].id;
        var ps = printSize(config, sizeId);
        if (!ps) { valid = false; return; }
        sizes.push({ location: loc, id: ps.id, label: ps.label });
        if (i > 0) lines.push({ label: loc + ' — second full-size print', cents: toCents(extra.second_full_print) });
        if (toCents(ps.surcharge) > 0) lines.push({ label: loc + ' — ' + ps.label + ' print size', cents: toCents(ps.surcharge) });
      });
      if (pl.left_chest) {
        // Only Bulk's config still carries a left_chest label; without one (the 1–9 page, Oct 3 2026) left chest is sized automatically.
        var lcLabel = config.left_chest && config.left_chest.label;
        var lcAuto = auto || !lcLabel;
        if (lcAuto) sizes.push({ location: 'Left Chest', id: 'auto', label: 'Sized to each garment', auto: true });
        else sizes.push({ location: 'Left Chest', id: '4x4', label: lcLabel });
        if ((pl.full_prints || []).length > 0) lines.push({ label: lcAuto ? 'Left chest print' : 'Left chest ' + lcLabel + ' print', cents: toCents(extra.left_chest) });
      }
      var perItem = lines.reduce(function (s, l) { return s + l.cents; }, 0);
      var locations = (pl.full_prints || []).slice();
      if (pl.left_chest) locations.unshift('Left Chest');
      return { valid: valid, placement: pl, perItemCents: perItem, lines: lines, locations: locations, sizes: sizes, auto: !!auto };
    }

    // Automatic print sizing: one row per print location × garment size on the order, each priced at its own print size.
    // Pieces not yet given a size (while Step 4 is incomplete) carry no print-size charge until they are assigned.
    function autoPrintRows(config, prod, pl, counts, qty) {
      var out = { rows: [], cents: 0, unassigned: 0, unmapped: [] };
      var assigned = counts.reduce(function (t, c) { return t + c[1]; }, 0);
      out.unassigned = Math.max(0, qty - assigned);
      var locs = (pl.full_prints || []).map(function (loc) { return { loc: loc, kind: 'main' }; });
      if (pl.left_chest) locs.unshift({ loc: 'Left Chest', kind: 'left_chest' });
      locs.forEach(function (L) {
        counts.forEach(function (c) {
          var dim = autoDimension(config, prod, L.kind, c[0], L.loc);
          if (!dim) out.unmapped.push(L.loc + ' ' + c[0]);
          var each = dim ? dim.eachCents : 0;
          out.rows.push({ location: L.loc, kind: L.kind, size: c[0], qty: c[1], dimId: dim ? dim.id : '', label: dim ? dim.label : 'TO CONFIRM',
            eachCents: each, cents: each * c[1] });
          out.cents += each * c[1];
        });
      });
      return out;
    }

    // ---- Product families (config v9+, Amber Oct 3 2026) ----
    // A product may list `categories` (Gildan Tees: Adult / Youth / Toddler; Comfort Colors: Adult / Youth). Each category
    // inherits the product's fields and may override the ones in CAT_FIELDS. The FIRST category is the product itself
    // (adult), so a family order with only that category prices exactly like the product did before families existed.
    var CAT_FIELDS = ['name', 'sizes', 'size_surcharges', 'print_size_group', 'colors', 'tiers', 'unit_singular', 'unit_plural'];
    function categoriesOf(prod) { return prod && Array.isArray(prod.categories) && prod.categories.length ? prod.categories : null; }
    // A product-like object for one category (sizesFor / autoSizing / tiers work on it unchanged); null if unknown.
    function categoryProduct(prod, catId) {
      var cats = categoriesOf(prod);
      if (!cats) return prod;
      var cat = findById(cats, catId || cats[0].id);
      if (!cat) return null;
      var out = {};
      Object.keys(prod).forEach(function (k) { if (k !== 'categories') out[k] = prod[k]; });
      CAT_FIELDS.forEach(function (k) { if (cat[k] !== undefined && cat[k] !== null) out[k] = cat[k]; });
      out.categoryId = cat.id;
      out.categoryLabel = cat.label || cat.id;
      return out;
    }
    // Price of one category at the family's tier index (category ladders share the product's min/max breaks).
    function categoryTierPrice(cp, index) {
      var t = sortedTiers(cp)[index];
      return t ? t.price : null;
    }

    // Garment colors on the order. Callers pass sel.colors = [{key, name, custom, sizes, category}] (one entry per color,
    // each with its own size breakdown; category = family category id, optional); older callers pass sel.color
    // (+ sel.customColor) and sel.sizes for a single color.
    function colorList(sel) {
      if (Array.isArray(sel.colors)) {
        return sel.colors.map(function (c) {
          c = c || {};
          return { key: String(c.key || c.name || ''), name: String(c.name || ''), custom: !!c.custom, sizes: c.sizes || {},
            category: c.category ? String(c.category) : '' };
        });
      }
      var other = sel.color === '__other';
      return [{ key: String(sel.color || ''), name: other ? String(sel.customColor || '') : String(sel.color || ''), custom: other, sizes: sel.sizes || {}, category: '' }];
    }
    function count(v) { var n = parseInt(v, 10); return isNaN(n) || n < 0 ? 0 : n; }

    // Family quantities: {catId: qty} for the categories on the order (config order, qty > 0 or carrying a color).
    // sel.categoryQuantities (what the builder sends) wins; otherwise one category takes the whole quantity, or, when the
    // colors span several categories, each category gets the pieces assigned to its colors.
    function familyQuantities(cats, sel, colors, qty) {
      var out = {};
      var given = sel.categoryQuantities && typeof sel.categoryQuantities === 'object' ? sel.categoryQuantities : null;
      if (given) {
        cats.forEach(function (c) { var n = count(given[c.id]); if (n > 0) out[c.id] = n; });
        return out;
      }
      var used = {};
      colors.forEach(function (c) { used[c.category || cats[0].id] = true; });
      var ids = cats.map(function (c) { return c.id; }).filter(function (id) { return used[id]; });
      if (ids.length <= 1) { out[ids[0] || cats[0].id] = qty; return out; }
      return null;   // derived from the colors' size totals in quote()
    }

    // Size rows for one set of colors priced against one product (or category): mutates `acc` with per-size totals.
    function addColorRows(prod, colors, qty, acc, catId, catLabel) {
      var surcharges = prod.size_surcharges || {};
      colors.forEach(function (c) {
        // Only sizes offered for this color are counted or charged ("Another color" always gets the full list).
        var offered = sizesFor(prod, c.custom ? '__other' : c.name);
        var row = { key: c.key, name: c.name, custom: c.custom, offered: offered, sizeRows: [], total: 0, extendedLines: [], extendedCents: 0,
          category: catId || '', categoryLabel: catLabel || '' };
        if (offered.length) {
          offered.forEach(function (size) {
            acc.offered[size] = true;
            var n = count(c.sizes[size]);
            row.total += n;
            if (n > 0) { row.sizeRows.push({ size: size, qty: n }); acc.bySize[size] = (acc.bySize[size] || 0) + n; }
            var each = toCents(surcharges[size]);
            if (n > 0 && each > 0) {
              row.extendedLines.push({ size: size, qty: n, eachCents: each, cents: each * n });
              row.extendedCents += each * n;
              acc.extBySize[size] = (acc.extBySize[size] || 0) + n;
            }
          });
        } else {
          // One-size product (totes): one color takes the whole quantity; several colors each carry their own count.
          row.total = colors.length > 1 ? count(c.sizes['One size']) : qty;
          if (row.total > 0) row.sizeRows.push({ size: 'One size', qty: row.total });
        }
        acc.total += row.total;
        acc.extendedCents += row.extendedCents;
        acc.rows.push(row);
      });
    }
    function sizeSummary(prod, acc, colorsLength) {
      var out = { sizeRows: [], extendedLines: [], offeredSizes: [] };
      var surcharges = prod.size_surcharges || {};
      var ladder = prod.sizes || [];
      if (ladder.length) {
        ladder.forEach(function (size) {
          if (acc.offered[size]) out.offeredSizes.push(size);
          if (acc.bySize[size]) out.sizeRows.push({ size: size, qty: acc.bySize[size] });
          if (acc.extBySize[size]) {
            var each = toCents(surcharges[size]);
            out.extendedLines.push({ size: size, qty: acc.extBySize[size], eachCents: each, cents: each * acc.extBySize[size] });
          }
        });
      } else if (colorsLength) {
        out.sizeRows.push({ size: 'One size', qty: acc.total });
      }
      return out;
    }
    function newAcc() { return { rows: [], total: 0, extendedCents: 0, bySize: {}, extBySize: {}, offered: {} }; }

    // The tier ALWAYS comes from the combined quantity (sel.quantity) — e.g. Black 50 + Natural 30 + Light Pink 20 is
    // priced as one 100-piece order. Extended-size charges are added per color, for the sizes offered in that color.
    // Families: the combined family quantity (all categories) picks ONE tier; each category's pieces are priced at that
    // category's own price in that tier (Adult 50 × adult rate + Youth 50 × youth rate at 100 pieces). Quantities are never averaged.
    function quote(config, sel) {
      sel = sel || {};
      var prod = product(config, sel.productId);
      var cats = categoriesOf(prod);
      var colors = colorList(sel);
      var qty = parseInt(sel.quantity, 10);
      if (isNaN(qty)) qty = 0;
      var famQty = cats ? familyQuantities(cats, sel, colors, qty) : null;
      if (cats && famQty && sel.categoryQuantities) qty = Object.keys(famQty).reduce(function (t, k) { return t + famQty[k]; }, 0);
      var tier = tierFor(config, prod, qty);
      var result = {
        product: prod, quantity: qty, tierStatus: tier.status, tier: tier.tier || null, tierIndex: tier.status === 'priced' ? tier.index : null,
        customQuote: tier.status === 'custom_quote', priced: tier.status === 'priced',
        family: !!cats, categoryRows: [],
        sizeTotal: 0, sizeRows: [], extendedLines: [], extendedCents: 0, offeredSizes: [], colorRows: [],
        print: printCharges(config, sel.placementId, sel.printSizes, prod),
        autoPrint: null,
        unitBaseCents: null, baseCents: null, printCents: null, subtotalCents: null, averagePerPieceCents: null,
        next: nextBreak(config, prod, qty)
      };
      if (!prod) return result;
      if (!cats) {
        var acc = newAcc();
        addColorRows(prod, colors, qty, acc);
        var summ = sizeSummary(prod, acc, colors.length);
        result.colorRows = acc.rows;
        result.sizeTotal = acc.total;
        result.extendedCents = acc.extendedCents;
        result.sizeRows = summ.sizeRows;
        result.extendedLines = summ.extendedLines;
        result.offeredSizes = summ.offeredSizes;
        if (result.print.auto && result.print.placement) {
          result.autoPrint = autoPrintRows(config, prod, result.print.placement,
            result.sizeRows.filter(function (r) { return r.qty > 0; }).map(function (r) { return [r.size, r.qty]; }), qty);
        }
        if (result.priced) {
          result.unitBaseCents = toCents(tier.tier.price);
          result.baseCents = result.unitBaseCents * qty;
        }
      } else {
        var auto = result.print.auto && result.print.placement;
        if (auto) result.autoPrint = { rows: [], cents: 0, unassigned: 0, unmapped: [] };
        var seenSize = {};
        cats.forEach(function (cat) {
          var mine = colors.filter(function (c) { return (c.category || cats[0].id) === cat.id; });
          var catQty = famQty ? (famQty[cat.id] || 0) : null;
          if (!mine.length && !catQty) return;
          var cp = categoryProduct(prod, cat.id);
          var cacc = newAcc();
          addColorRows(cp, mine, catQty === null ? 0 : catQty, cacc, cat.id, cp.categoryLabel);
          if (catQty === null) catQty = cacc.total;
          var cs = sizeSummary(cp, cacc, mine.length);
          var crow = { id: cat.id, label: cp.categoryLabel, name: cp.name, product: cp, quantity: catQty, colorRows: cacc.rows,
            sizeTotal: cacc.total, sizeRows: cs.sizeRows, extendedLines: cs.extendedLines, extendedCents: cacc.extendedCents,
            unitCents: null, baseCents: null, autoPrintCents: 0, autoPrintRows: [] };
          if (auto) {
            var ap = autoPrintRows(config, cp, result.print.placement,
              cs.sizeRows.filter(function (r) { return r.qty > 0; }).map(function (r) { return [r.size, r.qty]; }), catQty);
            ap.rows.forEach(function (r) { r.category = cat.id; r.categoryLabel = cp.categoryLabel; });
            crow.autoPrintRows = ap.rows;
            crow.autoPrintCents = ap.cents;
            result.autoPrint.rows = result.autoPrint.rows.concat(ap.rows);
            result.autoPrint.cents += ap.cents;
            result.autoPrint.unmapped = result.autoPrint.unmapped.concat(ap.unmapped.map(function (u) { return cp.categoryLabel + ' ' + u; }));
          }
          if (result.priced) {
            var price = categoryTierPrice(cp, tier.index);
            crow.unitCents = price === null ? null : toCents(price);
            crow.baseCents = crow.unitCents === null ? null : crow.unitCents * catQty;
          }
          result.categoryRows.push(crow);
          result.colorRows = result.colorRows.concat(cacc.rows);
          result.sizeTotal += cacc.total;
          result.extendedCents += cacc.extendedCents;
          cs.sizeRows.forEach(function (r) { result.sizeRows.push({ size: r.size, qty: r.qty, category: cat.id }); });
          cs.extendedLines.forEach(function (l) { result.extendedLines.push({ size: l.size, qty: l.qty, eachCents: l.eachCents, cents: l.cents, category: cat.id }); });
          cs.offeredSizes.forEach(function (s) { if (!seenSize[s]) { seenSize[s] = true; result.offeredSizes.push(s); } });
        });
        if (auto) result.autoPrint.unassigned = Math.max(0, qty - result.sizeTotal);
        if (result.priced) {
          var unpricedCat = result.categoryRows.some(function (c) { return c.baseCents === null; });
          if (unpricedCat) { result.priced = false; result.tierStatus = 'unpriced'; }
          else {
            result.baseCents = result.categoryRows.reduce(function (t, c) { return t + c.baseCents; }, 0);
            // One category on the order: its price per piece is THE base price (as before families existed).
            if (result.categoryRows.length === 1) result.unitBaseCents = result.categoryRows[0].unitCents;
            // Pieces not yet split by category (should not happen from the builder) are priced at the first category's rate.
            var split = result.categoryRows.reduce(function (t, c) { return t + c.quantity; }, 0);
            if (split < qty && result.categoryRows.length) result.baseCents += (qty - split) * (result.categoryRows[0].unitCents || 0);
          }
        }
        // Next price break per category ("Adult <rate> · Youth <rate> each").
        if (result.next && result.next.type === 'tier') {
          var nextIndex = sortedTiers(prod).map(function (t) { return t.min; }).indexOf(result.next.min);
          var onOrder = result.categoryRows.length ? result.categoryRows : [{ id: cats[0].id, product: categoryProduct(prod, cats[0].id) }];
          result.next.prices = onOrder.map(function (c) { return { id: c.id, label: c.product.categoryLabel, price: categoryTierPrice(c.product, nextIndex) }; });
          if (result.next.prices.length === 1) result.next.price = result.next.prices[0].price;
        }
      }
      if (result.priced) {
        result.printCents = result.print.perItemCents * qty + (result.autoPrint ? result.autoPrint.cents : 0);
        result.subtotalCents = result.baseCents + result.extendedCents + result.printCents;
        result.averagePerPieceCents = Math.round(result.subtotalCents / qty);
      }
      return result;
    }

    // ---- Rush service (pure date math; dates are local YYYY-MM-DD strings) ----
    function parseDay(s) {
      var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ''));
      if (!m) return null;
      var d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      return isNaN(d.getTime()) ? null : d;
    }
    function dayString(d) {
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    }
    // Business days d with fromDay < d <= toDay (Mon–Fri, minus config.rush_closed_dates). Counting stops at `cap`.
    function businessDays(config, fromDay, toDay, cap) {
      var from = parseDay(fromDay);
      var to = parseDay(toDay);
      if (!from || !to) return null;
      var closed = Array.isArray(config.rush_closed_dates) ? config.rush_closed_dates : [];
      var limit = cap || 400;
      var n = 0;
      for (var i = 0; i < 800 && from < to && n <= limit; i++) {
        from.setDate(from.getDate() + 1);
        var wd = from.getDay();
        if (wd !== 0 && wd !== 6 && closed.indexOf(dayString(from)) === -1) n++;
      }
      return n;
    }
    function rushEnabled(config) {
      return Array.isArray(config.rush_fees) && config.rush_fees.length > 0 && parseInt(config.rush_business_days, 10) > 0;
    }
    // {applies, days, manual, cents, tier}. applies = the need-by date is within the rush window.
    // manual = rush requested but no automatic fee (custom-quote quantity) — Carry Hope reviews it.
    function rush(config, qty, todayDay, needByDay) {
      if (!rushEnabled(config) || !needByDay) return { applies: false, days: null, manual: false, cents: 0, tier: null };
      var window_ = parseInt(config.rush_business_days, 10);
      var days = businessDays(config, todayDay, needByDay, window_ + 1);
      if (days === null || days > window_) return { applies: false, days: days, manual: false, cents: 0, tier: null };
      qty = parseInt(qty, 10);
      if (!isNaN(qty) && qty >= config.custom_quote_threshold) return { applies: true, days: days, manual: true, cents: 0, tier: null };
      var fees = config.rush_fees.slice().sort(function (a, b) { return a.min - b.min; });
      for (var i = 0; i < fees.length; i++) {
        if (qty >= fees[i].min && (fees[i].max === null || fees[i].max === undefined || qty <= fees[i].max)) {
          return { applies: true, days: days, manual: false, cents: toCents(fees[i].fee), tier: fees[i] };
        }
      }
      return { applies: true, days: days, manual: true, cents: 0, tier: null };
    }

    return { toCents: toCents, product: product, placement: placement, printSize: printSize, sizesFor: sizesFor, tierFor: tierFor,
      nextBreak: nextBreak, startingPrice: startingPrice, printCharges: printCharges, quote: quote,
      autoSizing: autoSizing, autoDimension: autoDimension,
      categoriesOf: categoriesOf, categoryProduct: categoryProduct, categoryTierPrice: categoryTierPrice, sortedTiers: sortedTiers,
      businessDays: businessDays, rushEnabled: rushEnabled, rush: rush, dayString: dayString };
  })();
  window.CarryHopeBulkPricing = Engine;

  /* ------------------------------------------------------------------ */
  /* Builder UI                                                          */
  /* ------------------------------------------------------------------ */
  var root = document.querySelector('[data-ch-bulk]');
  var configNode = document.getElementById('CarryHopeBulkConfig');
  if (!root || !configNode) return;
  var config;
  try { config = JSON.parse(configNode.textContent); } catch (e) { return; }
  if (!config || !config.products) return;
  window.CarryHopeBulkConfig = config;

  var form = root.querySelector('#CarryHopeBulkOrderRequest');
  if (!form) return;
  // 'custom' = 1–9 piece Custom Orders page: no tiers; live estimate only with config.custom_estimate (EST below).
  // Anything else = bulk (10+, priced).
  var CUSTOM = root.getAttribute('data-mode') === 'custom';
  var MEDIA = {};       // product id -> front-facing photo URL (rendered by Liquid from Shopify Files / theme assets)
  try { MEDIA = JSON.parse((document.getElementById('CarryHopeBulkMedia') || {}).textContent || '{}') || {}; } catch (e) { MEDIA = {}; }
  // ':r3' = saved-state shape with product families (categories, per-category quantities, Oct 3 2026); older saved state is ignored.
  var STORAGE_KEY = (CUSTOM ? 'carryHopeCustomOrder:' : 'carryHopeBulkOrder:') + config.version + ':r3';
  var REQUEST_KEY = CUSTOM ? 'carryHopeCustomOrderRequestNumber' : 'carryHopeBulkOrderRequestNumber';
  var TOTAL_STEPS = 7;
  var ART_STEP = 5;       // Placement & Artwork
  var DETAILS_STEP = 6;
  var CUSTOM_MAX = 9;     // the Custom Orders page takes 1–9 pieces; 10+ belongs on the bulk page
  var BULK_URL = '/pages/bulk-custom-orders';
  var CUSTOM_URL = '/pages/custom-orders';
  var ARTWORK_EMAIL = config.artwork_email || '';
  var REQUEST_RE = /^CH-BULK-\d{6}-[A-HJ-NP-Z2-9]{4}$/;
  var ART = {
    url: root.getAttribute('data-art-upload-url') || '',
    maxMb: Number(root.getAttribute('data-art-max-mb')) || 100,
    canvaUrl: root.getAttribute('data-art-canva-url') || 'https://www.canva.com/',
    maxInspo: 5,
    previewMaxBytes: 12 * 1024 * 1024,
    smallTimeoutMs: 12000,        // start/discard normally answer in 1–4 s; a stalled request is dropped and retried quickly
    firstChunk: 512 * 1024,       // the first piece is small so we can measure the connection
    targetChunkMs: 30000,         // later pieces are sized to take ~30 s on this connection (max = helper's chunkBytes, 8 MB)
    minRate: 40,                  // bytes per ms (~40 KB/s) assumed before the first piece is measured
    attempts: 5,
    parallel: 2
  };
  window.CarryHopeBulkBuilderVersion = '2026-10-05-naming';
  window.CarryHopeBulkBuilderMode = CUSTOM ? 'custom' : 'bulk';
  var MAX_CUSTOM_COLORS = 3;              // typed "Another color" entries per order
  var COLOR_ART = '__colorart';           // Step 5 uploader for other artwork versions (different artwork per garment color)
  var ORDER_WORD = CUSTOM ? 'custom order' : 'bulk order';
  // 1–9 live estimate (Amber, Oct 3 2026 16:47 UTC): the custom section sends custom_estimate:true with one synthetic
  // 1–9 tier (custom_page_price) + the Bulk add-ons + the flat 1–9 rush fee. EST = this page shows a live estimate.
  // Without the flag (older custom section) the 1–9 page keeps the emailed-quote behaviour.
  var EST = !CUSTOM || config.custom_estimate === true;
  var money = new Intl.NumberFormat('en-US', { style: 'currency', currency: config.currency || 'USD' });
  function fmt(cents) { return money.format(cents / 100); }
  // Whole dollars without ".00" ("$1,670"), cents only when needed ("$16.70") — the Bulk price breakdown (Amber, Oct 3 2026).
  var moneyWhole = new Intl.NumberFormat('en-US', { style: 'currency', currency: config.currency || 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
  function fmtAmt(cents) { return cents % 100 === 0 ? moneyWhole.format(cents / 100) : fmt(cents); }
  function fmtEach(dollars) {
    var c = Engine.toCents(dollars);
    return c % 100 === 0 ? '$' + (c / 100) : fmt(c);
  }
  function esc(v) {
    return String(v === undefined || v === null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function $(sel, scope) { return (scope || root).querySelector(sel); }
  function $all(sel, scope) { return Array.prototype.slice.call((scope || root).querySelectorAll(sel)); }
  // iPhone fix (Amber, Oct 3 2026 screen recording): iOS Safari closed the keyboard after every digit typed on Step 4
  // because each keystroke rebuilt the estimate/summary/placement markup. setHtml() writes only when the markup really
  // changed, and when only text/attributes changed it patches the existing nodes in place instead of replacing them.
  // Containers holding form controls are always fully replaced (when changed) so checked/value state stays correct.
  // Different child counts (e.g. Bulk price-breakdown rows appearing as sizes are typed, Oct 3 2026): the shared positions
  // are patched in place and only the extra nodes at the end are added or dropped, so a keystroke never rebuilds a list.
  var FORM_RE = /<(input|select|textarea|button)\b/i;
  function patchNodes(a, b) {
    var an = a.childNodes, bn = b.childNodes, i;
    var shared = Math.min(an.length, bn.length);
    for (i = 0; i < shared; i++) {
      var x = an[i], y = bn[i];
      if (x.nodeType !== y.nodeType || (x.nodeType === 1 && x.tagName !== y.tagName)) { a.replaceChild(y.cloneNode(true), x); continue; }
      if (x.nodeType !== 1) { if (x.nodeValue !== y.nodeValue) x.nodeValue = y.nodeValue; continue; }
      var j;
      for (j = x.attributes.length - 1; j >= 0; j--) { var an2 = x.attributes[j].name; if (!y.hasAttribute(an2)) x.removeAttribute(an2); }
      for (j = 0; j < y.attributes.length; j++) { var at = y.attributes[j]; if (x.getAttribute(at.name) !== at.value) x.setAttribute(at.name, at.value); }
      patchNodes(x, y);
    }
    while (a.childNodes.length > bn.length) a.removeChild(a.lastChild);
    for (i = a.childNodes.length; i < bn.length; i++) a.appendChild(bn[i].cloneNode(true));
  }
  function setHtml(el, html) {
    if (!el) return;
    html = String(html || '');
    if (el.__chHtml === html) return;
    if (typeof el.__chHtml !== 'string' || FORM_RE.test(html) || FORM_RE.test(el.__chHtml)) {
      el.innerHTML = html;
    } else {
      var tpl = document.createElement('template');
      tpl.innerHTML = html;
      patchNodes(el, tpl.content);
    }
    el.__chHtml = html;
  }
  function listText(items, conj) {
    if (items.length <= 1) return items.join('');
    return items.slice(0, -1).join(', ') + ' ' + (conj || 'or') + ' ' + items[items.length - 1];
  }
  function copyText(text, button) {
    function done() { if (button) { var label = button.textContent; button.textContent = 'Copied'; setTimeout(function () { button.textContent = label; }, 1800); } }
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) { navigator.clipboard.writeText(text).then(done, function () {}); return; }
    } catch (e) {}
    var ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'absolute'; ta.style.left = '-9999px';
    document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); done(); } catch (e) {}
    document.body.removeChild(ta);
  }

  /* ---------- Confirmation screen (after the native contact form posts) ---------- */
  var successNode = $('[data-bulk-success]');
  if (successNode) {
    var info = null;
    try {
      var raw = sessionStorage.getItem(REQUEST_KEY);
      if (raw) { try { info = JSON.parse(raw); } catch (e) { info = { number: raw }; } }
      if (info && typeof info !== 'object') info = { number: String(info) };
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
    var number = info && info.number ? info.number : '';
    $all('[data-request-number]').forEach(function (n) { n.textContent = number || 'Sent with your request'; });
    var copyBtns = $all('[data-copy]');
    copyBtns.forEach(function (b) {
      var what = b.getAttribute('data-copy');
      if (!number && what !== 'email') { b.hidden = true; return; }
      b.addEventListener('click', function () {
        copyText(what === 'email' ? ARTWORK_EMAIL : what === 'subject' ? 'Artwork for ' + number : number, b);
      });
    });
    var subject = number ? 'Artwork for ' + number : 'Artwork for my ' + ORDER_WORD + ' request';
    $all('[data-artmail-subject]').forEach(function (n) { n.textContent = subject; });
    if (!number) { var nn = $('[data-artmail-no-number]'); if (nn) nn.hidden = false; }
    if (info && info.summary) { var sum = $('[data-request-summary]'); if (sum) { sum.textContent = info.summary; sum.hidden = false; } }
    // Per-location artwork status saved at submit: [{label: 'Back Artwork', short: 'Back', status}] where
    // status is uploaded | canva | help | later. Without it (older tab, storage blocked) the email
    // instructions stay visible as the safe default.
    var art = info && Array.isArray(info.artwork) ? info.artwork.filter(function (a) { return a && a.label; }) : [];
    var missing = art.filter(function (a) { return a.status !== 'uploaded' && a.status !== 'canva' && a.status !== 'help'; });
    var title = $('[data-artmail-title]');
    var intro = $('[data-artmail-intro]');
    var artmail = $('[data-artmail]');
    var received = $('[data-art-received]');
    var STATUS_TEXT = { uploaded: 'Uploaded', canva: 'Canva design provided', help: 'Design help requested' };
    if (art.length && !missing.length && received) {
      var helpOnly = art.every(function (a) { return a.status === 'help'; });
      $('[data-art-received-title]').textContent = helpOnly ? 'Design request received' : 'Artwork received';
      $('[data-art-received-text]').textContent = helpOnly
        ? 'We’ll review your design idea along with your ' + ORDER_WORD + ' request and contact you after reviewing garment availability, design details, and production requirements.'
        : 'We’ll review your artwork along with your ' + ORDER_WORD + ' request and contact you after reviewing garment availability, artwork quality, and production requirements.';
      $('[data-art-received-list]').innerHTML = art.map(function (a) {
        return '<li><span>' + esc(a.label) + '</span><strong>' + esc(STATUS_TEXT[a.status] || '') + '</strong></li>';
      }).join('');
      received.hidden = false;
      if (artmail) artmail.hidden = true;
    } else if (missing.length && title && intro) {
      var got = art.filter(function (a) { return missing.indexOf(a) === -1; });
      var need = listText(missing.map(function (a) { return a.short || a.label; }), 'and');
      title.textContent = 'Next step: email us your artwork';
      intro.textContent = (got.length ? 'We received your ' + listText(got.map(function (a) { return a.short || a.label; }), 'and') + ' artwork. ' : '') +
        'We still need your ' + need + ' artwork before we can review your order. Send it in one email using the details below.';
      successNode.classList.add('is-artwork-needed');
    }
    var body = 'Request number: ' + (number || '(not shown — I used the name and email below)') + '\n' +
      'Name: ' + ((info && info.name) || '') + '\n' +
      'Print location(s): ' + ((info && info.placement) || '') + '\n' +
      (missing.length ? 'Artwork for: ' + missing.map(function (a) { return a.short || a.label; }).join(', ') + '\n' : '') + '\n' +
      'My artwork files are attached:\n- \n';
    $all('[data-artmail-link]').forEach(function (a) {
      a.href = 'mailto:' + ARTWORK_EMAIL + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
    });
    setTimeout(function () { successNode.scrollIntoView({ block: 'start' }); successNode.focus({ preventScroll: true }); }, 60);
    return;
  }

  // colors: color keys on the order; otherBy: {token: true} where that category's "Another color" is on; customColors:
  // typed "Another color" entries ({id: 'c1', name, category: token}); colorSizes: size breakdown per color key, e.g.
  // {Black: {S: '5', M: '15'}, 'youth|Black': {XS: '5'}}. A category "token" is '' for a family's first category (and for
  // products without categories) and the category id otherwise, so first-category keys look exactly as they did before.
  // Families only: categories = chosen category ids (config order), catQty = {catId: '50'}; quantity = their sum.
  var state = {
    step: 1, maxStep: 1, productId: null, quantity: '', categories: [], catQty: {}, catsTouched: false,
    colors: [], otherBy: {}, customColors: [], colorSizes: {},
    placementId: null, printSizes: {}, requestNumber: '', artSame: false, art: {}, artByColor: false, artColorNotes: '',
    name: '', organization: '', email: '', phone: '',
    needBy: '', fulfillment: '', address: '', notes: ''
  };
  try {
    var saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    if (saved && typeof saved === 'object') Object.keys(state).forEach(function (k) { if (k in saved) state[k] = saved[k]; });
  } catch (e) {}
  if (!state.art || typeof state.art !== 'object' || Array.isArray(state.art)) state.art = {};
  if (!REQUEST_RE.test(String(state.requestNumber || ''))) state.requestNumber = '';
  state.artSame = !!state.artSame;
  state.colors = (Array.isArray(state.colors) ? state.colors : []).filter(function (c) { return typeof c === 'string' && c; });
  state.customColors = (Array.isArray(state.customColors) ? state.customColors : []).filter(function (c) {
    return c && typeof c === 'object' && /^c\d{1,4}$/.test(String(c.id));
  }).map(function (c) { return { id: String(c.id), name: typeof c.name === 'string' ? c.name : '', category: typeof c.category === 'string' ? c.category : '' }; });
  if (!state.colorSizes || typeof state.colorSizes !== 'object' || Array.isArray(state.colorSizes)) state.colorSizes = {};
  state.categories = (Array.isArray(state.categories) ? state.categories : []).filter(function (c) { return typeof c === 'string' && c; });
  if (!state.catQty || typeof state.catQty !== 'object' || Array.isArray(state.catQty)) state.catQty = {};
  if (!state.otherBy || typeof state.otherBy !== 'object' || Array.isArray(state.otherBy)) state.otherBy = {};
  Object.keys(state.otherBy).forEach(function (t) {
    state.otherBy[t] = !!state.otherBy[t] && state.customColors.some(function (c) { return c.category === t; });
  });
  state.artByColor = !!state.artByColor;
  if (typeof state.artColorNotes !== 'string') state.artColorNotes = '';
  function persist() { try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) {} }
  var sizeNotice = '';

  function currentProduct() { return Engine.product(config, state.productId); }
  // Products whose print size is set automatically from the garment sizes (no customer print-size choice). Both pages
  // (1–9 page too since Amber, Oct 3 2026 — its config copy zeroes every print-size charge, so nothing is priced).
  function autoMode(prod) { return !!Engine.autoSizing(config, prod || currentProduct()); }
  function autoNote() { return ((config.auto_print_sizing || {}).customer_note) || 'Design sizing will be adjusted to fit each garment size appropriately.'; }
  /* ---------- Product families (categories) ---------- */
  function familyCats(prod) { return Engine.categoriesOf(prod === undefined ? currentProduct() : prod); }
  function familyLabel(prod) { prod = prod || currentProduct(); return (prod && (prod.family_label || prod.card_title || prod.name)) || ''; }
  // Chosen categories for the current product: [{id, label, token, prod}] (config order). Products without categories
  // have one pseudo-category {id: '', token: '', prod: product}.
  function activeCats() {
    var prod = currentProduct();
    if (!prod) return [];
    var cats = familyCats(prod);
    if (!cats) return [{ id: '', label: '', token: '', prod: prod }];
    return cats.filter(function (c) { return state.categories.indexOf(c.id) !== -1; }).map(function (c) {
      return { id: c.id, label: c.label || c.id, token: c.id === cats[0].id ? '' : c.id, prod: Engine.categoryProduct(prod, c.id) };
    });
  }
  function multiCat() { return activeCats().length > 1; }
  // Name shown for the order: the family name with 2+ categories ("Gildan Tees"), else the one category's own name
  // (an adult-only order reads exactly as before families), else the product name.
  function orderProductName(prod) {
    prod = prod || currentProduct();
    if (!prod) return '';
    if (!familyCats(prod)) return prod.name;
    var cats = activeCats();
    return cats.length === 1 ? cats[0].prod.name : familyLabel(prod);
  }
  // "Adult 50 · Youth 50" (categories on the order, config order).
  function categoryQtyText(q) { return (q.categoryRows || []).map(function (c) { return c.label + ' ' + c.quantity; }).join(' · '); }
  function catByToken(token) { return activeCats().filter(function (c) { return c.token === token; })[0] || null; }
  function tokenOf(catId) { var cats = familyCats(); return !cats || !catId || catId === cats[0].id ? '' : catId; }
  function keyFor(token, base) { return token ? token + '|' + base : base; }
  // Split a swatch value / color key into {token, base}: 'youth|Black' -> {token: 'youth', base: 'Black'}.
  function splitKey(key) {
    key = String(key || '');
    var i = key.indexOf('|');
    var cats = familyCats();
    if (i > 0 && cats && cats.some(function (c) { return c.id === key.slice(0, i); })) return { token: key.slice(0, i), base: key.slice(i + 1) };
    return { token: '', base: key };
  }
  // Families: keep chosen categories valid for this product (first category by default) and the total in sync.
  function ensureCategories() {
    var cats = familyCats();
    if (!cats) return;
    var ids = cats.map(function (c) { return c.id; });
    state.categories = ids.filter(function (id) { return state.categories.indexOf(id) !== -1; });
    if (!state.categories.length && !state.catsTouched) state.categories = [ids[0]];
    syncFamilyQuantity();
  }
  // Switching apparel: keep valid categories (first category when none), carry the quantity already entered into a
  // single-category family order, then drop colors/sizes/placement the new product doesn't offer.
  function selectProduct(id) {
    var before = String(state.quantity === undefined || state.quantity === null ? '' : state.quantity).trim();
    state.productId = id;
    var cats = familyCats();
    if (cats) {
      var ids = cats.map(function (c) { return c.id; });
      state.categories = ids.filter(function (cid) { return state.categories.indexOf(cid) !== -1; });
      if (!state.categories.length) state.categories = [ids[0]];
      if (state.categories.length === 1 && before !== '') state.catQty[state.categories[0]] = before;
      syncFamilyQuantity();
    }
    var prod = currentProduct();
    if (prod && state.placementId && prod.placements.indexOf(state.placementId) === -1) state.placementId = null;
    pruneColors();
    pruneSizes();
  }
  function catQtyOf(catId) { return String(state.catQty[catId] === undefined || state.catQty[catId] === null ? '' : state.catQty[catId]).trim(); }
  // The family total = the sum of the chosen categories' quantities ('' while none is entered).
  function syncFamilyQuantity() {
    if (!familyCats()) return;
    var any = false;
    var sum = 0;
    state.categories.forEach(function (id) {
      var v = catQtyOf(id);
      if (v === '') return;
      any = true;
      if (/^\d+$/.test(v)) sum += parseInt(v, 10);
    });
    state.quantity = any ? String(sum) : '';
  }
  function newCustomColor(token) {
    var n = 0;
    state.customColors.forEach(function (c) { n = Math.max(n, parseInt(c.id.slice(1), 10) || 0); });
    var c = { id: 'c' + (n + 1), name: '', category: token || '' };
    state.customColors.push(c);
    return c;
  }
  function customsOf(token) { return state.customColors.filter(function (c) { return (c.category || '') === (token || ''); }); }
  function otherOn(token) { return !!state.otherBy[token || ''] && customsOf(token).length > 0; }
  // Garment colors on the order, per chosen category: listed colors in that category's order, then its typed colors.
  function selectedColors() {
    var list = [];
    activeCats().forEach(function (cat) {
      (cat.prod.colors || []).forEach(function (c) {
        var key = keyFor(cat.token, c.name);
        if (state.colors.indexOf(key) !== -1) list.push({ key: key, name: c.name, hex: c.hex, custom: false, category: cat.id, categoryLabel: cat.label, token: cat.token });
      });
      if (otherOn(cat.token)) {
        customsOf(cat.token).forEach(function (cc) {
          list.push({ key: keyFor(cat.token, 'custom:' + cc.id), id: cc.id, name: (cc.name || '').trim(), custom: true, category: cat.id, categoryLabel: cat.label, token: cat.token });
        });
      }
    });
    return list;
  }
  function colorName(c) { return c.name || (c.custom ? 'Another color' : ''); }
  // "Youth — Black" when the order has 2+ categories (Amber's format), else just the color name.
  function colorLabel(c, multi) {
    var label = c.categoryLabel || '';
    return ((multi === undefined ? multiCat() : multi) && label ? label + ' — ' : '') + (c.name || (c.custom ? 'Another color' : ''));
  }
  function colorsText() { var m = multiCat(); return selectedColors().map(function (c) { return colorLabel(c, m); }).join(', '); }
  function sizesOf(key) {
    var m = state.colorSizes[key];
    if (!m || typeof m !== 'object' || Array.isArray(m)) m = state.colorSizes[key] = {};
    return m;
  }
  function catProdOf(c) {
    var prod = currentProduct();
    return c && c.category ? Engine.categoryProduct(prod, c.category) || prod : prod;
  }
  function offeredFor(c) { return Engine.sizesFor(catProdOf(c), c.custom ? '__other' : c.name); }
  // The engine selection for the order as entered (a fresh object each call — callers may adjust it, e.g. the
  // "order more, pay less" nudge quotes the next tier's minimum quantity without touching the order).
  function currentQuote() { return Engine.quote(config, quoteSel()); }
  function quoteSel() {
    var sel = { productId: state.productId, quantity: state.quantity,
      colors: selectedColors().map(function (c) { return { key: c.key, name: colorName(c), custom: c.custom, sizes: sizesOf(c.key), category: c.category || '' }; }),
      placementId: state.placementId, printSizes: state.printSizes };
    if (familyCats()) {
      sel.categoryQuantities = {};
      state.categories.forEach(function (id) { var v = catQtyOf(id); if (/^\d+$/.test(v)) sel.categoryQuantities[id] = parseInt(v, 10); });
    }
    return sel;
  }
  // Every size offered across the chosen colors of one category (the full list before any color is chosen).
  function orderSizes(cat) {
    cat = cat || activeCats()[0];
    if (!cat) return [];
    var cp = cat.prod;
    var sel = selectedColors().filter(function (c) { return c.token === cat.token; });
    if (!sel.length) return (cp.sizes || []).slice();
    var on = {};
    sel.forEach(function (c) { offeredFor(c).forEach(function (s) { on[s] = true; }); });
    return (cp.sizes || []).filter(function (s) { return on[s]; });
  }
  function units(prod, n) { return n === 1 ? prod.unit_singular : prod.unit_plural; }
  function tierRange(t) { return t.max ? t.min + '–' + t.max : t.min + '+'; }
  function sizeRange(list) { return list.length ? (list.length > 1 ? list[0] + '–' + list[list.length - 1] : list[0]) : ''; }
  function sizeLine(row) {
    return row.sizeRows.map(function (r) { return r.size === 'One size' ? 'One size: ' + r.qty : r.size + ': ' + r.qty; }).join(' · ');
  }
  // Keep only listed colors this product (category) offers. Typed colors of the first category carry over to another
  // product; typed colors of a category this product doesn't have are dropped.
  function pruneColors() {
    var prod = currentProduct();
    if (!prod) return;
    var cats = familyCats(prod);
    var tokens = cats ? cats.map(function (c, i) { return i === 0 ? '' : c.id; }) : [''];
    state.colors = state.colors.filter(function (key) {
      var k = splitKey(key);
      if (tokens.indexOf(k.token) === -1 || (!k.token && key.indexOf('|') !== -1)) return false;
      var cp = cats ? Engine.categoryProduct(prod, k.token || cats[0].id) : prod;
      return !!cp && (cp.colors || []).some(function (c) { return c.name === k.base; });
    });
    state.customColors = state.customColors.filter(function (c) { return tokens.indexOf(c.category || '') !== -1; });
    Object.keys(state.otherBy).forEach(function (t) { if (tokens.indexOf(t) === -1) delete state.otherBy[t]; });
  }

  // First size in the list that costs more (e.g. "2XL"); used only for non-numeric hints.
  function extendedFrom(prod, list) {
    var s = prod.size_surcharges || {};
    var l = list || prod.sizes || [];
    for (var i = 0; i < l.length; i++) { if (Engine.toCents(s[l[i]]) > 0) return l[i]; }
    return '';
  }
  // Customer-facing figures: estimated price per piece + estimated order total. When sizes are
  // priced differently the per-piece figure is the (labelled) average. No surcharge amounts.
  // The estimated order total includes the rush service fee once the need-by date falls in the rush window
  // (Amber, Oct 2026). The price per piece never includes it — rush is one flat fee per order.
  function figures(q) {
    if (!EST || !q || !q.priced || !q.quantity) return null;
    var r = rushNow(q);
    var rushCents = r && !r.manual ? r.cents : 0;
    // "Average" whenever pieces can be priced differently: extended sizes, print sizes that follow the garment size, or
    // family categories with different rates (adult rate + youth rate).
    var rates = {};
    (q.categoryRows || []).forEach(function (c) { if (c.quantity > 0) rates[c.unitCents] = true; });
    return { eachCents: q.averagePerPieceCents, totalCents: q.subtotalCents + rushCents, rushCents: rushCents,
      average: q.extendedCents > 0 || !!(q.autoPrint && q.autoPrint.cents > 0) || Object.keys(rates).length > 1 };
  }
  // noIncl (Bulk, where the price breakdown lists "Rush service fee $X"): drop the "Includes $X rush service fee" line.
  // Bulk with a rush fee: the per-piece figure reads "Average per piece (before rush fee)" — rush is never in the average
  // (Amber, Oct 3 2026). The 1–9 page keeps its labels.
  function figuresHtml(f, mod, noIncl) {
    var label = f.average ? 'Average estimated price' : 'Estimated price';
    if (!CUSTOM && f.rushCents) label = f.average ? 'Average per piece (before rush fee)' : 'Price per piece (before rush fee)';
    return '<div class="ch-bulk-figures' + (mod ? ' ' + mod : '') + '">' +
      '<div class="ch-bulk-figures__item"><span class="ch-bulk-figures__label">' + label + '</span>' +
      '<span class="ch-bulk-figures__value" data-estimate-each>' + esc(fmt(f.eachCents)) + ' <small>each</small></span></div>' +
      '<div class="ch-bulk-figures__item ch-bulk-figures__item--total"><span class="ch-bulk-figures__label">Estimated order total</span>' +
      '<span class="ch-bulk-figures__value" data-estimate-total>' + esc(fmt(f.totalCents)) + '</span>' +
      (f.rushCents && !(noIncl && !CUSTOM) ? '<span class="ch-bulk-figures__incl" data-estimate-rush-incl>Includes ' + esc(fmtFee(f.rushCents)) + ' rush service fee</span>' : '') +
      '</div></div>';
  }
  /* ---------- Bulk price explanation (Amber, Oct 3 2026 21:46 UTC) ----------
     PRESENTATION ONLY: every amount is read from the engine quote (the "order more, pay less" nudge asks the engine for its
     own quote at the next tier's minimum). Nothing here prices anything. Bulk page only — the 1–9 page keeps its wording. */
  function capFirst(s) { s = String(s || ''); return s.charAt(0).toUpperCase() + s.slice(1); }
  function inclLabel() { var l = config.included_print_label || 'One print location'; return l.charAt(0).toLowerCase() + l.slice(1); }
  // "$1–$5" (or "$4" when every amount is the same).
  function centsRange(list) {
    var lo = Math.min.apply(null, list), hi = Math.max.apply(null, list);
    return lo === hi ? fmtUnit(lo) : fmtUnit(lo) + '–' + fmtUnit(hi);
  }
  // Per-piece add-on for a placement's extra print locations (default print size, so no print-size charge is mixed in).
  function placementAddOnCents(prod, plId) {
    var pc = Engine.printCharges(config, plId, {}, prod);
    return pc && pc.placement ? pc.perItemCents : 0;
  }
  // Breakdown rows that apply — no $0 rows. null when the rows would not add up to the engine subtotal (never shown wrong).
  function breakdownRows(prod, q) {
    if (!q || !q.priced) return null;
    var rows = [];
    var cats = (q.categoryRows || []).filter(function (c) { return c.quantity > 0; });
    if (q.family && cats.length) {
      cats.forEach(function (c) {
        rows.push({ label: (cats.length > 1 ? c.label : capFirst(prod.unit_plural)) + ' — ' + c.quantity + ' × ' + fmtUnit(c.unitCents), cents: c.baseCents });
      });
    } else {
      rows.push({ label: capFirst(prod.unit_plural) + ' — ' + q.quantity + ' × ' + fmtUnit(q.unitBaseCents), cents: q.baseCents });
    }
    if (q.extendedCents > 0) rows.push({ label: 'Larger sizes', cents: q.extendedCents });
    if (q.autoPrint && q.autoPrint.cents > 0) rows.push({ label: 'Larger print sizes', cents: q.autoPrint.cents });
    if (q.print.placement && q.print.perItemCents > 0) {
      rows.push({ label: q.print.placement.label + ' — ' + q.quantity + ' × ' + fmtUnit(q.print.perItemCents), cents: q.print.perItemCents * q.quantity });
    }
    var sum = rows.reduce(function (t, r) { return t + r.cents; }, 0);
    return sum === q.subtotalCents ? rows : null;
  }
  // The itemized estimate. ctx 'review' = always shown; elsewhere a <details> "Price breakdown" — open on desktop, closed on
  // phones until opened; the customer's open/closed choice is kept across re-renders (breakdownOpen, set by the toggle listener).
  var breakdownOpen = null;
  function breakdownHtml(prod, q, f, ctx) {
    if (CUSTOM || !f) return '';
    var rows = breakdownRows(prod, q);
    if (!rows) return '';
    function li(label, value, cls) {
      return '<li' + (cls ? ' class="' + cls + '"' : '') + '><span>' + esc(label) + '</span><strong>' + esc(value) + '</strong></li>';
    }
    var html = rows.map(function (r) { return li(r.label, fmtAmt(r.cents)); }).join('');
    if (f.rushCents) {
      html += li('Subtotal', fmtAmt(q.subtotalCents), 'ch-bulk-breakdown__sub') +
        li('Average before rush', fmtUnit(f.eachCents) + '/ea', 'ch-bulk-breakdown__avg') +
        li('Rush service fee', fmtAmt(f.rushCents), 'ch-bulk-breakdown__rush');
    }
    html += li('Estimated order total', fmtAmt(f.totalCents), 'ch-bulk-breakdown__total');
    var list = '<ul class="ch-bulk-breakdown__list">' + html + '</ul>';
    if (ctx === 'review') {
      return '<div class="ch-bulk-breakdown ch-bulk-breakdown--review" data-price-breakdown><p class="ch-bulk-breakdown__title">Price breakdown</p>' + list + '</div>';
    }
    var open = breakdownOpen === null ? !!(window.matchMedia && window.matchMedia('(min-width: 990px)').matches) : breakdownOpen;
    return '<details class="ch-bulk-breakdown" data-price-breakdown' + (open ? ' open' : '') + '><summary>Price breakdown</summary>' + list + '</details>';
  }
  // Before the order's choices add anything (Amber #7) — what the price covers and what adds to it, from the config:
  // "Price is for sizes S–L with one print location. XL and up add $1–$5 per shirt; a second print location adds $2–$4 per shirt."
  function preChoiceNote(prod) {
    var cats = activeCats();
    if (!prod || !cats.length) return '';
    var unit = prod.unit_singular;
    var multi = cats.length > 1;
    var sizeParts = [];
    var base = '';
    cats.forEach(function (cat) {
      var list = orderSizes(cat);
      var sur = cat.prod.size_surcharges || {};
      var charged = list.filter(function (s) { return Engine.toCents(sur[s]) > 0; });
      if (!charged.length) return;
      sizeParts.push((multi ? cat.label + ' ' : '') + charged[0] + (charged.length > 1 ? ' and up add ' : ' adds ') +
        centsRange(charged.map(function (s) { return Engine.toCents(sur[s]); })) + ' per ' + unit);
      if (!multi) base = sizeRange(list.slice(0, list.indexOf(charged[0])));
    });
    var adds = (prod.placements || []).map(function (id) { return placementAddOnCents(prod, id); }).filter(function (c) { return c > 0; });
    var parts = sizeParts.slice();
    if (adds.length) parts.push('a second print location adds ' + centsRange(adds) + ' per ' + unit);
    var first = multi ? 'Prices include ' + inclLabel() + '.'
      : base ? 'Price is for sizes ' + base + ' with ' + inclLabel() + '.'
      : 'Price includes ' + inclLabel() + '.';
    return first + (parts.length ? ' ' + capFirst(parts.join('; ')) + '.' : '');
  }
  // The factors on THIS order that move the price per piece (Amber #4).
  function priceFactors(prod, q) {
    var cats = (q.categoryRows || []).filter(function (c) { return c.quantity > 0; });
    var rates = {};
    cats.forEach(function (c) { rates[c.unitCents] = true; });
    var incl = [];
    if (q.extendedCents > 0) {
      if (cats.length > 1) {
        incl.push(listText(cats.filter(function (c) { return (c.extendedLines || []).length; }).map(function (c) {
          return c.label + ' ' + c.extendedLines[0].size + '+';
        }), 'and') + ' charges');
      } else {
        var sizes = (q.extendedLines || []).map(function (l) { return l.size; });
        incl.push((sizes.length > 3 ? sizes[0] + '+' : sizes.join('/')) + ' size charges');
      }
    }
    if (q.autoPrint && q.autoPrint.cents > 0) incl.push('larger print sizes');
    if (q.print.placement && q.print.perItemCents > 0) incl.push(q.print.placement.label + ' (+' + fmtUnit(q.print.perItemCents) + ' per ' + prod.unit_singular + ')');
    return { ratesDiffer: Object.keys(rates).length > 1, labels: cats.map(function (c) { return c.label; }), incl: incl };
  }
  // "Average across all 40 shirts — includes XL/2XL size charges and Front + Back (+$4 per shirt)." when the price per piece
  // is an average; "Includes Front + Back (+$4 per shirt)." when only the placement adds to it; otherwise the pre-choice
  // note (nothing on Review, where every choice is made).
  function priceExplain(prod, q, f, ctx) {
    if (CUSTOM || !prod || !q || !q.priced || !f) return '';
    var x = priceFactors(prod, q);
    if (f.average) {
      var body = x.ratesDiffer ? listText(x.labels, 'and') + ' rates differ' + (x.incl.length ? '; includes ' + listText(x.incl, 'and') : '')
        : 'includes ' + listText(x.incl, 'and');
      return 'Average across all ' + q.quantity + ' ' + units(prod, q.quantity) + ' — ' + body + '.';
    }
    if (x.incl.length) return 'Includes ' + listText(x.incl, 'and') + '.';
    return ctx === 'review' ? '' : preChoiceNote(prod);
  }
  // 500+ (Amber #8) — never implies a lower price.
  function quoteNudgeText() { return 'Ordering ' + config.custom_quote_threshold + ' or more? We’ll send you a custom quote.'; }
  // Order more, pay less (Amber #9): the engine's quote at the next tier's minimum, the extra pieces added at the base size
  // (to the first category on a family order). '' unless that merchandise subtotal is ≤ today's.
  function buyUpText(prod, q) {
    if (CUSTOM || !prod || !q || !q.priced || !q.next || q.next.type !== 'tier' || !(q.next.remaining > 0)) return '';
    var sel = quoteSel();
    var who = '';
    var unitProd = prod;
    if (sel.categoryQuantities) {
      var on = (q.categoryRows || []).filter(function (c) { return c.quantity > 0; });
      if (!on.length) return '';
      sel.categoryQuantities[on[0].id] = (sel.categoryQuantities[on[0].id] || 0) + q.next.remaining;
      if (on.length > 1) who = on[0].label.toLowerCase() + ' ';
      unitProd = on[0].product || prod;
    }
    sel.quantity = String(q.next.min);
    var nq = Engine.quote(config, sel);
    if (!nq.priced || nq.quantity !== q.next.min || nq.subtotalCents > q.subtotalCents) return '';
    var r = rushNow(q);
    return 'Add ' + q.next.remaining + ' more ' + who + units(unitProd, q.next.remaining) + ' and your total' + (r && !r.manual ? ' before the rush fee' : '') +
      (nq.subtotalCents < q.subtotalCents ? ' drops to ' : ' stays at ') + fmtAmt(nq.subtotalCents) + ' (' + fmtUnit(nq.averagePerPieceCents) + '/ea).';
  }
  // Step 4 (Amber #5): with a second print location chosen, a prominent note right above the size prices.
  function sizePlacementNote(prod, q) {
    if (CUSTOM || !EST || !prod || !q || !q.priced || !(prod.sizes || []).length || !q.print.placement || !(q.print.perItemCents > 0)) return '';
    return 'Prices below include ' + inclLabel() + '. Your selected ' + q.print.placement.label + ' placement adds ' + fmtUnit(q.print.perItemCents) + ' per piece.';
  }
  /* ---------- Rush service (bulk only; amounts come from config.rush_fees) ---------- */
  function fmtFee(cents) { return cents % 100 === 0 ? '$' + (cents / 100) : fmt(cents); }
  function todayDay() { return Engine.dayString(new Date()); }
  // null = nothing to show (custom page, no/invalid date, date outside the window, or no valid quantity yet).
  function rushNow(q) {
    if (!EST || !Engine.rushEnabled(config) || !state.needBy || state.needBy < minDate()) return null;
    q = q || currentQuote();
    if (!q.priced && !q.customQuote) return null;
    var r = Engine.rush(config, q.quantity, todayDay(), state.needBy);
    return r.applies ? r : null;
  }
  function rushWindowText() { return 'within ' + parseInt(config.rush_business_days, 10) + ' business days'; }
  // Details step: shown right under the need-by date as soon as a rush date is chosen.
  function renderRush() {
    var box = $('[data-rush-note]');
    var dateNote = $('[data-date-note]');
    if (!box) return;
    var r = rushNow();
    if (!r) { box.hidden = true; box.innerHTML = ''; if (dateNote) dateNote.hidden = false; return; }
    var head = r.manual
      ? '<p class="ch-bulk-rush__title"><span aria-hidden="true">⚡</span> Rush order</p><p>Your need-by date is ' + esc(rushWindowText()) + '. Rush service for orders of ' +
        config.custom_quote_threshold + '+ pieces is reviewed with your custom quote.</p>'
      : '<p class="ch-bulk-rush__title"><span aria-hidden="true">⚡</span> Rush order — <span data-rush-fee>' + esc(fmtFee(r.cents)) + '</span> rush service fee</p>' +
        '<p>Your need-by date is ' + esc(rushWindowText()) + ', so a rush service fee applies. It’s included in your estimated order total.</p>';
    box.innerHTML = head + (config.rush_note ? '<p class="ch-bulk-rush__note">' + esc(config.rush_note) + '</p>' : '');
    box.hidden = false;
    if (dateNote) dateNote.hidden = true;
  }
  function rushLineHtml(r) {
    if (!r) return '';
    return r.manual
      ? '<p class="ch-bulk-rushline" data-rush-line><span>Rush service</span><strong>Reviewed with your quote</strong></p>'
      : '<p class="ch-bulk-rushline" data-rush-line><span>Rush service fee</span><strong>' + esc(fmtFee(r.cents)) + '</strong></p>';
  }
  // Automatic print sizing: the first garment size whose price per piece differs (extended-size charge or a larger,
  // charged print size), e.g. "L" for adult garments. '' when no size changes the price.
  // Families with 2+ categories: "Adult L" (only categories where some size changes the price).
  function autoPricedFrom(prod) {
    if (!prod || !autoMode(prod)) return '';
    var cats = activeCats();
    var multi = cats.length > 1;
    var parts = [];
    cats.forEach(function (cat) {
      var cp = cat.prod;
      var list = orderSizes(cat);
      var sur = cp.size_surcharges || {};
      for (var i = 0; i < list.length; i++) {
        // Front and Back may be mapped separately (hoodies); groups with one `main` map return the same size for both.
        var charged = ['Front', 'Back'].some(function (loc) {
          var dim = Engine.autoDimension(config, cp, 'main', list[i], loc);
          return dim && dim.eachCents > 0;
        });
        if (Engine.toCents(sur[list[i]]) > 0 || charged) { parts.push((multi ? cat.label + ' ' : '') + list[i]); break; }
      }
    });
    return listText(parts, 'and');
  }
  // What can change the price per piece, without amounts (data-driven from the config).
  function priceChangeHint(prod) {
    var parts = [];
    if (autoMode(prod)) {
      // No print-size choice: larger garment sizes get larger prints (and 2XL+ cost more), so name the first such size.
      var ex = config.additional_location_charges || {};
      if (Object.keys(ex).some(function (k) { return Engine.toCents(ex[k]) > 0; })) parts.push('extra print locations');
      var pf = autoPricedFrom(prod);
      if (pf) parts.push(pf + '+ sizes');
      if (!parts.length) return '';
      var t = listText(parts, 'and');
      return t.charAt(0).toUpperCase() + t.slice(1) + ' may change your price per piece.';
    }
    if ((config.print_sizes || []).some(function (p) { return Engine.toCents(p.surcharge) > 0; })) parts.push('larger prints');
    var extra = config.additional_location_charges || {};
    if (Object.keys(extra).some(function (k) { return Engine.toCents(extra[k]) > 0; })) parts.push('extra print locations');
    var from = prod ? extendedFrom(prod, orderSizes()) : '';
    if (from) parts.push(from + '+ sizes');
    if (!parts.length) return '';
    var s = listText(parts, 'and');
    return s.charAt(0).toUpperCase() + s.slice(1) + ' may change your price per piece.';
  }

  // For each chosen color, drop quantities entered for sizes outside that color's commonly available sizes.
  function pruneSizes() {
    var prod = currentProduct();
    if (!prod) return;
    var removed = [];
    var multi = multiCat();
    selectedColors().forEach(function (c) {
      var offered = offeredFor(c);
      var m = sizesOf(c.key);
      var cleaned = {};
      Object.keys(m).forEach(function (s) {
        var n = parseInt(m[s], 10);
        if (offered.length ? offered.indexOf(s) !== -1 : s === 'One size') cleaned[s] = m[s];
        else if (n > 0 && offered.length && s !== 'One size') removed.push({ color: colorLabel(c, multi), text: s + ' (' + n + ')' });
      });
      state.colorSizes[c.key] = cleaned;
    });
    if (removed.length === 1) {
      sizeNotice = removed[0].text + ' isn’t commonly available in ' + removed[0].color + ', so we removed it from your size breakdown. ' +
        'See which colors commonly carry that size on the Color step.';
    } else if (removed.length) {
      sizeNotice = 'We removed sizes that aren’t commonly available in the colors you chose: ' +
        listText(removed.map(function (r) { return r.color + ' ' + r.text; }), 'and') + '. See which colors commonly carry those sizes on the Color step.';
    }
  }

  /* ---------- Step 1: products (front photo + a few color swatches) ---------- */
  var MINI_SWATCHES = 6;
  function miniSwatchesHtml(p) {
    var colors = p.colors || [];
    if (!colors.length) return '';
    var more = colors.length - MINI_SWATCHES;
    return '<span class="ch-bulk-option__colors"><span class="ch-bulk-minis" aria-hidden="true">' +
      colors.slice(0, MINI_SWATCHES).map(function (c) { return '<span class="ch-bulk-mini" style="background:' + esc(c.hex) + '"></span>'; }).join('') + '</span>' +
      (more > 0 ? '<span class="ch-bulk-option__more" aria-hidden="true">+' + more + ' more</span>' : '') +
      '<span class="ch-bulk-sr">' + colors.length + (colors.length === 1 ? ' color' : ' colors') + ' available</span></span>';
  }
  // 1–9 page starting prices (config v11, Amber Oct 3 2026): custom_page_price on the product (= its first category) and
  // on each other category. Display only — the quote is still emailed. [{label, price}] in config order.
  function customPrices(p) {
    var cats = familyCats(p);
    var list = cats ? cats.map(function (c, i) { return { label: c.label || c.id, price: Number(i === 0 ? p.custom_page_price : c.custom_page_price) }; })
      : [{ label: '', price: Number(p.custom_page_price) }];
    return list.filter(function (x) { return x.price > 0; });
  }
  // "From <lowest>" (the same wording as the 1–9 landing tile); card_price_by_category (hoodies): "Adult from <a> · Youth from <y>".
  function customFromText(p) {
    var list = customPrices(p);
    if (!list.length) return '';
    if (p.card_price_by_category && familyCats(p)) return list.map(function (x) { return x.label + ' from ' + fmtEach(x.price); }).join(' · ');
    return 'From ' + fmtEach(Math.min.apply(null, list.map(function (x) { return x.price; })));
  }
  function productMeta(p) {
    if (CUSTOM) {
      var cfrom = customFromText(p);
      if (cfrom) return esc(cfrom);
      var sz = p.sizes && p.sizes.length ? 'Sizes ' + sizeRange(p.sizes) : 'One size';
      return esc(sz + ' · ' + (p.colors || []).length + ((p.colors || []).length === 1 ? ' color' : ' colors'));
    }
    // Price ranges (Amber, Oct 3 2026 — the same ranges as the landing-page tiles, never implying the lowest rate applies at
    // the minimum): "<low>–<high>/ea · based on quantity · 10-piece minimum". card_price_by_category (hoodies): one line
    // per category ("Adult <low>–<high>/ea", "Youth <low>–<high>/ea"), then "Based on quantity · 10-piece minimum".
    var minTxt = config.minimum_quantity + '-piece minimum';
    var cats = p.card_price_by_category ? familyCats(p) : null;
    if (cats) {
      var lines = cats.map(function (c) {
        var r = tierPriceRange(Engine.categoryProduct(p, c.id));
        return r ? '<span class="ch-bulk-option__line">' + esc((c.label || c.id) + ' ' + r) + '</span>' : '';
      }).filter(Boolean);
      if (lines.length) return lines.join('') + '<span class="ch-bulk-option__line">' + esc('Based on quantity · ' + minTxt) + '</span>';
    }
    var range = tierPriceRange(p);
    return esc(range ? range + ' · based on quantity · ' + minTxt : minTxt);
  }
  // "<low>–<high>/ea" across the product's (category's) bulk tiers below the custom-quote threshold; '' without tiers.
  function tierPriceRange(cp) {
    var ps = ((cp && cp.tiers) || []).filter(function (t) { return t.min < config.custom_quote_threshold; }).map(function (t) { return Number(t.price); });
    if (!ps.length) return '';
    var lo = Math.min.apply(null, ps), hi = Math.max.apply(null, ps);
    return (lo === hi ? fmtEach(lo) : fmtEach(lo) + '–' + fmtEach(hi)) + '/ea';
  }
  function renderProducts() {
    var box = $('[data-products]');
    // Built once (photos would flicker if rebuilt on every change); afterwards only the checked state is synced.
    if (box.getAttribute('data-built') !== '1' || !box.firstChild) {
      box.setAttribute('data-built', '1');
      box.innerHTML = config.products.map(function (p) {
        var img = MEDIA[p.id] || '';
        var cats = familyCats(p);
        // Families show the family name ("Gildan Tees") and their categories ("Adult · Youth · Toddler").
        return '<label class="ch-bulk-option ch-bulk-option--product">' +
          '<input type="radio" name="bulk_product" value="' + esc(p.id) + '"' + (state.productId === p.id ? ' checked' : '') + '>' +
          '<span class="ch-bulk-option__body">' +
          (img ? '<span class="ch-bulk-option__media"><img src="' + esc(img) + '" alt="" width="160" height="160" loading="lazy"></span>' : '') +
          '<span class="ch-bulk-option__text"><span class="ch-bulk-option__title">' + esc(cats ? familyLabel(p) : p.name) + '</span>' +
          (cats ? '<span class="ch-bulk-option__cats" data-family-cats>' + esc(cats.map(function (c) { return c.label || c.id; }).join(' · ')) + '</span>' : '') +
          '<span class="ch-bulk-option__meta">' + productMeta(p) + '</span>' + miniSwatchesHtml(p) + '</span></span></label>';
      }).join('');
      return;
    }
    $all('input[name="bulk_product"]', box).forEach(function (input) { input.checked = input.value === state.productId; });
  }

  /* ---------- Step 2: quantity ---------- */
  function catSizeText(cp) { return (cp.sizes || []).length ? sizeRange(cp.sizes) : 'One size'; }
  // "Adult <rate> · Youth <rate> each" for the categories on the order at the current tier.
  function ratesText(q) {
    return (q.categoryRows || []).filter(function (c) { return c.unitCents !== null; })
      .map(function (c) { return c.label + ' ' + fmtEach(c.unitCents / 100); }).join(' · ') + ' each';
  }
  function nextPriceText(q) {
    var n = q.next;
    if (n.prices && n.prices.length > 1) return n.prices.map(function (p) { return p.label + ' ' + fmtEach(p.price); }).join(' · ') + ' each';
    return fmtEach(n.price) + ' each';
  }
  // Families: "Who are these for?" category checkboxes + one quantity box per chosen category, then "Total Gildan Tees: N".
  // Rebuilt only when the product or the chosen categories change; quantities update in place so typing keeps focus.
  // The plain "Total pieces" box stays in the page (hidden) — with one category chosen it still sets that category.
  function renderCategoryPicker(prod, q) {
    var input = $('[data-quantity]');
    var qtyField = input ? input.closest('label') : null;
    var box = $('[data-categories]');
    var cats = prod ? familyCats(prod) : null;
    if (!cats) {
      if (box) { box.hidden = true; box.innerHTML = ''; box.removeAttribute('data-key'); }
      if (qtyField) qtyField.hidden = false;
      return;
    }
    if (!box && qtyField) {
      box = document.createElement('div');
      box.className = 'ch-bulk-cats';
      box.setAttribute('data-categories', '');
      qtyField.parentNode.insertBefore(box, qtyField);
    }
    if (!box) return;
    box.hidden = false;
    if (qtyField) qtyField.hidden = true;
    var fam = familyLabel(prod);
    var active = activeCats();
    var key = prod.id + '|' + state.categories.join(',');
    if (box.getAttribute('data-key') !== key) {
      box.setAttribute('data-key', key);
      box.innerHTML = '<fieldset class="ch-bulk-cats__pick"><legend>Who are these ' + esc(prod.unit_plural) + ' for? <small>Choose one or more</small></legend>' +
        '<div class="ch-bulk-cats__options">' + cats.map(function (c) {
          var cp = Engine.categoryProduct(prod, c.id);
          var on = state.categories.indexOf(c.id) !== -1;
          return '<label class="ch-bulk-cat"><input type="checkbox" name="bulk_categories" value="' + esc(c.id) + '"' + (on ? ' checked' : '') + '>' +
            '<span class="ch-bulk-cat__body"><span class="ch-bulk-cat__check" aria-hidden="true">✓</span><span class="ch-bulk-cat__title">' + esc(c.label || c.id) + '</span>' +
            '<span class="ch-bulk-cat__meta">Sizes ' + esc(catSizeText(cp)) + '</span></span></label>';
        }).join('') + '</div></fieldset>' +
        (active.length ? '<div class="ch-bulk-catqty">' + active.map(function (c) {
          return '<label class="ch-bulk-field ch-bulk-catqty__row"><span>' + esc(c.label + ' ' + c.prod.unit_plural) + ' <small>(' + esc(catSizeText(c.prod)) + ')</small></span>' +
            '<input type="number" inputmode="numeric" min="0"' + (CUSTOM ? ' max="' + CUSTOM_MAX + '"' : '') + ' step="1" data-cat-qty="' + esc(c.id) + '" value="' + esc(catQtyOf(c.id)) + '"></label>';
        }).join('') + '</div><p class="ch-bulk-cattotal" data-family-total></p>'
          : '<p class="ch-bulk-alert" data-family-none>Choose ' + esc(listText(cats.map(function (c) { return c.label || c.id; }), 'or')) + ' to enter your quantities.</p>');
    } else {
      $all('[data-cat-qty]', box).forEach(function (i) { if (document.activeElement !== i) i.value = catQtyOf(i.getAttribute('data-cat-qty')); });
    }
    var tot = $('[data-family-total]', box);
    if (tot) {
      var n = parseInt(state.quantity, 10) || 0;
      var tierTxt = CUSTOM ? '' : q && q.priced ? tierRange(q.tier) + ' price tier' : q && q.customQuote ? 'Custom Volume Pricing' : '';
      // 1–9 page: no tiers — the combined total is capped at CUSTOM_MAX (Amber, Oct 3 2026).
      setHtml(tot, '<span>Total ' + esc(fam) + ': <strong data-family-total-n>' + n + '</strong></span>' +
        (tierTxt ? '<span class="ch-bulk-cattotal__tier" data-family-tier>' + esc(tierTxt) + '</span>'
          : CUSTOM ? '<small data-family-cap>1 to ' + CUSTOM_MAX + ' in total</small>' : '<small>Minimum ' + config.minimum_quantity + ' in total</small>'));
    }
  }
  function renderQuantity() {
    var prod = currentProduct();
    var input = $('[data-quantity]');
    input.min = CUSTOM ? 1 : config.minimum_quantity;
    if (CUSTOM) input.max = CUSTOM_MAX;
    if (document.activeElement !== input) input.value = state.quantity;
    var minNode = $('[data-min-qty]');
    if (minNode) minNode.textContent = CUSTOM ? 1 : config.minimum_quantity;
    var ladder = $('[data-tier-ladder]') || document.createElement('ul');
    var feedback = $('[data-quantity-feedback]');
    if (!prod) { setHtml(ladder, ''); setHtml(feedback, ''); return; }
    if (CUSTOM) {
      setHtml(ladder, '');
      // Families on the 1–9 page (Amber, Oct 3 2026): same "Who are these for?" picker as Bulk, no tiers or estimate.
      renderCategoryPicker(prod, null);
      var cfc = familyCats(prod) ? activeCats() : null;
      var n = parseInt(state.quantity, 10);
      var cmsg;
      if (cfc && !cfc.length) {
        cmsg = '';
      } else if (state.quantity === '' || state.quantity === null) {
        cmsg = cfc
          ? '<p class="ch-bulk-note">Enter how many ' + esc(prod.unit_plural) + ' you need in each group — 1 to ' + CUSTOM_MAX + ' in total. You’ll split them by color and size next.</p>'
          : '<p class="ch-bulk-note">Enter how many ' + esc(prod.unit_plural) + ' you need — 1 to ' + CUSTOM_MAX + '. You’ll split them by color and size next.</p>';
      } else if (!/^\d+$/.test(String(state.quantity)) || n < 1) {
        cmsg = '<p class="ch-bulk-alert">Enter at least 1 piece.</p>';
      } else if (n > CUSTOM_MAX) {
        cmsg = '<p class="ch-bulk-callout" data-over-max><strong>Ordering ' + (CUSTOM_MAX + 1) + ' or more?</strong> ' +
          (cfc && cfc.length > 1 ? 'Your combined ' + esc(familyLabel(prod)) + ' total is ' + n + '. ' : '') + 'Bulk pricing starts at ' + (CUSTOM_MAX + 1) +
          ' pieces — use <a href="' + BULK_URL + '">Bulk Orders (10+)</a> to see your price as you go.</p>';
      } else if (EST) {
        // 1–9 live estimate: the figures for the pieces entered so far (no tiers, ladder or nudges on this page).
        var cf = figures(currentQuote());
        cmsg = cf ? figuresHtml(cf, 'ch-bulk-figures--inline') + '<p class="ch-bulk-note">Your estimate updates as you choose colors, sizes, and placement. No payment is taken today.</p>'
          : '<p class="ch-bulk-callout">Your estimate updates as you choose colors, sizes, and placement. No payment is taken today.</p>';
      } else {
        cmsg = '<p class="ch-bulk-callout">We’ll email your price after we review your request. No payment is taken today.</p>';
      }
      setHtml(feedback, cmsg);
      return;
    }
    var q = currentQuote();
    renderCategoryPicker(prod, q);
    var fcats = familyCats(prod) ? activeCats() : null;
    var multi = !!fcats && fcats.length > 1;
    var tiers = Engine.sortedTiers(prod);
    var rows = tiers.map(function (t, i) {
      var active = q.priced && q.tierIndex === i;
      // Families: each chosen category's own price in this tier (one category: its price, e.g. Youth $16/ea).
      var price = !fcats || !fcats.length ? '<strong>' + esc(fmtEach(t.price)) + '/ea</strong>'
        : !multi ? '<strong>' + esc(fmtEach(Engine.categoryTierPrice(fcats[0].prod, i))) + '/ea</strong>'
        : fcats.map(function (c) {
          return '<span class="ch-bulk-ladder__cat"><span>' + esc(c.label) + '</span><strong>' + esc(fmtEach(Engine.categoryTierPrice(c.prod, i))) + '</strong></span>';
        }).join('');
      return '<li class="' + (active ? 'is-active' : '') + (multi ? ' ch-bulk-ladder__multi' : '') + '"><span>' + tierRange(t) + '</span>' + price + '</li>';
    });
    rows.push('<li class="' + (q.customQuote ? 'is-active' : '') + '"><span>' + config.custom_quote_threshold + '+</span><strong>Custom Volume Pricing</strong></li>');
    setHtml(ladder, rows.join(''));
    var fam = familyLabel(prod);
    var msg = '';
    if (fcats && !fcats.length) {
      msg = '';
    } else if (state.quantity === '' || state.quantity === null) {
      msg = fcats
        ? '<p class="ch-bulk-note">Enter how many ' + esc(prod.unit_plural) + ' you need in each group. Your price tier comes from your combined ' + esc(fam) + ' total.</p>'
        : '<p class="ch-bulk-note">Enter the total number of ' + esc(prod.unit_plural) + ' for your order. Your price per piece is based on the total quantity across all sizes.</p>';
    } else if (q.tierStatus === 'below_minimum') {
      // Cross-link to the 1–9 page (Amber, Oct 3 2026).
      msg = '<p class="ch-bulk-alert" data-under-min-link>Bulk orders start at ' + config.minimum_quantity + ' pieces' + (multi ? ' in total' : '') + '. ' +
        'Need fewer than ' + config.minimum_quantity + '? <a href="' + CUSTOM_URL + '">Custom Orders (1–9)<span aria-hidden="true"> →</span></a></p>';
    } else if (q.customQuote) {
      msg = '<p class="ch-bulk-callout"><strong>Custom Volume Pricing.</strong> Orders of ' + config.custom_quote_threshold + '+ pieces' + (multi ? ' (combined ' + esc(fam) + ' total)' : '') +
        ' receive a custom quote. Finish building your order so we have everything we need to quote it.</p>';
    } else if (q.priced) {
      // Amber, Oct 3 2026: the pre-choice note ("Price is for sizes S–L with one print location. XL and up add …") until the
      // order's choices add something, then the "why this is an average" line — replaces "… may change your price per piece."
      var qf = figures(q);
      var qHint = priceExplain(prod, q, qf, 'quantity');
      var incl = esc(inclLabel());
      msg = '<div class="ch-bulk-callout ch-bulk-callout--price">' + figuresHtml(qf, 'ch-bulk-figures--inline') +
        '<p class="ch-bulk-excl ch-bulk-excl--small">Excludes shipping &amp; sales tax</p>' +
        (multi
          ? '<p class="ch-bulk-callout__sub" data-family-rates>Your combined ' + q.quantity + ' ' + esc(fam) + ' are priced in the ' + tierRange(q.tier) + ' tier: ' +
            esc(ratesText(q)) + '.' + (qHint ? ' ' + esc(qHint) : ' Includes ' + incl + '.') + '</p>'
          : '<p class="ch-bulk-callout__sub" data-price-note>Bulk pricing for ' + tierRange(q.tier) + ' ' + esc(prod.unit_plural) +
            (qHint ? '. ' + esc(qHint) : ' includes ' + incl + '.') + '</p>') +
        '</div>';
      if (q.next && q.next.nudge) {
        var buy = q.next.type === 'tier' ? buyUpText(prod, q) : '';
        msg += q.next.type === 'tier'
          ? (buy ? '<p class="ch-bulk-nudge" data-buy-up>' + esc(buy) + '</p>'
            : '<p class="ch-bulk-nudge">Add ' + q.next.remaining + ' more ' + esc(units(prod, q.next.remaining)) + ' to unlock the next bulk price: ' + esc(nextPriceText(q)) + '.</p>')
          : '<p class="ch-bulk-nudge" data-quote-nudge>' + esc(quoteNudgeText()) + '</p>';
      }
    }
    setHtml(feedback, msg);
  }

  /* ---------- Step 3: colors (choose one or more) ---------- */
  // Swatch values: a color key (keyFor(token, name)) or keyFor(token, '__other') for that category's "Another color".
  function isOtherValue(value) { return splitKey(value).base === '__other'; }
  function swatchHtml(value, name, hex, on) {
    var other = isOtherValue(value);
    return '<label class="ch-bulk-swatch' + (other ? ' ch-bulk-swatch--other' : '') + '"><input type="checkbox" name="bulk_colors" value="' + esc(value) + '"' + (on ? ' checked' : '') + '>' +
      '<span class="ch-bulk-swatch__chip' + (other ? ' ch-bulk-swatch__chip--other' : '') + '"' + (other ? '' : ' style="background:' + esc(hex) + '"') + '>' + (other ? '+' : '') +
      '<span class="ch-bulk-swatch__check" aria-hidden="true">✓</span></span><span class="ch-bulk-swatch__name">' + esc(name) + '</span></label>';
  }
  function swatchesHtml(cat) {
    return (cat.prod.colors || []).map(function (c) { var k = keyFor(cat.token, c.name); return swatchHtml(k, c.name, c.hex, state.colors.indexOf(k) !== -1); }).join('') +
      swatchHtml(keyFor(cat.token, '__other'), 'Another color', '', !!state.otherBy[cat.token]);
  }
  function syncSwatches(scope) {
    $all('input[name="bulk_colors"]', scope).forEach(function (input) {
      var k = splitKey(input.value);
      input.checked = k.base === '__other' ? !!state.otherBy[k.token] : state.colors.indexOf(input.value) !== -1;
    });
  }
  // Typed "Another color" boxes for one category (token); up to MAX_CUSTOM_COLORS per category.
  function renderCustomColors(list, add, token) {
    if (!list) return;
    var mine = customsOf(token);
    var key = token + ':' + mine.map(function (c) { return c.id; }).join(',');
    if (list.getAttribute('data-key') !== key || !list.firstChild) {
      list.setAttribute('data-key', key);
      var many = mine.length > 1;
      list.innerHTML = mine.map(function (c, i) {
        return '<div class="ch-bulk-custom"><label class="ch-bulk-field"><span>' + (many ? 'Color ' + (i + 1) : 'Color you’d like') + '</span>' +
          '<input type="text" maxlength="80" data-custom-color="' + esc(c.id) + '" value="' + esc(c.name) + '" placeholder="e.g. Heather Royal, Kelly Green"></label>' +
          (many ? '<button type="button" class="ch-bulk-copy ch-bulk-custom__remove" data-custom-remove="' + esc(c.id) + '" aria-label="Remove color ' + (i + 1) + '">Remove</button>' : '') + '</div>';
      }).join('');
    } else {
      $all('[data-custom-color]', list).forEach(function (input) {
        var c = state.customColors.filter(function (x) { return x.id === input.getAttribute('data-custom-color'); })[0];
        if (c && document.activeElement !== input) input.value = c.name;
      });
    }
    if (add) { add.setAttribute('data-custom-add', token); add.hidden = mine.length >= MAX_CUSTOM_COLORS; }
  }
  // If some listed colors stop short of the largest size, say which colors carry it ('' when every color has every size).
  function sizeNoteHtml(cp) {
    var all = cp.sizes || [];
    var colors = cp.colors || [];
    var largest = all[all.length - 1];
    var limited = colors.filter(function (c) { return Engine.sizesFor(cp, c.name).indexOf(largest) === -1; });
    if (!all.length || !limited.length) return '';
    var full = colors.filter(function (c) { return limited.indexOf(c) === -1; }).map(function (c) { return c.name; });
    var limitedNames = esc(listText(limited.map(function (c) { return c.name; }), 'and'));
    var upTo = esc(Engine.sizesFor(cp, limited[0].name).slice(-1)[0]);
    // Long bulk color lists (Oct 2026): name only the exceptions instead of every full-range color.
    return full.length > 8
      ? '<strong>Need ' + esc(largest) + '?</strong> It’s commonly available in every listed color except ' + limitedNames + ', which ' +
        (limited.length === 1 ? 'is' : 'are') + ' commonly available up to ' + upTo + '.'
      : '<strong>Need ' + esc(largest) + '?</strong> It’s commonly available in ' + esc(listText(full)) + '. ' +
        limitedNames + ' ' + (limited.length === 1 ? 'is' : 'are') + ' commonly available up to ' + upTo + '.';
  }
  function setNote(node, html) { if (!node) return; node.innerHTML = html; node.hidden = !html; }
  // Products without categories and family orders with ONE category use the page's own Step 3 markup ([data-colors],
  // [data-color-other] …). A family order with 2+ categories gets one block per category ([data-cat-colors=token]),
  // each with its own swatches, its own "Another color" and its own size note.
  function renderColors() {
    var prod = currentProduct();
    var box = $('[data-colors]');
    var sizeNote = $('[data-color-size-note]');
    var picked = $('[data-colors-selected]');
    var other = $('[data-color-other]');
    var fam = $('[data-family-colors]');
    var cats = prod ? activeCats() : [];
    function hideFamily() { if (fam) { fam.hidden = true; fam.innerHTML = ''; fam.removeAttribute('data-key'); } }
    if (!prod || !cats.length) {
      hideFamily();
      box.hidden = false;
      box.removeAttribute('data-for');
      box.innerHTML = prod ? '<p class="ch-bulk-note">Choose who your ' + esc(prod.unit_plural) + ' are for on the Quantity step first.</p>' : '';
      setNote(sizeNote, '');
      if (other) other.hidden = true;
      if (picked) picked.textContent = '';
      return;
    }
    if (cats.length > 1) {
      box.hidden = true;
      box.removeAttribute('data-for');
      box.innerHTML = '';
      setNote(sizeNote, '');
      if (other) other.hidden = true;
      if (!fam) {
        fam = document.createElement('div');
        fam.className = 'ch-bulk-catcolors-wrap';
        fam.setAttribute('data-family-colors', '');
        box.parentNode.insertBefore(fam, box);
      }
      fam.hidden = false;
      // Rebuilt only when the product or the chosen categories change, so focus stays on the checkbox just toggled.
      var fkey = prod.id + '|' + cats.map(function (c) { return c.id; }).join(',');
      if (fam.getAttribute('data-key') !== fkey) {
        fam.setAttribute('data-key', fkey);
        fam.innerHTML = cats.map(function (cat) {
          var low = cat.label.toLowerCase();
          return '<section class="ch-bulk-catcolors" data-cat-colors="' + esc(cat.token) + '">' +
            '<h4 class="ch-bulk-catgroup__title"><span>' + esc(cat.label + ' colors') + '</span><small data-cat-colors-meta></small></h4>' +
            '<div class="ch-bulk-swatches" role="group" aria-label="' + esc(cat.label + ' garment colors') + '">' + swatchesHtml(cat) + '</div>' +
            '<p class="ch-bulk-note" data-cat-size-note hidden></p>' +
            '<div class="ch-bulk-other" data-cat-other hidden>' +
            '<p class="ch-bulk-note"><strong>Looking for another ' + esc(low) + ' color?</strong> Enter each color you’d like below. ' +
            'Availability is subject to supplier confirmation — we’ll confirm color and size availability before your order is finalized.</p>' +
            '<div class="ch-bulk-custom-list" data-cat-custom-list></div>' +
            '<button type="button" class="ch-bulk-copy ch-bulk-custom-add" data-custom-add="' + esc(cat.token) + '">+ Add another ' + esc(low) + ' color</button>' +
            '</div></section>';
        }).join('');
      }
      var all = selectedColors();
      cats.forEach(function (cat) {
        var sec = byAttr('data-cat-colors', cat.token);
        if (!sec) return;
        syncSwatches(sec);
        var n = all.filter(function (c) { return c.token === cat.token; }).length;
        var qn = catQtyOf(cat.id);
        $('[data-cat-colors-meta]', sec).textContent = 'Sizes ' + catSizeText(cat.prod) + (/^\d+$/.test(qn) ? ' · ' + qn + ' ' + units(cat.prod, parseInt(qn, 10)) : '') +
          ' · ' + (n ? n + (n === 1 ? ' color' : ' colors') + ' selected' : 'none selected yet');
        setNote($('[data-cat-size-note]', sec), sizeNoteHtml(cat.prod));
        $('[data-cat-other]', sec).hidden = !state.otherBy[cat.token];
        renderCustomColors($('[data-cat-custom-list]', sec), $('[data-custom-add]', sec), cat.token);
      });
    } else {
      hideFamily();
      box.hidden = false;
      var cat = cats[0];
      // Rebuilt only when the product (or the one category) changes, so keyboard focus stays on the toggled checkbox.
      var bkey = prod.id + (cat.token ? '|' + cat.token : '');
      if (box.getAttribute('data-for') !== bkey || !box.firstChild || !box.querySelector('input')) {
        box.setAttribute('data-for', bkey);
        box.innerHTML = swatchesHtml(cat);
      } else {
        syncSwatches(box);
      }
      if (other) other.hidden = !state.otherBy[cat.token];
      renderCustomColors($('[data-custom-list]'), $('[data-custom-add]'), cat.token);
      setNote(sizeNote, sizeNoteHtml(cat.prod));
    }
    var sel = selectedColors();
    if (picked) {
      picked.textContent = sel.length ? sel.length + (sel.length === 1 ? ' color' : ' colors') + ' selected: ' + colorsText()
        : 'No colors selected yet — choose every color you need.';
      picked.className = 'ch-bulk-selected' + (sel.length ? '' : ' is-empty');
    }
  }

  /* ---------- Step 4: size breakdown, one card per color ---------- */
  var openCards = {};   // color key -> false once the customer collapses that card (this page view only)
  // Listed sizes are commonly available options, never a guarantee; a customer-typed color shows every size so the
  // real breakdown is kept (never "count X under Y").
  function availabilityNote(prod, c, offered) {
    prod = catProdOf(c) || prod;   // families: the category's own sizes ("Black youth tees … XS–L")
    if (!(prod.sizes || []).length) return '';
    if (c.custom) {
      return '<strong>All sizes are shown' + (c.name ? ' for ' + esc(c.name) : '') + '.</strong> Availability is subject to supplier confirmation — ' +
        'we’ll confirm color and size availability before your order is finalized.';
    }
    var missing = prod.sizes.filter(function (s) { return offered.indexOf(s) === -1; });
    var note = esc(c.name + ' ' + (c.token && c.categoryLabel ? c.categoryLabel.toLowerCase() + ' ' : '') + prod.unit_plural) + ' are commonly available in ' + esc(sizeRange(offered)) + '.';
    if (missing.length) note += ' Need ' + esc(listText(missing)) + '? See which colors commonly carry ' + (missing.length === 1 ? 'it' : 'them') + ' on the Color step.';
    return note;
  }
  function chipHtml(c, cls) {
    return c.custom ? '<span class="' + cls + ' ' + cls + '--other" aria-hidden="true">+</span>'
      : '<span class="' + cls + '" style="background:' + esc(c.hex || '#fff') + '" aria-hidden="true"></span>';
  }
  function colorCardHtml(prod, c, i) {
    var offered = offeredFor(c);
    var m = sizesOf(c.key);
    var open = openCards[c.key] !== false;
    var name = colorName(c);
    var aria = colorLabel(c);   // "Youth — Black" when the order has 2+ categories
    var fields = (offered.length ? offered : ['One size']).map(function (s) {
      var label = s === 'One size' ? 'Quantity' : s;
      return '<label class="ch-bulk-size"><span class="ch-bulk-size__name">' + esc(label) + '</span>' +
        (EST && s !== 'One size' ? '<span class="ch-bulk-size__price" data-size-price="' + esc(s) + '"></span>' : '') +
        '<input type="number" inputmode="numeric" min="0" step="1" data-size-input="' + esc(s) + '" data-color-key="' + esc(c.key) + '" value="' + esc(m[s] || '') + '"' +
        ' aria-label="' + esc(aria + (s === 'One size' ? ' — quantity' : ' — quantity in size ' + s)) + '"></label>';
    }).join('');
    var note = availabilityNote(prod, c, offered);
    return '<section class="ch-bulk-ccard' + (open ? ' is-open' : '') + '" data-ccard="' + esc(c.key) + '">' +
      '<button type="button" class="ch-bulk-ccard__head" data-ccard-toggle="' + esc(c.key) + '" aria-expanded="' + open + '" aria-controls="ch-bulk-ccard-' + i + '">' +
      chipHtml(c, 'ch-bulk-ccard__chip') +
      '<span class="ch-bulk-ccard__title"><span class="ch-bulk-ccard__name">' + esc(name) + (c.custom ? ' <small>Another color</small>' : '') + '</span>' +
      '<span class="ch-bulk-ccard__sum" data-ccard-sum></span></span>' +
      '<span class="ch-bulk-ccard__sub" data-ccard-sub></span><span class="ch-bulk-ccard__chev" aria-hidden="true"></span></button>' +
      '<div class="ch-bulk-ccard__body" id="ch-bulk-ccard-' + i + '"' + (open ? '' : ' hidden') + '>' +
      '<div class="ch-bulk-sizes">' + fields + '</div>' + (note ? '<p class="ch-bulk-ccard__note">' + note + '</p>' : '') + '</div></section>';
  }
  // Size prices (Amber, Oct 3 2026 — "show each size's price"): one garment size's price per piece = its category's rate in
  // the tier picked by the combined quantity + that size's size_surcharges amount. One print location is included (print
  // sizes carry no charge since config 2026-10-03 size prices). null while the order has no price (below the minimum,
  // Custom Volume Pricing, 1–9 page without custom_estimate). The 1–9 estimate page shows the same labels (one 1–9 tier).
  function sizeUnitCents(q, c, size) {
    if (!EST || !q || !q.priced || q.tierIndex === null || q.tierIndex === undefined) return null;
    var cp = catProdOf(c);
    if (!cp) return null;
    var tiers = (cp.tiers || []).slice().sort(function (a, b) { return a.min - b.min; });
    var t = tiers[q.tierIndex];
    if (!t) return null;
    return Engine.toCents(t.price) + Engine.toCents((cp.size_surcharges || {})[size]);
  }
  function fmtUnit(cents) { return cents % 100 === 0 ? '$' + (cents / 100) : fmt(cents); }
  function renderSizePrices(q) {
    var sel = selectedColors();
    sel.forEach(function (c) {
      var card = byAttr('data-ccard', c.key);
      if (!card) return;
      $all('[data-size-price]', card).forEach(function (el) {
        var cents = sizeUnitCents(q, c, el.getAttribute('data-size-price'));
        var text = cents === null ? '' : fmtUnit(cents);
        if (el.textContent !== text) el.textContent = text;
        if (el.hidden !== !text) el.hidden = !text;
      });
    });
  }
  // Note under the size cards: what the size prices include, the placement add-on (flat per piece), and how pieces without
  // a size yet are counted. '' when there is nothing priced to explain.
  function sizePriceNote(prod, q, diff) {
    if (!EST || !q || !q.priced || !(prod.sizes || []).length) return '';
    var parts = [];
    // Bulk with a second print location: what the prices include + the add-on sit above the size cards instead.
    if (!sizePlacementNote(prod, q)) {
      parts.push('Prices shown are per piece and include one print location.');
      if (q.print.placement && q.print.perItemCents > 0) {
        parts.push(q.print.placement.label + ' adds ' + fmtUnit(q.print.perItemCents) + ' per piece.');
      }
    }
    if (diff > 0 && q.sizeTotal > 0) {
      var cats = q.categoryRows || [];
      parts.push(cats.length > 1
        ? 'Pieces without a size yet are counted at their group’s lowest price until you enter them.'
        : 'Pieces without a size yet are counted at ' + fmtUnit(cats.length ? cats[0].unitCents : q.unitBaseCents) + ' until you enter them.');
    }
    return parts.join(' ');
  }
  function toggleCard(key) {
    var card = byAttr('data-ccard', key);
    if (!card) return;
    var open = !card.classList.contains('is-open');
    openCards[key] = open;
    card.classList.toggle('is-open', open);
    var head = card.querySelector('[data-ccard-toggle]');
    var body = card.querySelector('.ch-bulk-ccard__body');
    if (head) head.setAttribute('aria-expanded', String(open));
    if (body) body.hidden = !open;
  }
  // "Total assigned" bar + live estimate. Built once, then updated in place so typing never loses focus or flickers.
  function renderAssign(prod, q, show) {
    var tally = $('[data-size-tally]');
    if (!tally) return;
    if (!show) { tally.innerHTML = ''; return; }
    if (!tally.querySelector('[data-assign]')) {
      tally.innerHTML = '<p class="ch-bulk-alert" data-size-notice hidden></p>' +
        '<div class="ch-bulk-assign" data-assign>' +
        '<div class="ch-bulk-assign__row"><span class="ch-bulk-assign__label" data-assign-count></span>' +
        '<span class="ch-bulk-assign__msg" data-assign-msg aria-live="polite"></span></div>' +
        '<div class="ch-bulk-assign__meter" aria-hidden="true"><span data-assign-meter></span></div>' +
        '<div class="ch-bulk-assign__est" data-assign-est aria-live="polite"></div></div>' +
        '<p class="ch-bulk-note" data-ext-hint hidden></p>';
    }
    var total = parseInt(state.quantity, 10) || 0;
    var diff = total - q.sizeTotal;
    var bar = $('[data-assign]', tally);
    bar.className = 'ch-bulk-assign' + (diff === 0 && total > 0 ? ' is-complete' : diff < 0 ? ' is-over' : '');
    setHtml($('[data-assign-count]', tally), (multiCat() ? 'Total ' + esc(familyLabel(prod)) + ' assigned: ' : 'Total assigned: ') +
      '<strong>' + q.sizeTotal + ' / ' + total + '</strong>');
    $('[data-assign-msg]', tally).textContent = diff === 0 && total > 0 ? 'All ' + total + ' ' + units(prod, total) + ' assigned'
      : diff > 0 ? diff + ' left to assign' : (-diff) + ' over your total — adjust your sizes or update your quantity';
    $('[data-assign-meter]', tally).style.width = (total ? Math.min(100, Math.round(q.sizeTotal / total * 100)) : 0) + '%';
    var f = figures(q);
    var est = f ? figuresHtml(f, 'ch-bulk-figures--inline') : (!CUSTOM && q.customQuote) ? '<p class="ch-bulk-assign__quote">Custom Volume Pricing</p>' : '';
    setHtml($('[data-assign-est]', tally), est);
    var notice = $('[data-size-notice]', tally);
    notice.textContent = sizeNotice;
    notice.hidden = !sizeNotice;
    var hint = $('[data-ext-hint]', tally);
    var hintText = sizePriceNote(prod, q, diff);
    // No "may change your price" hint on Bulk without a price (500+ custom quote) — Amber, Oct 3 2026.
    if (!hintText && EST && !q.priced && CUSTOM) {
      var extFrom = autoMode(prod) ? autoPricedFrom(prod) : extendedFrom(prod, orderSizes());
      hintText = extFrom ? extFrom + '+ sizes may change your price per piece — your estimate updates as you enter sizes.' : '';
    }
    setHtml(hint, esc(hintText));   // in place: this note changes while the customer types (iPhone keyboard)
    hint.hidden = !hintText;
  }
  // Bulk Step 4 (Amber, Oct 3 2026): "Prices below include one print location. Your selected Front + Back placement adds $4
  // per piece." right above the size cards once a second print location is chosen. Updated in place (typing keeps focus).
  function renderSizePlacementNote(prod, q, show) {
    var grid = $('[data-size-grid]');
    if (!grid) return;
    var node = $('[data-size-placement-note]');
    var text = show && prod && q ? sizePlacementNote(prod, q) : '';
    if (!node) {
      if (!text) return;
      node = document.createElement('p');
      node.className = 'ch-bulk-sizenote';
      node.setAttribute('data-size-placement-note', '');
      grid.parentNode.insertBefore(node, grid);
    }
    if (node.textContent !== text) node.textContent = text;
    if (node.hidden !== !text) node.hidden = !text;
  }
  function renderSizes() {
    var prod = currentProduct();
    var grid = $('[data-size-grid]');
    var intro = $('[data-size-intro]');
    if (!prod) { grid.innerHTML = ''; grid.removeAttribute('data-key'); renderSizePlacementNote(null, null, false); renderAssign(null, null, false); return; }
    var sel = selectedColors();
    var oneSize = !(prod.sizes || []).length;
    var q = currentQuote();
    var cats = activeCats();
    var multi = cats.length > 1;
    if (intro) {
      intro.textContent = multi && CUSTOM
        ? 'Enter how many you need in each size for each color. Each group’s sizes must add up to its quantity' +
          (q.quantity ? ' (' + q.quantity + ' ' + familyLabel(prod) + ' in total)' : '') + '.'
        : multi
        ? 'Enter how many you need in each size for each color. Each group’s sizes must add up to its quantity; your price tier is based on your combined total' +
          (q.quantity ? ' of ' + q.quantity + ' ' + familyLabel(prod) : '') + '.'
        : sel.length > 1
        ? (oneSize ? 'Enter how many you need in each color.' : 'Enter how many you need in each size for each color.') +
          (CUSTOM ? ' All colors together must add up to your total' + (q.quantity ? ' of ' + q.quantity : '') + '.'
            : ' Your price per piece is based on your combined total' + (q.quantity ? ' of ' + q.quantity + ' ' + prod.unit_plural : '') + ' across all colors.')
        : 'Enter how many you need in each size. Sizes must add up to your total quantity.';
      intro.hidden = oneSize && sel.length <= 1;
    }
    if (!sel.length || (oneSize && sel.length === 1)) {
      grid.removeAttribute('data-key');
      grid.innerHTML = !sel.length ? '<p class="ch-bulk-note">Choose your colors on the Color step first.</p>'
        : '<p class="ch-bulk-note">' + esc(prod.name) + ' is one size — no size breakdown needed' + (q.quantity ? '. All ' + q.quantity + ' will be ' + esc(colorName(sel[0])) : '') + '.</p>';
      renderSizePlacementNote(prod, q, false);
      renderAssign(null, null, false);
      return;
    }
    renderSizePlacementNote(prod, q, true);
    var key = JSON.stringify([prod.id, multi, sel.map(function (c) { return [c.key, colorName(c), offeredFor(c).join(',')]; })]);
    if (grid.getAttribute('data-key') !== key) {
      grid.setAttribute('data-key', key);
      if (multi) {
        // Families with 2+ categories: one group per category ("Youth — 50 · assigned 40 / 50"), then its color cards.
        var idx = 0;
        grid.innerHTML = cats.map(function (cat) {
          var mine = sel.filter(function (c) { return c.token === cat.token; });
          return '<div class="ch-bulk-catgroup" data-cat-sizes="' + esc(cat.token) + '">' +
            '<h4 class="ch-bulk-catgroup__title"><span data-cat-name>' + esc(cat.label) + '</span><small data-cat-assign></small></h4>' +
            (mine.length ? mine.map(function (c) { return colorCardHtml(prod, c, idx++); }).join('')
              : '<p class="ch-bulk-note">Choose ' + esc(cat.label.toLowerCase()) + ' colors on the Color step first.</p>') + '</div>';
        }).join('');
      } else {
        grid.innerHTML = sel.map(function (c, i) { return colorCardHtml(prod, c, i); }).join('');
      }
    }
    if (multi) {
      cats.forEach(function (cat) {
        var g = byAttr('data-cat-sizes', cat.token);
        if (!g) return;
        var cr = q.categoryRows.filter(function (r) { return r.id === cat.id; })[0];
        var want = cr ? cr.quantity : (parseInt(catQtyOf(cat.id), 10) || 0);
        var got = cr ? cr.sizeTotal : 0;
        g.className = 'ch-bulk-catgroup' + (want > 0 && got === want ? ' is-complete' : got > want ? ' is-over' : '');
        g.querySelector('[data-cat-name]').textContent = cat.label + ' — ' + want;
        g.querySelector('[data-cat-assign]').textContent = (want > 0 && got === want ? '✓ ' : '') + 'Assigned ' + got + ' / ' + want;
      });
    }
    q.colorRows.forEach(function (row) {
      var card = byAttr('data-ccard', row.key);
      if (!card) return;
      var sub = card.querySelector('[data-ccard-sub]');
      sub.textContent = 'Subtotal: ' + row.total;
      sub.className = 'ch-bulk-ccard__sub' + (row.total ? ' has-qty' : '');
      card.querySelector('[data-ccard-sum]').textContent = sizeLine(row) || (oneSize ? 'No quantity yet' : 'No sizes yet');
    });
    renderSizePrices(q);
    renderAssign(prod, q, true);
  }

  /* ---------- Step 5A: placement (Placement & Artwork step) ---------- */
  function renderPlacements() {
    var prod = currentProduct();
    var box = $('[data-placements]');
    var sizeBox = $('[data-print-sizes]');
    if (!prod) { setHtml(box, ''); setHtml(sizeBox, ''); return; }
    setHtml(box, prod.placements.map(function (id) {
      var pl = Engine.placement(config, id);
      if (!pl) return '';
      var nLoc = (pl.full_prints || []).length + (pl.left_chest ? 1 : 0);
      // Cards show only the number of print locations (Amber, Oct 2 2026): no "Included"/"Added cost" here, and in bulk
      // mode single-location titles read "<Location> Design Only" so Front and Back don't look like they come together.
      // Card wording only — the review, email and pricing keep the config labels. Both pages since Oct 3 2026 (1–9 parity).
      var note = nLoc > 1 ? nLoc + ' print locations' : '1 print location';
      // Bulk (Amber, Oct 3 2026 21:46 UTC — supersedes the Oct 2 "no Included / added cost" rule on this page): the add-on
      // per piece from the config ("2 print locations · +$4 per piece"; totes "+$4 per tote"), else "· included".
      if (!CUSTOM) {
        var addOn = placementAddOnCents(prod, pl.id);
        note += addOn > 0 ? ' · +' + fmtUnit(addOn) + ' per ' + ((prod.sizes || []).length ? 'piece' : prod.unit_singular) : ' · included';
      }
      var title = nLoc === 1 ? pl.label + ' Design Only' : pl.label;
      return '<label class="ch-bulk-option"><input type="radio" name="bulk_placement" value="' + esc(pl.id) + '"' + (state.placementId === pl.id ? ' checked' : '') + '>' +
        '<span class="ch-bulk-option__body"><span class="ch-bulk-option__title">' + esc(title) + '</span><span class="ch-bulk-option__meta">' + esc(note) + '</span></span></label>';
    }).join(''));
    var pl = Engine.placement(config, state.placementId);
    if (!pl || prod.placements.indexOf(pl.id) === -1) { setHtml(sizeBox, ''); return; }
    if (CUSTOM || autoMode(prod)) {
      // Amber, Oct 2 2026: no print-size choice — we size each design to each garment size (internal mapping in the config).
      // Callout wording for auto mode: Amber, Oct 3 2026 ("garment sizes" — customers no longer pick a print size).
      // 1–9 page (Amber, Oct 3 2026): the sizing note only — no chips, no left-chest size note, no estimate callout.
      setHtml(sizeBox, '<p class="ch-bulk-note" data-auto-size-note>' + esc(autoNote()) + '</p>' +
        (!EST ? '' : '<p class="ch-bulk-callout">Your estimated price will update automatically based on your selected print placement and garment sizes.</p>'));
      return;
    }
    var html = pl.full_prints.map(function (loc) {
      var current = state.printSizes[loc] || config.print_sizes[0].id;
      return '<fieldset class="ch-bulk-printsize"><legend>' + esc(loc) + ' print size</legend><div class="ch-bulk-printsize__options">' +
        config.print_sizes.map(function (ps) {
          var c = Engine.toCents(ps.surcharge);
          return '<label class="ch-bulk-chip"><input type="radio" name="bulk_print_' + esc(loc) + '" data-print-location="' + esc(loc) + '" value="' + esc(ps.id) + '"' + (current === ps.id ? ' checked' : '') + '>' +
            '<span>' + esc(ps.label) + (CUSTOM ? '' : '<small>' + (c > 0 ? 'Added cost' : 'Included') + '</small>') + '</span></label>';
        }).join('') + '</div></fieldset>';
    }).join('');
    if (pl.left_chest && config.left_chest && config.left_chest.label) html += '<p class="ch-bulk-note">Left chest prints are ' + esc(config.left_chest.label) + '.</p>';
    if (CUSTOM) { setHtml(sizeBox, html); return; }
    // One consistent note for every placement/size (Amber, Oct 2 2026) — no "included"/"added to your price" wording here.
    html += '<p class="ch-bulk-callout">Your estimated price will update automatically based on your selected print placement and size.</p>';
    setHtml(sizeBox, html);
  }

  /* ---------- Step 5B: artwork, one choice per print location ---------- */
  var METHODS = [
    { id: 'upload', title: 'Upload My Artwork', meta: 'I have a PNG or JPG ready.' },
    { id: 'canva', title: 'Create My Design in Canva', meta: 'I want to create my own design.' },
    { id: 'help', title: 'Help Me Design It', meta: 'I have an idea and need help bringing it to life.' }
  ];
  var HELP_FIELDS = [
    { key: 'wording', label: 'Wording / text', rows: 2, max: 600, placeholder: 'Exactly what it should say, e.g. “Grace Church Fall Retreat 2026”' },
    { key: 'theme', label: 'Theme / concept', max: 300, placeholder: 'e.g. mountains at sunrise, “faith over fear”, our team mascot' },
    { key: 'colors', label: 'Preferred colors', optional: true, max: 200, placeholder: 'e.g. navy and gold, or “match our logo”' },
    { key: 'style', label: 'General style', optional: true, max: 200, placeholder: 'e.g. modern, vintage, playful, elegant, bold' },
    { key: 'instructions', label: 'Additional instructions', rows: 3, max: 2000, placeholder: 'Anything else — logos to include, names, things to avoid.' }
  ];
  var REQUIRED_HELP = ['wording', 'theme', 'instructions'];
  var STATUS_LABEL = { uploaded: 'Uploaded', canva: 'Canva design provided', help: 'Design help requested', later: 'You’ll email this after you submit' };
  var INTERNAL_STATUS = { uploaded: 'Uploaded', canva: 'Canva design provided', help: 'Design help requested', later: 'NOT RECEIVED — customer will email it' };
  var uploads = {};      // uploader key ("<slot>|file" or "<slot>|inspo") -> runtime entries (checking/queued/uploading/error)
  var previews = {};     // Drive fileId -> object URL thumbnail for this page view only
  var queue = [];
  var activeUploads = 0;
  var entrySeq = 0;
  var ticker = null;
  var warmed = false;

  function cleanFile(f) {
    if (!f || typeof f !== 'object' || !f.fileId || !f.token) return null;
    return { name: String(f.name || 'artwork'), size: Number(f.size) || 0, type: String(f.type || ''), w: Number(f.w) || 0, h: Number(f.h) || 0,
      fileId: String(f.fileId), token: String(f.token), fileUrl: String(f.fileUrl || ''), folderUrl: String(f.folderUrl || '') };
  }
  function normalizeSlot(s) {
    s = s && typeof s === 'object' ? s : {};
    var help = s.help && typeof s.help === 'object' ? s.help : {};
    var out = { method: ['upload', 'canva', 'help'].indexOf(s.method) !== -1 ? s.method : '', file: cleanFile(s.file),
      canvaLink: typeof s.canvaLink === 'string' ? s.canvaLink : '', later: !!s.later, help: {},
      inspo: (Array.isArray(s.inspo) ? s.inspo : []).map(cleanFile).filter(Boolean).slice(0, ART.maxInspo) };
    HELP_FIELDS.forEach(function (f) { out.help[f.key] = typeof help[f.key] === 'string' ? help[f.key] : ''; });
    return out;
  }
  function slotState(key) {
    if (!state.art[key]) state.art[key] = normalizeSlot(null);
    return state.art[key];
  }
  function artLocations() {
    var prod = currentProduct();
    var pl = Engine.placement(config, state.placementId);
    if (!prod || !pl || prod.placements.indexOf(pl.id) === -1) return [];
    return Engine.printCharges(config, pl.id, state.printSizes).locations;
  }
  function makeSlot(locs, same) {
    var both = locs.join(' & ');
    return same ? { key: '__same:' + locs.join('+'), locations: locs, short: both, label: both + ' Artwork', same: true }
      : { key: locs[0], locations: locs, short: locs[0], label: locs[0] + ' Artwork', same: false };
  }
  function artSlots() {
    var locs = artLocations();
    if (locs.length > 1 && state.artSame) return [makeSlot(locs, true)];
    return locs.map(function (l) { return makeSlot([l], false); });
  }
  function slotByKey(key) {
    if (key === COLOR_ART) return { key: COLOR_ART, locations: [], short: 'Color versions', label: 'Color Variation Artwork', same: false };
    var current = artSlots().filter(function (s) { return s.key === key; })[0];
    if (current) return current;
    // A slot no longer on the order (placement changed mid-upload) still finishes under its own name.
    return key.indexOf('__same:') === 0 ? makeSlot(key.slice(7).split('+'), true) : makeSlot([key], false);
  }
  function parseKey(key) { var i = key.lastIndexOf('|'); return { slotKey: key.slice(0, i), inspo: key.slice(i + 1) === 'inspo' }; }
  function validLink(v) { return /^https:\/\/\S+$/i.test(String(v || '').trim()); }
  function helpFilled(s) { return REQUIRED_HELP.some(function (k) { return (s.help[k] || '').trim(); }); }
  function busyEntries(key) { return (uploads[key] || []).filter(function (e) { return e.status !== 'error'; }); }
  function slotBusy(slot) { var s = slotState(slot.key); return busyEntries(slot.key + (s.method === 'help' ? '|inspo' : '|file')).length > 0; }
  // uploaded | canva | help | later | '' (not finished)
  function slotStatus(slot) {
    var s = slotState(slot.key);
    if (s.method === 'upload') return s.file ? 'uploaded' : (s.later ? 'later' : '');
    if (s.method === 'canva') return (s.file || validLink(s.canvaLink)) ? 'canva' : (s.later ? 'later' : '');
    if (s.method === 'help') return helpFilled(s) ? 'help' : '';
    return '';
  }
  function fmtBytes(n) {
    return n >= 1048576 ? (n / 1048576).toFixed(n >= 104857600 ? 0 : 1) + ' MB' : Math.max(1, Math.round(n / 1024)) + ' KB';
  }
  function fileMeta(f) {
    return [f.type === 'image/png' ? 'PNG' : f.type === 'image/jpeg' ? 'JPG' : '', f.w && f.h ? f.w + ' × ' + f.h + ' px' : '', f.size ? fmtBytes(f.size) : '']
      .filter(Boolean).join(' · ');
  }
  function printSizeFor(slot, q) {
    return q.print.sizes.filter(function (s) { return !s.auto && slot.locations.indexOf(s.location) !== -1; })
      .map(function (s) { return (slot.locations.length > 1 ? s.location + ' ' : '') + s.label; }).join(' · ');
  }
  function byAttr(attr, value) {
    var list = root.querySelectorAll('[' + attr + ']');
    for (var i = 0; i < list.length; i++) { if (list[i].getAttribute(attr) === value) return list[i]; }
    return null;
  }
  function quoteName(n) { return '“' + n + '”'; }
  function ensureRequestNumber() {
    if (!REQUEST_RE.test(state.requestNumber || '')) { state.requestNumber = requestNumber(); persist(); }
    return state.requestNumber;
  }
  function warmUp() {
    if (warmed || !ART.url) return;
    warmed = true;
    try { fetch(ART.url, { credentials: 'omit' }).catch(function () {}); } catch (e) {}
  }

  /* ----- Step 5B HTML (rebuilt only when locations, methods, or print sizes change) ----- */
  function uploaderHtml(key, multi) {
    var k = esc(key);
    return '<div class="ch-art-up" data-up="' + k + '">' +
      '<input type="file" class="ch-art-up__input" accept="image/png,image/jpeg,.png,.jpg,.jpeg"' + (multi ? ' multiple' : '') + ' data-up-input="' + k + '" tabindex="-1" aria-hidden="true">' +
      '<div data-up-view="' + k + '"></div>' +
      '<p class="ch-art-up__msg" data-up-msg="' + k + '" role="status" aria-live="polite" hidden></p></div>';
  }
  function laterHtml(slot, s, what) {
    return '<label class="ch-art-later" data-art-later-wrap="' + esc(slot.key) + '"><input type="checkbox" data-art-later data-slot="' + esc(slot.key) + '"' + (s.later ? ' checked' : '') + '>' +
      '<span>I’ll email this ' + what + ' after I submit <small>You’ll get your request number and a one-tap email button.</small></span></label>';
  }
  function uploadPanel(slot, s) {
    return '<div class="ch-bulk-subpanel ch-art-panel">' +
      '<p class="ch-art-lead">PNG with a transparent background is preferred for the best print quality.</p>' +
      uploaderHtml(slot.key + '|file', false) +
      '<p class="ch-bulk-note">For best results, upload the highest-resolution version of your artwork available. We’ll review all artwork for print quality before production.</p>' +
      laterHtml(slot, s, 'artwork') + '</div>';
  }
  function canvaPanel(slot, s) {
    return '<div class="ch-bulk-subpanel ch-art-panel">' +
      '<ol class="ch-art-steps">' +
      '<li><span><strong>Create your design in Canva.</strong> It opens in a new tab — keep this page open.</span>' +
      '<a class="ch-bulk__button ch-bulk__button--ghost ch-art-canva" href="' + esc(ART.canvaUrl) + '" target="_blank" rel="noopener" data-canva-link>Open Canva <span aria-hidden="true">↗</span></a></li>' +
      '<li><span><strong>Download it as a PNG</strong> (or JPG) at the largest size Canva offers. A transparent-background PNG prints best — that download option requires Canva Pro.</span></li>' +
      '<li><span><strong>Come back to this tab</strong> and upload your file below, or paste your Canva share link.</span></li></ol>' +
      uploaderHtml(slot.key + '|file', false) +
      '<label class="ch-bulk-field ch-art-link"><span>Or paste your Canva share link <small>(optional if you upload a file)</small></span>' +
      '<input type="url" inputmode="url" maxlength="500" autocomplete="off" data-art-field="canvaLink" data-slot="' + esc(slot.key) + '" value="' + esc(s.canvaLink) + '" placeholder="https://www.canva.com/design/…"></label>' +
      '<p class="ch-bulk-note">In Canva, choose <strong>Share</strong>, set access to <strong>Anyone with the link</strong>, then copy the link. We’ll review your design for print quality before production.</p>' +
      laterHtml(slot, s, 'design') + '</div>';
  }
  function helpPanel(slot, s) {
    return '<div class="ch-bulk-subpanel ch-art-panel">' +
      '<p class="ch-bulk-note">Tell us what you have in mind. The wording, theme, or instructions is enough to get started — add whatever else you know.</p>' +
      '<div class="ch-art-help">' + HELP_FIELDS.map(function (f) {
        var attrs = ' maxlength="' + f.max + '" data-art-field="' + f.key + '" data-slot="' + esc(slot.key) + '" placeholder="' + esc(f.placeholder) + '"';
        return '<label class="ch-bulk-field' + (f.rows ? ' ch-art-help__wide' : '') + '"><span>' + esc(f.label) + (f.optional ? ' <small>(optional)</small>' : '') + '</span>' +
          (f.rows ? '<textarea rows="' + f.rows + '"' + attrs + '>' + esc(s.help[f.key]) + '</textarea>' : '<input type="text"' + attrs + ' value="' + esc(s.help[f.key]) + '">') + '</label>';
      }).join('') + '</div>' +
      '<div class="ch-art-inspo"><p class="ch-art-label">Inspiration images <small>(optional · up to ' + ART.maxInspo + ' PNG or JPG)</small></p>' +
      '<p class="ch-bulk-note">Logos, photos, sketches, or designs you love.</p>' +
      uploaderHtml(slot.key + '|inspo', true) + '</div>' +
      '<p class="ch-bulk-info">Design assistance may require an additional fee depending on complexity. We’ll confirm any design charge before work begins.</p></div>';
  }
  function slotHtml(slot, i, q) {
    var s = slotState(slot.key);
    var k = esc(slot.key);
    var size = printSizeFor(slot, q);
    var html = '<section class="ch-art-slot" data-art-slot="' + k + '" aria-labelledby="ch-art-slot-' + i + '">' +
      '<div class="ch-art-slot__head"><h4 id="ch-art-slot-' + i + '">' + esc(slot.label) + '</h4>' +
      (size ? '<span class="ch-art-slot__size">Print size: ' + esc(size) + '</span>' : '') + '</div>' +
      '<div class="ch-bulk-options ch-art-methods" role="radiogroup" aria-label="How you’ll provide your ' + esc(slot.label) + '">' +
      METHODS.map(function (m) {
        return '<label class="ch-bulk-option"><input type="radio" name="bulk_art_method_' + i + '" value="' + m.id + '" data-art-method data-slot="' + k + '"' + (s.method === m.id ? ' checked' : '') + '>' +
          '<span class="ch-bulk-option__body"><span class="ch-bulk-option__title">' + esc(m.title) + '</span><span class="ch-bulk-option__meta">' + esc(m.meta) + '</span></span></label>';
      }).join('') + '</div>';
    if (s.method === 'upload') html += uploadPanel(slot, s);
    else if (s.method === 'canva') html += canvaPanel(slot, s);
    else if (s.method === 'help') html += helpPanel(slot, s);
    return html + '</section>';
  }
  /* ----- Different artwork for some garment colors (offered only when the order has 2+ colors) ----- */
  function colorArtActive() { return !!state.artByColor && selectedColors().length > 1; }
  function renderColorArt() {
    var box = $('[data-art-colors]');
    if (!box) return;
    var sel = selectedColors();
    if (sel.length < 2) { box.hidden = true; box.innerHTML = ''; box.removeAttribute('data-key'); return; }
    box.hidden = false;
    var key = String(!!state.artByColor);
    if (box.getAttribute('data-key') !== key) {
      box.setAttribute('data-key', key);
      box.innerHTML = '<label class="ch-art-same ch-art-bycolor"><input type="checkbox" data-art-bycolor' + (state.artByColor ? ' checked' : '') + '>' +
        '<span><strong>I need different artwork for some garment colors</strong> <small data-art-bycolor-meta></small></span></label>' +
        (state.artByColor ? '<div class="ch-bulk-subpanel ch-art-panel">' +
          '<label class="ch-bulk-field"><span>Which colors get which design?</span>' +
          '<textarea rows="3" maxlength="1500" data-art-color-notes placeholder="e.g. Black and Navy: the white version of our logo. White and Light Pink: the original design.">' +
          esc(state.artColorNotes) + '</textarea></label>' +
          '<div class="ch-art-inspo"><p class="ch-art-label">Other artwork versions <small>(optional · up to ' + ART.maxInspo + ' PNG or JPG)</small></p>' +
          '<p class="ch-bulk-note">Upload the other version(s) here — name each file by color if you can — or email them after you submit.</p>' +
          uploaderHtml(COLOR_ART + '|inspo', true) + '</div></div>' : '');
      if (state.artByColor) renderUploader(COLOR_ART + '|inspo');
    }
    var meta = $('[data-art-bycolor-meta]', box);
    if (meta) {
      meta.textContent = state.artByColor ? 'Tell us which colors get which design. Your print locations and artwork above stay the same.'
        : 'Otherwise the artwork above prints the same on all ' + sel.length + ' colors (' + colorsText() + ').';
    }
  }
  function renderArtwork() {
    var box = $('[data-art-slots]');
    if (!box) return;
    renderColorArt();
    var locs = artLocations();
    var slots = artSlots();
    var q = currentQuote();
    var sameWrap = $('[data-art-same-wrap]');
    if (sameWrap) {
      sameWrap.hidden = locs.length < 2;
      $('[data-art-same]').checked = state.artSame;
      $('[data-art-same-meta]').textContent = locs.length > 1 ? 'One design, printed on the ' + listText(locs, 'and') + '.' : '';
    }
    var intro = $('[data-art-intro]');
    if (intro) {
      intro.textContent = !locs.length ? 'Choose where we should print above, then add your artwork here.'
        : slots.length > 1 ? 'Your order prints in ' + locs.length + ' locations. Choose how you’ll provide the artwork for each one.'
        : 'Choose how you’ll provide the artwork for your ' + listText(locs, 'and') + ' print' + (locs.length > 1 ? 's' : '') + '.';
    }
    var key = JSON.stringify([slots.map(function (sl) { return [sl.key, slotState(sl.key).method]; }),
      q.print.sizes.map(function (s) { return s.location + ':' + s.id; })]);
    if (box.getAttribute('data-key') === key) return;
    var active = document.activeElement;
    var refocus = active && active.hasAttribute && active.hasAttribute('data-art-method') ? [active.getAttribute('data-slot'), active.value] : null;
    box.setAttribute('data-key', key);
    box.innerHTML = slots.map(function (sl, i) { return slotHtml(sl, i, q); }).join('');
    slots.forEach(function (sl) { renderUploader(sl.key + '|file'); renderUploader(sl.key + '|inspo'); });
    if (refocus) {
      $all('[data-art-method]').forEach(function (r) { if (r.getAttribute('data-slot') === refocus[0] && r.value === refocus[1]) r.focus({ preventScroll: true }); });
    }
  }

  /* ----- Uploader view (re-rendered on phase changes; progress is painted in place) ----- */
  function thumbHtml(f, objectUrl) {
    var src = objectUrl || (f && f.fileId ? previews[f.fileId] : '');
    return '<span class="ch-art-thumb">' + (src ? '<img src="' + esc(src) + '" alt="">'
      : '<span class="ch-art-thumb__icon" aria-hidden="true">' + (f && f.type === 'image/jpeg' ? 'JPG' : 'PNG') + '</span>') + '</span>';
  }
  function doneCard(key, f, inspo) {
    var tip = !inspo && f.type === 'image/jpeg'
      ? '<p class="ch-art-file__tip">Tip: a PNG with a transparent background prints best. We’ll review this JPG for print quality.</p>' : '';
    return '<div class="ch-art-file is-done">' + thumbHtml(f) +
      '<div class="ch-art-file__body"><p class="ch-art-file__name">' + esc(f.name) + '</p>' +
      '<p class="ch-art-file__meta"><span class="ch-art-ok">✓ Uploaded</span>' + (fileMeta(f) ? ' · ' + esc(fileMeta(f)) : '') + '</p>' + tip + '</div>' +
      '<div class="ch-art-file__actions">' + (inspo ? '' : '<button type="button" class="ch-bulk-copy" data-up-choose="' + esc(key) + '">Replace</button>') +
      '<button type="button" class="ch-bulk-copy" data-up-remove="' + esc(key) + '" data-file="' + esc(f.fileId) + '" aria-label="Remove ' + esc(f.name) + '">Remove</button></div></div>';
  }
  function progressCard(key, e) {
    var label = e.status === 'checking' ? 'Checking file…' : e.status === 'queued' ? 'Waiting to upload…' : 'Uploading…';
    return '<div class="ch-art-file is-uploading">' + thumbHtml({ type: e.type }, e.objectUrl) +
      '<div class="ch-art-file__body"><p class="ch-art-file__name">' + esc(e.name) + '</p>' +
      '<div class="ch-art-bar" role="progressbar" aria-label="Upload progress for ' + esc(e.name) + '" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" data-up-bar="' + e.id + '"><span></span></div>' +
      '<p class="ch-art-file__meta" data-up-text="' + e.id + '">' + label + '</p></div>' +
      '<div class="ch-art-file__actions"><button type="button" class="ch-bulk-copy" data-up-cancel="' + esc(key) + '" data-entry="' + e.id + '">Cancel</button></div></div>';
  }
  function errorCard(key, e) {
    var p = parseKey(key);
    var what = slotState(p.slotKey).method === 'canva' ? 'design' : 'artwork';
    var hint = !e.hint ? '' : p.inspo ? ' You can also email images to ' + ARTWORK_EMAIL + ' after you submit.'
      : ' If it keeps happening, check “I’ll email this ' + what + ' after I submit” below.';
    return '<div class="ch-art-file is-error" role="alert"><span class="ch-art-thumb ch-art-thumb--error" aria-hidden="true">!</span>' +
      '<div class="ch-art-file__body"><p class="ch-art-file__name">' + esc(e.name) + ' — not uploaded</p><p class="ch-art-file__err">' + esc(e.error + hint) + '</p></div>' +
      '<div class="ch-art-file__actions">' + (e.retry ? '<button type="button" class="ch-bulk-copy" data-up-retry="' + esc(key) + '" data-entry="' + e.id + '">Try again</button>' : '') +
      '<button type="button" class="ch-bulk-copy" data-up-dismiss="' + esc(key) + '" data-entry="' + e.id + '">Dismiss</button></div></div>';
  }
  function dropHtml(key, inspo, have) {
    var k = esc(key);
    return '<div class="ch-art-drop' + (inspo ? ' ch-art-drop--small' : '') + '" data-drop="' + k + '">' +
      '<span class="ch-art-drop__icon" aria-hidden="true">↑</span>' +
      '<p class="ch-art-drop__title"><span class="ch-art-drop__hint">' + (inspo ? 'Drag images here, or ' : 'Drag &amp; drop your file here, or ') + '</span>' +
      '<button type="button" class="ch-art-drop__btn" data-up-choose="' + k + '">' + (inspo ? (have ? 'Add more images' : 'Choose images') : 'Choose a PNG or JPG') + '</button></p>' +
      '<p class="ch-art-drop__meta">' + (inspo ? 'PNG or JPG · up to ' + ART.maxInspo + ' images · ' + ART.maxMb + ' MB each' : 'PNG (preferred) or JPG · up to ' + ART.maxMb + ' MB') + '</p></div>';
  }
  function renderUploader(key) {
    var view = byAttr('data-up-view', key);
    if (!view) return;
    var p = parseKey(key);
    var s = slotState(p.slotKey);
    var list = uploads[key] || [];
    var pending = null;
    var html = '';
    if (p.inspo) {
      html += s.inspo.map(function (f) { return doneCard(key, f, true); }).join('');
      html += list.map(function (e) { return e.status === 'error' ? errorCard(key, e) : progressCard(key, e); }).join('');
      if (s.inspo.length + busyEntries(key).length < ART.maxInspo) html += dropHtml(key, true, s.inspo.length);
    } else {
      pending = busyEntries(key)[0] || null;
      var failed = list.filter(function (e) { return e.status === 'error'; })[0];
      if (failed) html += errorCard(key, failed);
      if (pending) {
        html += progressCard(key, pending) + (s.file ? '<p class="ch-bulk-muted ch-art-replacing">Replacing ' + esc(quoteName(s.file.name)) + ' — it stays until the new file finishes uploading.</p>' : '');
      } else {
        html += s.file ? doneCard(key, s.file, false) : dropHtml(key, false, 0);
      }
    }
    view.innerHTML = html;
    list.forEach(function (e) { if (e.status === 'uploading') paintProgress(e); });
    if (!p.inspo) {
      var wrap = byAttr('data-art-later-wrap', p.slotKey);
      if (wrap) {
        wrap.hidden = !!s.file || !!pending;
        var cb = wrap.querySelector('input');
        if (cb) cb.checked = s.later;
      }
    }
  }
  function setMsg(key, text, kind) {
    var el = byAttr('data-up-msg', key);
    if (!el) return;
    el.textContent = text || '';
    el.className = 'ch-art-up__msg' + (kind ? ' is-' + kind : '');
    el.hidden = !text;
  }
  function shownPct(e) {
    if (e.status !== 'uploading' || !e.size) return 0;
    var at = e.done || 0;
    if (e.cEnd > e.cStart) {
      var len = e.cEnd - e.cStart;
      var expected = len / (e.rate || 150);            // bytes per ms; first guess ~150 KB/s
      at = Math.max(at, e.cStart + len * Math.min(0.92, (Date.now() - e.cT0) / Math.max(800, expected)));
    }
    return Math.max(1, Math.min(99, Math.floor(at / e.size * 100)));
  }
  function paintProgress(e) {
    var bar = byAttr('data-up-bar', e.id);
    var text = byAttr('data-up-text', e.id);
    if (!bar || !text) return;
    var pct = shownPct(e);
    bar.firstChild.style.width = pct + '%';
    bar.setAttribute('aria-valuenow', String(pct));
    text.textContent = 'Uploading… ' + pct + '%' + (e.size >= 1048576 ? ' · ' + fmtBytes(Math.floor(e.size * pct / 100)) + ' of ' + fmtBytes(e.size) : '');
  }
  function tick() {
    var any = false;
    Object.keys(uploads).forEach(function (k) { uploads[k].forEach(function (e) { if (e.status === 'uploading') { any = true; paintProgress(e); } }); });
    if (!any && ticker) { clearInterval(ticker); ticker = null; }
  }

  /* ----- File checks (type by content, not just the name) ----- */
  var ERR = {
    pdf: function (n) { return quoteName(n) + ' is a PDF. PDFs can’t be uploaded — please export your artwork as a PNG (preferred) or JPG and upload that instead.'; },
    svg: function (n) { return quoteName(n) + ' is an SVG file. SVGs aren’t accepted — please export your artwork as a PNG (preferred) or JPG.'; },
    type: function (n) { return quoteName(n) + ' isn’t a PNG or JPG. Please upload a PNG (preferred) or JPG image.'; },
    fake: function (n) { return quoteName(n) + ' isn’t a real PNG or JPG image (it may have been renamed). Please export your artwork as a PNG (preferred) or JPG.'; },
    empty: function (n) { return quoteName(n) + ' looks empty. Please choose the file again.'; },
    big: function (n, size) { return quoteName(n) + ' is ' + fmtBytes(size) + ' — the limit is ' + ART.maxMb + ' MB per file. Please export a smaller PNG or JPG.'; },
    read: function (n) { return 'We couldn’t read ' + quoteName(n) + '. Please choose the file again.'; },
    net: 'The upload didn’t go through. Check your connection and try again.',
    slow: 'The upload is taking too long. Check your connection and try again.',
    interrupted: 'The upload was interrupted. Please try again.',
    unavailable: 'Uploads aren’t available right now.',
    request: 'Something went wrong with your request number. Please refresh the page and try again.',
    busy: 'Uploads are paused right now.',
    many: 'That’s the most files we can take for one request.'
  };
  function readBlob(blob, asDataUrl) {
    return new Promise(function (resolve, reject) {
      var r = new FileReader();
      r.onload = function () { resolve(r.result); };
      r.onerror = function () { reject(r.error || new Error('read failed')); };
      if (asDataUrl) r.readAsDataURL(blob); else r.readAsArrayBuffer(blob);
    });
  }
  function sniff(b) {
    if (b.length >= 8 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4E && b[3] === 0x47 && b[4] === 0x0D && b[5] === 0x0A && b[6] === 0x1A && b[7] === 0x0A) return 'png';
    if (b.length >= 3 && b[0] === 0xFF && b[1] === 0xD8 && b[2] === 0xFF) return 'jpeg';
    if (b.length >= 4 && b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46) return 'pdf';
    var head = '';
    for (var i = 0; i < Math.min(b.length, 512); i++) head += String.fromCharCode(b[i]);
    return /<svg|<\?xml/i.test(head) ? 'svg' : '';
  }
  function u32(b, i) { return ((b[i] << 24) >>> 0) + (b[i + 1] << 16) + (b[i + 2] << 8) + b[i + 3]; }
  function pngDims(b) { return b.length >= 24 && b[12] === 0x49 && b[13] === 0x48 && b[14] === 0x44 && b[15] === 0x52 ? [u32(b, 16), u32(b, 20)] : null; }
  function jpegDims(b) {
    var i = 2;
    while (i + 9 < b.length) {
      if (b[i] !== 0xFF) { i++; continue; }
      var m = b[i + 1];
      if (m === 0xFF) { i++; continue; }
      if (m === 0xD8 || m === 0x01 || (m >= 0xD0 && m <= 0xD7)) { i += 2; continue; }
      if (m >= 0xC0 && m <= 0xCF && m !== 0xC4 && m !== 0xC8 && m !== 0xCC) return [(b[i + 7] << 8) | b[i + 8], (b[i + 5] << 8) | b[i + 6]];
      i += 2 + ((b[i + 2] << 8) | b[i + 3]);
    }
    return null;
  }
  function precheck(file) {
    var name = file.name || 'artwork';
    var ext = ((/\.([a-z0-9]+)$/i.exec(name) || [])[1] || '').toLowerCase();
    var t = String(file.type || '').toLowerCase();
    if (ext === 'pdf' || t === 'application/pdf') return Promise.resolve({ error: ERR.pdf(name) });
    if (ext === 'svg' || t === 'image/svg+xml') return Promise.resolve({ error: ERR.svg(name) });
    var okExt = ext === 'png' || ext === 'jpg' || ext === 'jpeg';
    var okType = ['image/png', 'image/x-png', 'image/jpeg', 'image/jpg', 'image/pjpeg'].indexOf(t) !== -1;
    if (!(okType || (!t && okExt))) return Promise.resolve({ error: ERR.type(name) });
    if (!file.size) return Promise.resolve({ error: ERR.empty(name) });
    if (file.size > ART.maxMb * 1048576) return Promise.resolve({ error: ERR.big(name, file.size), hint: true });
    return readBlob(file.slice(0, 262144), false).then(function (buf) {
      var b = new Uint8Array(buf);
      var kind = sniff(b);
      if (kind === 'pdf') return { error: ERR.pdf(name) };
      if (kind === 'svg') return { error: ERR.svg(name) };
      if (kind !== 'png' && kind !== 'jpeg') return { error: ERR.fake(name) };
      var d = kind === 'png' ? pngDims(b) : jpegDims(b);
      return { type: kind === 'png' ? 'image/png' : 'image/jpeg', w: d ? d[0] : 0, h: d ? d[1] : 0 };
    }, function () { return { error: ERR.read(name) }; });
  }

  /* ----- Upload helper calls (simple text/plain POST, no CORS preflight; bounded retries) ----- */
  function mkErr(msg, userRetry, code, transient, hint) {
    var err = new Error(msg);
    err.userRetry = !!userRetry; err.code = code || ''; err.transient = !!transient; err.hint = hint !== false;
    return err;
  }
  function serverError(r, e) {
    var map = {
      bad_request: [ERR.request, false, false], bad_slot: [ERR.request, false, false], bad_type: [ERR.fake(e.name || 'That file'), false, false],
      bad_size: [ERR.empty(e.name || 'That file'), false, false], too_big: [ERR.big(e.name || 'That file', e.size || 0), false, true],
      busy: [ERR.busy, false, true], too_many: [ERR.many, false, true], expired: [ERR.interrupted, true, true],
      bad_token: [ERR.net, false, false], bad_action: [ERR.net, false, false],
      out_of_order: [ERR.interrupted, true, true], bad_chunk: [ERR.interrupted, true, true], incomplete: [ERR.interrupted, true, true]
    };
    var m = map[r.code] || [ERR.net, true, true];
    return mkErr(m[0], m[1], r.code || 'server_error', !map[r.code], m[2]);
  }
  // Some requests to Apps Script stall with no answer at all (seen 15–50% in testing: the POST either answers in ~1 s or
  // never), and a retry then answers at once. So every call has a short per-attempt timeout that grows 50% per retry.
  // maxAttempts=1 lets sendFrom() run its own retries (it re-sizes the piece between tries).
  function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function retryDelay(n) { return n <= 1 ? 500 : n === 2 ? 2000 : 4000; }
  function call(body, e, timeoutMs, maxAttempts) {
    var attempt = 0;
    var limit = maxAttempts || ART.attempts;
    function once() {
      attempt++;
      var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      var timedOut = false;
      var t0 = Date.now();
      if (e) e.ctrl = ctrl;
      var timer = setTimeout(function () { timedOut = true; if (ctrl) ctrl.abort(); }, Math.round(timeoutMs * (1 + 0.5 * (attempt - 1))));
      return fetch(ART.url, { method: 'POST', body: JSON.stringify(body), redirect: 'follow', credentials: 'omit', signal: ctrl ? ctrl.signal : undefined })
        .then(function (res) {
          if (!res.ok) throw mkErr(ERR.net, true, 'http_' + res.status, true);
          return res.json();
        })
        .then(function (r) {
          clearTimeout(timer);
          if (!r || typeof r !== 'object') throw mkErr(ERR.net, true, 'bad_json', true);
          if (!r.ok) throw serverError(r, e || {});
          if (e) e.lastMs = Date.now() - t0;
          return r;
        }, function (err) {
          clearTimeout(timer);
          if (err && typeof err.userRetry === 'boolean') throw err;
          throw mkErr(timedOut ? ERR.slow : ERR.net, true, timedOut ? 'timeout' : 'network', true);
        })
        .catch(function (err) {
          if (e && e.cancelled) throw mkErr('Canceled', false, 'cancelled', false, false);
          if (err.transient && attempt < limit) return wait(retryDelay(attempt)).then(once);
          throw err;
        });
    }
    return once();
  }
  function discardFile(f, keepalive) {
    if (!ART.url || !f || !f.fileId || !f.token) return;
    var body = { action: 'discard', fileId: f.fileId, token: f.token };
    if (keepalive) {             // page is navigating away (submit), so no retries: send two (trashing is idempotent) in case one stalls
      for (var i = 0; i < 2; i++) {
        try {
          fetch(ART.url, { method: 'POST', body: JSON.stringify(body), redirect: 'follow', credentials: 'omit', keepalive: true }).catch(function () {});
        } catch (x) {}
      }
      return;
    }
    call(body, null, ART.smallTimeoutMs, 3).catch(function () {});
  }

  /* ----- Upload lifecycle ----- */
  function addFiles(key, list) {
    var p = parseKey(key);
    var files = Array.prototype.slice.call(list || []).filter(function (f) { return f && typeof f.size === 'number'; });
    if (!files.length) { setMsg(key, 'That didn’t include a file. Choose or drop a PNG or JPG image.', 'error'); return; }
    var notes = [];
    if (!p.inspo) {
      if (files.length > 1) notes.push('One file per print location — we used ' + quoteName(files[0].name) + '.');
      files = files.slice(0, 1);
      (uploads[key] || []).slice().forEach(dropEntry);                 // a new pick replaces a pending or failed one
    } else {
      (uploads[key] || []).filter(function (x) { return x.status === 'error'; }).forEach(dropEntry);
      var room = ART.maxInspo - slotState(p.slotKey).inspo.length - busyEntries(key).length;
      if (room <= 0) { renderUploader(key); setMsg(key, 'You’ve added the maximum of ' + ART.maxInspo + ' inspiration images. Remove one to add another.', 'error'); return; }
      if (files.length > room) { notes.push('You can add up to ' + ART.maxInspo + ' inspiration images — we added the first ' + room + '.'); files = files.slice(0, room); }
    }
    setMsg(key, notes.join(' '), notes.length ? 'note' : '');
    files.forEach(function (f) { newEntry(key, f); });
    refreshVisibleError();
  }
  function newEntry(key, file) {
    var p = parseKey(key);
    var slot = slotByKey(p.slotKey);
    var e = { id: 'u' + (++entrySeq), key: key, slotKey: p.slotKey, inspo: p.inspo, file: file, name: file.name || 'artwork', size: file.size,
      status: 'checking', slotName: p.slotKey === COLOR_ART ? slot.label : p.inspo ? slot.short + ' Inspiration' : slot.label };
    (uploads[key] = uploads[key] || []).push(e);
    renderUploader(key);
    precheck(file).then(function (info) {
      if (e.cancelled) return;
      if (info.error) { failEntry(e, info.error, false, !!info.hint); return; }
      e.type = info.type; e.w = info.w; e.h = info.h;
      if (file.size <= ART.previewMaxBytes && window.URL && URL.createObjectURL) { try { e.objectUrl = URL.createObjectURL(file); } catch (x) {} }
      enqueue(e);
    });
  }
  function enqueue(e) {
    e.status = 'queued'; e.error = ''; e.done = 0; e.cStart = e.cEnd = 0;
    queue.push(e);
    renderUploader(e.key);
    pump();
  }
  function pump() {
    while (activeUploads < ART.parallel && queue.length) {
      var next = queue.shift();
      if (next.cancelled) continue;
      activeUploads++;
      upload(next).then(function () { activeUploads--; pump(); refreshVisibleError(); });
    }
  }
  function upload(e) {
    if (!ART.url) { failEntry(e, ERR.unavailable, false, true); return Promise.resolve(); }
    var number = ensureRequestNumber();
    e.status = 'uploading';
    renderUploader(e.key);
    if (!ticker) ticker = setInterval(tick, 400);
    e.rate = 0;
    return call({ action: 'start', request: number, slot: e.slotName, type: e.type, size: e.size, name: e.name }, e, ART.smallTimeoutMs)
      .then(function (r) {
        e.uploadId = r.uploadId;
        var unit = 262144;
        e.chunk = Math.max(unit * 2, Math.floor((Number(r.chunkBytes) || 8388608) / unit) * unit);
        return sendFrom(e, 0, 0);
      })
      .then(function (r) { finishEntry(e, r); }, function (err) { if (!e.cancelled) failEntry(e, err.message, err.userRetry, err.hint); });
  }
  // Piece size adapts to the measured speed (whole 256 KB units except the last piece, as the helper requires). Big
  // pieces amortize the ~2 s per-request overhead; the timeout (12 s + 1.5x the expected time) bounds what a stall costs.
  // Tested Oct 2 2026: 18 MB took 4 min with 15 s pieces and a 3x timeout because of stalls (see SKILL.md).
  function chunkLen(e) {
    var unit = 262144;
    var len = e.rate ? Math.floor(e.rate * ART.targetChunkMs / unit) * unit : ART.firstChunk;
    return Math.max(unit * 2, Math.min(e.chunk, len));
  }
  function chunkTimeout(e, len) {
    var expected = e.rate ? 1.5 * len / e.rate : len / ART.minRate;
    return Math.min(300000, 12000 + expected);
  }
  function sendFrom(e, offset, tries) {
    if (e.cancelled) return Promise.reject(mkErr('Canceled', false, 'cancelled', false, false));
    var end = Math.min(offset + chunkLen(e), e.size);
    e.cStart = offset; e.cEnd = end; e.cT0 = Date.now();
    return readBlob(e.file.slice(offset, end), true).then(function (url) {
      var s = String(url);
      e.cT0 = Date.now();
      return call({ action: 'chunk', uploadId: e.uploadId, offset: offset, data: s.slice(s.indexOf(',') + 1) }, e,
        chunkTimeout(e, end - offset) * (1 + 0.5 * tries), 1);
    }, function () { throw mkErr(ERR.read(e.name), true, 'read', false, true); })
      .then(function (r) {
        if (r.done) return r;
        var next = Number(r.next);
        if (!(next > offset) || next > e.size) throw mkErr(ERR.interrupted, true, 'bad_next');
        if (next === end) e.rate = (end - offset) / Math.max(1, e.lastMs || (Date.now() - e.cT0));   // skip "already had it" replies
        e.done = next;
        paintProgress(e);
        return sendFrom(e, next, 0);
      }, function (err) {
        if (e.cancelled) throw mkErr('Canceled', false, 'cancelled', false, false);
        // Safe to resend: the helper answers {next} for pieces it already has. If the final piece had actually finished,
        // the retry gets "expired" and the customer sees "interrupted — Try again" (rare; see SKILL.md).
        if (err.transient && tries + 1 < ART.attempts) {
          if (err.code === 'timeout' && e.rate) e.rate = e.rate / 2;   // slow, not stalled? use smaller pieces
          return wait(retryDelay(tries + 1)).then(function () { return sendFrom(e, offset, tries + 1); });
        }
        throw err;
      });
  }
  function finishEntry(e, r) {
    var meta = cleanFile({ name: e.name, size: r.size || e.size, type: e.type, w: e.w, h: e.h, fileId: r.fileId, token: r.token,
      fileUrl: r.fileUrl, folderUrl: r.folderUrl });
    if (!meta || Number(r.size) !== e.size) { failEntry(e, ERR.interrupted, true, true); return; }
    removeEntry(e);
    if (e.objectUrl) previews[meta.fileId] = e.objectUrl;
    var s = slotState(e.slotKey);
    if (e.inspo) {
      if (s.inspo.length >= ART.maxInspo) { discardFile(meta); setMsg(e.key, 'You’ve already added ' + ART.maxInspo + ' inspiration images.', 'error'); renderUploader(e.key); return; }
      s.inspo.push(meta);
    } else {
      var old = s.file;
      s.file = meta;
      s.later = false;
      if (old && old.fileId !== meta.fileId) discardFile(old);
    }
    persist();
    renderUploader(e.key);
    setMsg(e.key, quoteName(e.name) + ' uploaded.', 'ok');
  }
  function failEntry(e, msg, retry, hint) {
    e.status = 'error';
    e.error = msg || ERR.net;
    e.retry = !!retry && !!e.file;
    e.hint = !!hint;
    queue = queue.filter(function (x) { return x !== e; });
    renderUploader(e.key);
    refreshVisibleError();
  }
  function removeEntry(e) {
    uploads[e.key] = (uploads[e.key] || []).filter(function (x) { return x !== e; });
    queue = queue.filter(function (x) { return x !== e; });
  }
  function dropEntry(e) {
    e.cancelled = true;
    if (e.ctrl) { try { e.ctrl.abort(); } catch (x) {} }
    removeEntry(e);
    if (e.objectUrl) { try { URL.revokeObjectURL(e.objectUrl); } catch (x) {} }
  }
  function removeFile(key, fileId) {
    var p = parseKey(key);
    var s = slotState(p.slotKey);
    var f = null;
    if (p.inspo) s.inspo = s.inspo.filter(function (x) { if (x.fileId === fileId) { f = x; return false; } return true; });
    else if (s.file && s.file.fileId === fileId) { f = s.file; s.file = null; }
    if (!f) return;
    discardFile(f);
    if (previews[fileId]) { try { URL.revokeObjectURL(previews[fileId]); } catch (x) {} delete previews[fileId]; }
    persist();
    renderUploader(key);
    setMsg(key, 'Removed ' + quoteName(f.name) + '.', 'note');
    var view = byAttr('data-up-view', key);
    var btn = view && view.querySelector('[data-up-choose]');
    if (btn) btn.focus({ preventScroll: true });
    refreshVisibleError();
  }
  function openPicker(key) { var input = byAttr('data-up-input', key); if (input) input.click(); }
  function uploaderAction(btn) {
    var key = btn.getAttribute('data-up-choose');
    if (key !== null) { openPicker(key); return; }
    key = btn.getAttribute('data-up-remove');
    if (key !== null) { removeFile(key, btn.getAttribute('data-file')); return; }
    var attr = ['data-up-cancel', 'data-up-retry', 'data-up-dismiss'].filter(function (a) { return btn.hasAttribute(a); })[0];
    key = btn.getAttribute(attr);
    var entry = (uploads[key] || []).filter(function (x) { return x.id === btn.getAttribute('data-entry'); })[0];
    if (!entry) return;
    if (attr === 'data-up-retry') { entry.cancelled = false; setMsg(key, '', ''); enqueue(entry); return; }
    dropEntry(entry);
    renderUploader(key);
    setMsg(key, attr === 'data-up-cancel' ? 'Upload canceled.' : '', 'note');
    refreshVisibleError();
  }
  // Files uploaded for a location or method that is no longer on the order go to Drive trash at submit.
  function pruneUnusedFiles() {
    var used = {};
    artSlots().forEach(function (slot) {
      var s = slotState(slot.key);
      if ((s.method === 'upload' || s.method === 'canva') && s.file) used[s.file.fileId] = true;
      if (s.method === 'help') s.inspo.forEach(function (f) { used[f.fileId] = true; });
    });
    if (colorArtActive()) slotState(COLOR_ART).inspo.forEach(function (f) { used[f.fileId] = true; });
    Object.keys(state.art).forEach(function (k) {
      var s = slotState(k);
      if (s.file && !used[s.file.fileId]) { discardFile(s.file, true); s.file = null; }
      s.inspo = s.inspo.filter(function (f) { if (used[f.fileId]) return true; discardFile(f, true); return false; });
    });
  }
  function artworkProblem(slot, multi) {
    var s = slotState(slot.key);
    var named = multi || slot.same;
    var name = named ? slot.short + ' artwork' : 'artwork';
    if (!s.method) return 'Choose how you’ll provide your ' + name + '.';
    if (slotBusy(slot)) {
      return s.method === 'help' ? 'Your inspiration images' + (named ? ' for the ' + slot.short : '') + ' are still uploading — you can continue as soon as they finish.'
        : 'Your ' + name + ' is still uploading — you can continue as soon as it finishes.';
    }
    if (s.method === 'upload' && !s.file && !s.later) return 'Upload your ' + name + ' (PNG or JPG), or check “I’ll email this artwork after I submit.”';
    if (s.method === 'canva') {
      var link = (s.canvaLink || '').trim();
      if (link && !validLink(link)) return 'Canva links should start with https://';
      if (!s.file && !link && !s.later) return 'Add your Canva design' + (named ? ' for the ' + slot.short : '') + ' — upload the PNG or JPG you downloaded, or paste your Canva share link.';
    }
    if (s.method === 'help' && !helpFilled(s)) return 'Tell us about your ' + (named ? slot.short + ' ' : '') + 'design idea — the wording, theme, or instructions.';
    return '';
  }

  /* ---------- Fields bound by data-bind ---------- */
  function syncBoundFields() {
    $all('[data-bind]').forEach(function (el) {
      var key = el.getAttribute('data-bind');
      if (el.type === 'radio') el.checked = state[key] === el.value;
      else if (document.activeElement !== el) el.value = state[key] || '';
    });
    var opt = fulfillmentOption();
    $('[data-address-wrap]').hidden = !(opt && opt.needs_address);
  }
  function fulfillmentOption() {
    return (config.fulfillment_options || []).filter(function (o) { return o.id === state.fulfillment; })[0] || null;
  }

  function renderFulfillment() {
    $('[data-fulfillment]').innerHTML = (config.fulfillment_options || []).map(function (o) {
      return '<label class="ch-bulk-option"><input type="radio" name="bulk_fulfillment" data-bind="fulfillment" value="' + esc(o.id) + '">' +
        '<span class="ch-bulk-option__body"><span class="ch-bulk-option__title">' + esc(o.label) + '</span></span></label>';
    }).join('');
  }

  /* ---------- Summary + review ---------- */
  var METHOD_TITLE = { upload: 'Upload My Artwork', canva: 'Create My Design in Canva', help: 'Help Me Design It' };
  function artworkList() {
    return artSlots().map(function (slot) { return { slot: slot, s: slotState(slot.key), status: slotStatus(slot) }; });
  }
  // Customer-facing Review value for one print location. Never shows Drive/storage links.
  function artworkReviewValue(a) {
    var parts = [];
    if (a.status === 'uploaded' || a.status === 'canva') {
      if (a.s.file) parts.push(a.s.file.name);
      if (a.status === 'canva' && validLink(a.s.canvaLink)) parts.push('Canva share link');
    } else if (a.status === 'help' && a.s.inspo.length) {
      parts.push(a.s.inspo.length + ' inspiration image' + (a.s.inspo.length > 1 ? 's' : ''));
    }
    return '<strong>' + esc(STATUS_LABEL[a.status] || 'Not finished') + '</strong>' +
      (parts.length ? ' <span class="ch-bulk-muted">— ' + esc(parts.join(' + ')) + '</span>' : '');
  }
  // Internal wording (request email to Carry Hope): method, status, private Drive links, Canva link, design brief.
  function fileLine(f) { return f.name + (fileMeta(f) ? ' (' + fileMeta(f) + ')' : '') + (f.fileUrl ? ' — ' + f.fileUrl : ''); }
  function multiline(v) { return String(v || '').trim().replace(/\s*\n\s*/g, '\n      '); }
  function artworkEmailLines(number) {
    var list = artworkList();
    var folder = '';
    list.forEach(function (a) {
      var files = a.s.method === 'help' ? a.s.inspo : (a.s.file ? [a.s.file] : []);
      files.forEach(function (f) { if (!folder && f.folderUrl) folder = f.folderUrl; });
    });
    var colorArt = colorArtActive() ? slotState(COLOR_ART).inspo : [];
    colorArt.forEach(function (f) { if (!folder && f.folderUrl) folder = f.folderUrl; });
    var locs = artLocations();
    var lines = ['', 'ARTWORK'];
    if (locs.length > 1) lines.push('Same artwork for both locations: ' + (state.artSame ? 'Yes' : 'No'));
    if (folder) lines.push('Private Drive folder for this request: ' + folder);
    list.forEach(function (a) {
      var s = a.s;
      lines.push(a.slot.label + ': ' + (INTERNAL_STATUS[a.status] || 'NOT PROVIDED'));
      lines.push('  Method: ' + (METHOD_TITLE[s.method] || '—'));
      if ((s.method === 'upload' || s.method === 'canva') && s.file) lines.push('  File (Drive): ' + fileLine(s.file));
      if (s.method === 'canva') lines.push('  Canva link: ' + ((s.canvaLink || '').trim() || '—'));
      if (s.method === 'help') {
        HELP_FIELDS.forEach(function (f) { lines.push('  ' + f.label + ': ' + (multiline(s.help[f.key]) || '—')); });
        lines.push('  Inspiration images: ' + (s.inspo.length ? s.inspo.length : 'None'));
        s.inspo.forEach(function (f) { lines.push('    - ' + fileLine(f)); });
        lines.push('  Design fee: not included in the estimate — confirm any design charge with the customer before work begins.');
      }
      if (a.status === 'later') lines.push('  Customer will email it to ' + (ARTWORK_EMAIL || 'the store inbox') + ' with subject "Artwork for ' + number + '"');
    });
    var nColors = selectedColors().length;
    if (nColors > 1 && colorArtActive()) {
      lines.push('Artwork by garment color: DIFFERENT artwork for some colors');
      lines.push('  Which colors get which design: ' + (multiline(state.artColorNotes) || '—'));
      lines.push('  Other artwork versions: ' + (colorArt.length ? colorArt.length : 'None uploaded (customer may email them)'));
      colorArt.forEach(function (f) { lines.push('    - ' + fileLine(f)); });
    } else if (nColors > 1) {
      lines.push('Artwork by garment color: Same artwork on all ' + nColors + ' colors');
    }
    return lines;
  }
  function printSizeText(q) { return q.print.sizes.map(function (s) { return s.location + ': ' + s.label; }).join('; '); }
  // Internal email only: the print size each garment size gets, per location ("Front: S 10 → 10″ × 10″ · L 20 → 11″ × 11″").
  function autoPrintEmailLines(q) {
    var ap = q.autoPrint;
    if (!ap) return ['Print sizes: (choose a placement)'];
    var lines = ['Print sizes (set automatically by garment size — the customer did not choose a print size):'];
    // Families with 2+ categories: one line per location and category ("Front — Youth: XS 5 → 7″ × 7″ · …").
    var groups = q.family && (q.categoryRows || []).length > 1 ? q.categoryRows.map(function (c) { return { id: c.id, label: c.label }; }) : [null];
    q.print.locations.forEach(function (loc) {
      groups.forEach(function (g) {
        var rows = ap.rows.filter(function (r) { return r.location === loc && (!g || r.category === g.id); });
        lines.push('  ' + loc + (g ? ' — ' + g.label : '') + ': ' + (rows.length ? rows.map(function (r) {
          return (r.size === 'One size' ? 'One size' : r.size) + ' ' + r.qty + ' → ' + r.label;
        }).join(' · ') : 'No sizes entered'));
      });
    });
    if (ap.unmapped.length) lines.push('  ⚠ CONFIRM PRINT SIZE (no rule in the config): ' + ap.unmapped.join(', '));
    lines.push('  One artwork file per location — resize it for each garment size in production.');
    return lines;
  }
  function exclusionLine() {
    var opt = fulfillmentOption();
    if (opt && !opt.needs_address) {
      var where = opt.label.replace(/\s+—\s+/, ' in ');
      return 'You chose ' + where.charAt(0).toLowerCase() + where.slice(1) + ', so no shipping is added. Any applicable sales tax is added to your final invoice.';
    }
    if (opt && opt.needs_address) return 'Shipping and any applicable sales tax are added to your final invoice once we confirm your order.';
    return 'Shipping (if you choose it) and any applicable sales tax are added to your final invoice.';
  }
  // 1–9 Review (Amber, Oct 3 2026): what the emailed quote will include, from the Step 6 choice (pickup wording built
  // exactly like exclusionLine: "Local pickup — Spring, TX" -> "local pickup in Spring, TX").
  function quoteFulfillmentLine() {
    var opt = fulfillmentOption();
    if (opt && !opt.needs_address) {
      var where = opt.label.replace(/\s+—\s+/, ' in ');
      return 'You chose ' + where.charAt(0).toLowerCase() + where.slice(1) + ', so no shipping is added. Any applicable sales tax will be included in your quote.';
    }
    if (opt && opt.needs_address) return 'Your quote will include shipping and any applicable sales tax.';
    return 'Your quote will include shipping (if you choose it) and any applicable sales tax.';
  }
  function nextStepsHtml() {
    return '<div class="ch-bulk-next"><p class="ch-bulk-next__title">What happens after you submit</p><ol>' +
      '<li><strong>No payment today.</strong> You’re sending a request, not placing a paid order.</li>' +
      '<li><strong>We review your request</strong> — garment and color availability, sizes, artwork, and your need-by date.</li>' +
      (!EST ? '<li><strong>We email your price</strong>, including shipping (if any) and sales tax.</li>'
        : '<li><strong>We confirm your final price</strong> by email, including shipping (if any) and sales tax.</li>') +
      '<li><strong>You receive a secure Shopify invoice</strong> to approve and pay online.</li>' +
      '</ol></div>';
  }

  function renderSummary() {
    var box = $('[data-summary-body]');
    var prod = currentProduct();
    if (!prod) { setHtml(box, '<p class="ch-bulk-note">' + (CUSTOM ? 'Choose your item to start your order.' : 'Choose your apparel to see your estimate.') + '</p>'); return; }
    var q = currentQuote();
    var sMulti = multiCat();
    var rows = '<dt>Product</dt><dd>' + esc(orderProductName(prod)) + '</dd>';
    if (q.quantity) {
      rows += '<dt>Quantity</dt><dd>' + (sMulti ? esc(categoryQtyText(q)) + ' — ' + q.quantity + ' total' : q.quantity + (q.colorRows.length > 1 ? ' total' : '')) + '</dd>';
    }
    if (q.colorRows.length) {
      rows += '<dt>' + (q.colorRows.length > 1 ? 'Colors' : 'Color') + '</dt><dd>' + q.colorRows.map(function (row) {
        return '<span class="ch-bulk-summary__color">' + esc(colorLabel(row, sMulti)) +
          (q.colorRows.length > 1 && row.total ? ' <span class="ch-bulk-muted">' + row.total + '</span>' : '') + '</span>';
      }).join('') + '</dd>';
    }
    if (q.print.placement) rows += '<dt>Print</dt><dd>' + esc(q.print.placement.label) + '</dd>';
    if (!EST) {
      setHtml(box, '<dl>' + rows + '</dl><p class="ch-bulk-note">We’ll email your price after we review your request — no payment today.</p>');
      return;
    }
    var price = '';
    var sRush = rushNow(q);
    if (q.customQuote) price = '<p class="ch-bulk-summary__price">Custom Volume Pricing</p>' + rushLineHtml(sRush);
    else if (q.priced && !CUSTOM) {
      // Bulk (Amber, Oct 3 2026 21:46 UTC): figures, the itemized "Price breakdown" (its rush row replaces the separate rush
      // line), the pre-choice / why-average note, then the nudge (order-more-pay-less, next tier, or the 500+ custom quote).
      var bf = figures(q);
      var bd = breakdownHtml(prod, q, bf, 'summary');
      var bNote = priceExplain(prod, q, bf, 'summary');
      price = figuresHtml(bf, '', !!bd) + (bd || rushLineHtml(sRush)) + '<p class="ch-bulk-excl ch-bulk-excl--small">Excludes shipping &amp; sales tax</p>' +
        (bNote ? '<p class="ch-bulk-note" data-price-why>' + esc(bNote) + '</p>' : '');
      if (q.next && q.next.nudge) {
        var sBuy = q.next.type === 'tier' ? buyUpText(prod, q) : '';
        price += q.next.type === 'tier'
          ? '<p class="ch-bulk-nudge"' + (sBuy ? ' data-buy-up' : '') + '>' + (sBuy ? esc(sBuy) : 'Add ' + q.next.remaining + ' more to unlock ' + esc(nextPriceText(q)) + '.') + '</p>'
          : '<p class="ch-bulk-nudge" data-quote-nudge>' + esc(quoteNudgeText()) + '</p>';
      }
    }
    else if (q.priced) {
      var f = figures(q);
      // 1–9 page sidebar note (Amber, Oct 3 2026); Bulk keeps priceChangeHint.
      var sNote = !f.average ? (CUSTOM ? 'Your price updates as you choose sizes and print locations.' : priceChangeHint(prod))
        : sMulti ? listText(q.categoryRows.map(function (c) { return c.label.toLowerCase(); }), 'and') + ' ' + prod.unit_plural +
          ' are priced at their own rates, so this is the average price per piece.'
        : 'Your sizes are priced differently, so this is the average price per piece.';
      if (sNote) sNote = sNote.charAt(0).toUpperCase() + sNote.slice(1);
      price = figuresHtml(f) + rushLineHtml(sRush) + '<p class="ch-bulk-excl ch-bulk-excl--small">Excludes shipping &amp; sales tax</p>' +
        (sNote ? '<p class="ch-bulk-note">' + esc(sNote) + '</p>' : '');
      if (!CUSTOM && q.next && q.next.nudge) price += '<p class="ch-bulk-nudge">' + (q.next.type === 'tier'
        ? 'Add ' + q.next.remaining + ' more to unlock ' + esc(nextPriceText(q)) + '.'
        : 'Add ' + q.next.remaining + ' more for Custom Volume Pricing.') + '</p>';
    }
    setHtml(box, '<dl>' + rows + '</dl>' + price);
  }

  function renderReview() {
    var box = $('[data-review]');
    var prod = currentProduct();
    if (!prod) { box.innerHTML = ''; return; }
    var q = currentQuote();
    var arts = artworkList();
    var sel = selectedColors();
    var r = function (label, value) { return '<tr><th scope="row">' + esc(label) + '</th><td>' + value + '</td></tr>'; };
    var colorArtRow = '';
    if (sel.length > 1) {
      var ca = slotState(COLOR_ART).inspo;
      colorArtRow = r('Artwork by color', colorArtActive()
        ? '<strong>Different artwork for some colors</strong>' + (state.artColorNotes.trim() ? '<span class="ch-bulk-review__notes">' + esc(state.artColorNotes.trim()) + '</span>' : '') +
          (ca.length ? '<span class="ch-bulk-muted">' + ca.length + ' other version' + (ca.length > 1 ? 's' : '') + ' uploaded</span>' : '')
        : 'Same artwork on all ' + sel.length + ' colors');
    }
    var html = matrixHtml(prod, q, sel) + '<table class="ch-bulk-review"><tbody>' +
      // Automatic print sizing (Amber, Oct 3 2026): "Design sizing: Automatically adjusted for each garment size".
      (q.print.auto ? r('Design sizing', 'Automatically adjusted for each garment size') : r('Print size', esc(printSizeText(q)))) +
      r('Print placement(s)', esc(q.print.placement ? q.print.placement.label : '')) +
      arts.map(function (a) { return r(a.slot.label, artworkReviewValue(a)); }).join('') + colorArtRow +
      '</tbody></table>';
    var rr = rushNow(q);
    var rushNote = rr ? rushLineHtml(rr) + '<p class="ch-bulk-estimate__note">' + (rr.manual ? '' : '<strong>Included in your estimated order total.</strong> ') + esc(config.rush_note || '') + '</p>' : '';
    if (!EST) {
      html += '<div class="ch-bulk-estimate ch-bulk-estimate--quote"><p class="ch-bulk-estimate__label">Your price</p><p class="ch-bulk-estimate__total">We’ll email your quote</p>' +
        '<p>Custom orders of 1–' + CUSTOM_MAX + ' pieces are priced individually. We’ll review your garments, artwork, and need-by date, then email your price.</p>' +
        '<p class="ch-bulk-estimate__sub" data-quote-fulfillment>' + esc(quoteFulfillmentLine()) + '</p>' +
        '<p class="ch-bulk-excl ch-bulk-excl--ok"><span aria-hidden="true">✓</span>No payment today</p></div>';
    } else if (q.customQuote) {
      html += '<div class="ch-bulk-estimate ch-bulk-estimate--quote"><p class="ch-bulk-estimate__label">Your price</p><p class="ch-bulk-estimate__total">Custom Volume Pricing</p>' +
        '<p>Orders of ' + config.custom_quote_threshold + '+ pieces receive a custom quote. We’ll review your full order and email your pricing.</p>' + rushNote +
        '<p class="ch-bulk-excl" role="note"><span aria-hidden="true">!</span>Shipping &amp; sales tax are not included</p>' +
        '<p class="ch-bulk-estimate__sub">' + esc(exclusionLine()) + '</p></div>';
    } else if (q.priced && !CUSTOM) {
      // Bulk Review (Amber, Oct 3 2026 21:46 UTC): figures, the itemized breakdown (always shown here — its rush row replaces
      // the separate rush line), the why-average line, the rush availability note, then "Priced as one …" (the group rates
      // and the old average sentence now live in the breakdown and the why-average line).
      var bq = figures(q);
      var rbd = breakdownHtml(prod, q, bq, 'review');
      var rWhy = priceExplain(prod, q, bq, 'review');
      var rbMulti = multiCat();
      html += '<div class="ch-bulk-estimate">' + figuresHtml(bq, 'ch-bulk-figures--review', !!rbd) + (rbd || (rr ? rushLineHtml(rr) : '')) +
        (rWhy ? '<p class="ch-bulk-estimate__note ch-bulk-why" data-price-why>' + esc(rWhy) + '</p>' : '') +
        (rr ? '<p class="ch-bulk-estimate__note" data-review-rush-note>' + (rbd || rr.manual ? '' : '<strong>Included in your estimated order total.</strong> ') + esc(config.rush_note || '') + '</p>' : '') +
        '<p class="ch-bulk-estimate__note" data-review-pricing-note>' +
          (rbMulti ? 'Priced as one ' + q.quantity + '-piece ' + esc(familyLabel(prod)) + ' order. '
            : q.colorRows.length > 1 ? 'Priced as one ' + q.quantity + '-piece order across all ' + q.colorRows.length + ' colors. ' : '') +
          'Includes your garments, colors, sizes, and printing as selected above.</p>' +
        '<p class="ch-bulk-excl" role="note"><span aria-hidden="true">!</span>Shipping &amp; sales tax are not included</p>' +
        '<p class="ch-bulk-estimate__sub">' + esc(exclusionLine()) + '</p></div>';
    } else if (q.priced) {
      var rf = figures(q);
      var rMulti = multiCat();
      html += '<div class="ch-bulk-estimate">' + figuresHtml(rf, 'ch-bulk-figures--review') + rushNote +
        '<p class="ch-bulk-estimate__note" data-review-pricing-note>' + (CUSTOM
          // 1–9 (no tiers): each group at its own 1–9 price.
          ? (rMulti ? 'Each group is priced at its own rate: ' + esc(ratesText(q)) + '. ' : '') +
            (rf.average ? 'The price shown is the average per piece across all ' + q.quantity + ' ' + esc(prod.unit_plural) + '. ' : '')
          : rMulti
          // Families (Amber, Oct 3 2026): one tier from the combined total, each group at its own rate in that tier.
          ? 'Priced as one ' + q.quantity + '-piece ' + esc(familyLabel(prod)) + ' order: ' + esc(ratesText(q)) + '. ' +
            (rf.average ? 'The price shown is the average per piece across all ' + q.quantity + ' ' + esc(prod.unit_plural) + '. ' : '')
          : (q.colorRows.length > 1 ? 'Priced as one ' + q.quantity + '-piece order across all ' + q.colorRows.length + ' colors. ' : '') +
            (rf.average ? 'Your sizes are priced differently, so this is the average price per piece across all ' + q.quantity + ' ' + esc(prod.unit_plural) + '. ' : '')) +
          'Includes your garments, colors, sizes, and printing as selected above.</p>' +
        '<p class="ch-bulk-excl" role="note"><span aria-hidden="true">!</span>Shipping &amp; sales tax are not included</p>' +
        '<p class="ch-bulk-estimate__sub">' + esc(exclusionLine()) + '</p></div>';
    }
    var later = arts.filter(function (a) { return a.status === 'later'; });
    if (later.length && ARTWORK_EMAIL) {
      html += '<p class="ch-bulk-info"><strong>Emailing your ' + esc(listText(later.map(function (a) { return a.slot.short; }), 'and')) + ' artwork?</strong> ' +
        'After you submit, you’ll get your request number and a one-tap button to email it to ' + esc(ARTWORK_EMAIL) + '.</p>';
    }
    if (arts.some(function (a) { return a.status === 'help'; })) {
      html += '<p class="ch-bulk-info">Design assistance may require an additional fee depending on complexity. We’ll confirm any design charge before work begins.</p>';
    }
    html += nextStepsHtml();
    box.innerHTML = html;
  }

  // Review: "Gildan Adult Tee — 100 pieces", then each color with its own sizes ("Black — 50 · S: 5 · M: 15 …").
  // Families with 2+ categories: "Gildan Tees — 100 pieces", then one group per category ("Adult — 50") with its colors;
  // typed colors read "Requested color: Sage — 10" with the supplier-confirmation note.
  function matrixHtml(prod, q, sel) {
    var multi = multiCat();
    function rowHtml(row) {
      var c = sel.filter(function (x) { return x.key === row.key; })[0] || { custom: row.custom };
      var sizes = row.sizeRows.filter(function (s) { return s.size !== 'One size'; })
        .map(function (s) { return '<span class="ch-bulk-nowrap">' + esc(s.size + ': ' + s.qty) + '</span>'; }).join(' · ');
      var name = multi && row.custom ? 'Requested color: ' + (row.name || '—') : (row.name || 'Another color');
      return '<li class="ch-bulk-matrix__row">' + chipHtml(c, 'ch-bulk-matrix__chip') + '<div>' +
        '<p class="ch-bulk-matrix__color"><strong>' + esc(name + ' — ' + row.total) + '</strong>' +
        (row.custom ? (multi ? '<small>Availability is subject to supplier confirmation — we’ll confirm it during our review</small>'
          : '<small>Another color — availability confirmed during our review</small>') : '') + '</p>' +
        (sizes ? '<p class="ch-bulk-matrix__sizes">' + sizes + '</p>' : '') + '</div></li>';
    }
    var body = multi
      ? q.categoryRows.map(function (cr) {
        return '<div class="ch-bulk-matrix__group" data-review-cat="' + esc(cr.id) + '"><p class="ch-bulk-matrix__cat">' + esc(cr.label + ' — ' + cr.quantity) + '</p>' +
          '<ul>' + cr.colorRows.map(rowHtml).join('') + '</ul></div>';
      }).join('')
      : '<ul>' + q.colorRows.map(rowHtml).join('') + '</ul>';
    return '<div class="ch-bulk-matrix" data-review-matrix><p class="ch-bulk-matrix__title">' + esc(orderProductName(prod) + ' — ' + q.quantity + ' pieces') + '</p>' +
      body + '</div>';
  }

  /* ---------- Steps ---------- */
  var STEP_TITLES = [CUSTOM ? 'Item' : 'Apparel', 'Quantity', 'Colors', 'Sizes', 'Placement & Artwork', 'Details', 'Review'];
  function renderStepNav() {
    $('[data-step-nav]').innerHTML = STEP_TITLES.map(function (t, i) {
      var n = i + 1;
      var cls = n === state.step ? 'is-current' : (n <= state.maxStep ? 'is-done' : '');
      var disabled = n > state.maxStep ? ' disabled' : '';
      return '<li><button type="button" class="' + cls + '" data-goto="' + n + '"' + disabled + (n === state.step ? ' aria-current="step"' : '') + '>' +
        '<span>' + n + '</span>' + esc(t) + '</button></li>';
    }).join('');
    // Phones: the step list scrolls sideways — keep the current step centred in view (sideways only, never moves the page).
    var nav = $('[data-step-nav]');
    var cur = nav.querySelector('[aria-current="step"]');
    if (cur && nav.scrollWidth > nav.clientWidth + 1) {
      var nr = nav.getBoundingClientRect();
      var cr = cur.parentNode.getBoundingClientRect();
      nav.scrollLeft = Math.max(0, nav.scrollLeft + (cr.left - nr.left) - (nr.width - cr.width) / 2);
    }
    $all('[data-step-panel]').forEach(function (p) { p.hidden = Number(p.getAttribute('data-step-panel')) !== state.step; });
    $('[data-prev]').hidden = state.step === 1;
    $('[data-next]').hidden = state.step === TOTAL_STEPS;
    $('[data-submit-wrap]').hidden = state.step !== TOTAL_STEPS;
  }

  // Plain-text pointer to the 1–9 page for Step 2 errors (shown as text; the clickable link sits in the feedback above).
  function underMinHint() { return 'Need fewer than ' + config.minimum_quantity + '? Use Custom Orders (1–' + CUSTOM_MAX + ') instead.'; }
  function validateStep(n) {
    var prod = currentProduct();
    var q = currentQuote();
    switch (n) {
      case 1: return prod ? '' : 'Choose your apparel to continue.';
      case 2:
        var fc2 = familyCats(prod);
        if (fc2) {
          // Families: at least one category, a whole number (1+) for each chosen category, minimum on the combined total.
          var ac2 = activeCats();
          if (!ac2.length) return 'Choose who your ' + prod.unit_plural + ' are for — ' + listText(fc2.map(function (c) { return c.label || c.id; }), 'or') + '.';
          for (var k2 = 0; k2 < ac2.length; k2++) {
            var v2 = catQtyOf(ac2[k2].id);
            if (!/^\d+$/.test(v2) || parseInt(v2, 10) < 1) {
              return 'Enter how many ' + ac2[k2].label.toLowerCase() + ' ' + prod.unit_plural + ' you need' + (ac2.length > 1 ? ', or uncheck ' + ac2[k2].label : '') + '.';
            }
          }
          if (CUSTOM) {
            // 1–9 page: the COMBINED family total is capped at CUSTOM_MAX (Amber, Oct 3 2026). No tiers to check.
            var ct2 = parseInt(state.quantity, 10) || 0;
            if (ct2 > CUSTOM_MAX) {
              return 'Custom orders are 1–' + CUSTOM_MAX + ' pieces in total (you have ' + ct2 + '). For ' + (CUSTOM_MAX + 1) + ' or more, use Bulk Orders (' + (CUSTOM_MAX + 1) + '+).';
            }
            return '';
          }
          if (q.tierStatus === 'below_minimum') return 'Bulk orders start at ' + config.minimum_quantity + ' pieces' + (ac2.length > 1 ? ' in total' : '') + '. ' + underMinHint();
          if (q.tierStatus === 'unpriced') return 'We couldn’t price that quantity. Please contact us.';
          return '';
        }
        if (!/^\d+$/.test(String(state.quantity))) return 'Enter a whole number of pieces.';
        if (CUSTOM) {
          var cn = parseInt(state.quantity, 10);
          if (cn < 1) return 'Enter at least 1 piece.';
          if (cn > CUSTOM_MAX) return 'Custom orders are 1–' + CUSTOM_MAX + ' pieces. For ' + (CUSTOM_MAX + 1) + ' or more, use Bulk Orders (' + (CUSTOM_MAX + 1) + '+).';
          return '';
        }
        if (q.tierStatus === 'below_minimum') return 'Bulk orders start at ' + config.minimum_quantity + ' pieces. ' + underMinHint();
        if (q.tierStatus === 'unpriced') return 'We couldn’t price that quantity. Please contact us.';
        return '';
      case 3:
        var sel3 = selectedColors();
        var ac3 = activeCats();
        var multi3 = ac3.length > 1;
        if (multi3) {
          // Families: every chosen category needs at least one color of its own.
          var none3 = ac3.filter(function (cat) { return !sel3.some(function (c) { return c.token === cat.token; }); });
          if (none3.length) {
            return 'Choose at least one ' + listText(none3.map(function (c) { return c.label.toLowerCase(); }), 'and') +
              ' color, or select Another color and tell us what you need.';
          }
        }
        if (!sel3.length) return 'Choose at least one color, or select Another color and tell us what you need.';
        var typed = sel3.filter(function (c) { return c.custom; });
        var emptyTyped = typed.filter(function (c) { return !c.name; });
        if (emptyTyped.length) {
          if (multi3) return 'Tell us which ' + emptyTyped[0].categoryLabel.toLowerCase() + ' color you’d like' + (customsOf(emptyTyped[0].token).length > 1 ? ', or remove the empty one.' : '.');
          return typed.length > 1 ? 'Tell us each color you’d like, or remove the empty one.' : 'Tell us which color you’d like.';
        }
        for (var ti = 0; ti < typed.length; ti++) {
          var low = typed[ti].name.toLowerCase();
          var tcp = catProdOf(typed[ti]) || prod;
          var tcat = multi3 ? typed[ti].categoryLabel.toLowerCase() + ' ' : '';
          // A typed color is checked against its own category's listed colors (Youth Sage ≠ Adult Sage).
          var listed = (tcp.colors || []).filter(function (c) { return c.name.toLowerCase() === low; })[0];
          if (listed) return quoteName(listed.name) + ' is one of our listed ' + tcat + 'colors — select it above instead of typing it.';
          if (typed.some(function (c, j) { return j < ti && c.token === typed[ti].token && c.name.toLowerCase() === low; })) {
            return 'You entered ' + quoteName(typed[ti].name) + ' twice' + (multi3 ? ' for ' + typed[ti].categoryLabel : '') + ' — remove one.';
          }
        }
        return '';
      case 4:
        var rows4 = q.colorRows;
        if (!rows4.length) return 'Choose your colors on the Color step first.';
        var oneSize = !(prod.sizes || []).length;
        if (oneSize && rows4.length === 1) return '';
        var multi4 = multiCat();
        var rowName = function (row) { return (multi4 && row.categoryLabel ? row.categoryLabel + ' ' : '') + (row.name || 'Another color'); };
        for (var ci = 0; ci < rows4.length; ci++) {
          var m4 = sizesOf(rows4[ci].key);
          var off4 = rows4[ci].offered;
          var bad = oneSize ? [] : Object.keys(m4).filter(function (s) { return parseInt(m4[s], 10) > 0 && off4.indexOf(s) === -1; });
          if (bad.length) {
            return listText(bad) + (bad.length === 1 ? ' isn’t' : ' aren’t') + ' commonly available in ' + rowName(rows4[ci]) + ' — please choose a color that carries ' +
              (bad.length === 1 ? 'it' : 'them') + ' on the Color step.';
          }
        }
        var empty = rows4.filter(function (row) { return !row.total; });
        if (rows4.length > 1 && empty.length) {
          return 'Add ' + (oneSize ? 'a quantity' : 'sizes') + ' for ' + listText(empty.map(rowName), 'and') +
            ', or remove ' + (empty.length === 1 ? 'that color' : 'those colors') + ' on the Color step.';
        }
        if (multi4 && q.family) {
          // Each category's sizes must add up to that category's quantity (Youth 50 = its colors' sizes).
          for (var cr = 0; cr < q.categoryRows.length; cr++) {
            var crow4 = q.categoryRows[cr];
            if (crow4.sizeTotal !== crow4.quantity) return crow4.label + ' sizes must add up to ' + crow4.quantity + ' (currently ' + crow4.sizeTotal + ').';
          }
        }
        if (q.sizeTotal !== q.quantity) {
          return (rows4.length > 1 ? 'Your quantities across all colors must add up to ' : 'Your size quantities must add up to ') + q.quantity + ' (currently ' + q.sizeTotal + ').';
        }
        return '';
      case 5:   // Placement & Artwork: placement first, then the artwork for each print location
        if (!state.placementId || prod.placements.indexOf(state.placementId) === -1) return 'Choose where we should print.';
        if (!q.print.valid) return 'Choose a print size.';
        var slots = artSlots();
        if (!slots.length) return 'Choose where we should print.';
        for (var si = 0; si < slots.length; si++) { var problem = artworkProblem(slots[si], slots.length > 1); if (problem) return problem; }
        if (colorArtActive()) {
          if (busyEntries(COLOR_ART + '|inspo').length) return 'Your other artwork versions are still uploading — you can continue as soon as they finish.';
          if (!state.artColorNotes.trim()) return 'Tell us which garment colors get which design, or uncheck “I need different artwork for some garment colors.”';
        }
        return '';
      case 6:
        if (!(state.name || '').trim()) return 'Enter your name.';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((state.email || '').trim())) return 'Enter a valid email address.';
        if (((state.phone || '').replace(/\D/g, '')).length < 10) return 'Enter a phone number we can reach you at.';
        if (!state.needBy) return 'Choose your need-by date.';
        if (state.needBy < minDate()) return 'Choose a need-by date after today.';
        if (!state.fulfillment) return 'Choose shipping or local pickup.';
        var opt = fulfillmentOption();
        if (opt && opt.needs_address && !(state.address || '').trim()) return 'Enter the shipping address.';
        return '';
      default: return '';
    }
  }
  function firstInvalidStep() {
    for (var i = 1; i < TOTAL_STEPS; i++) { if (validateStep(i)) return i; }
    return 0;
  }
  function showError(n, msg) {
    var el = $('[data-step-error="' + n + '"]');
    if (el) { el.textContent = msg || ''; el.hidden = !msg; }
  }
  // While a step error is visible, keep it in sync with what the customer types so it
  // clears as soon as the step is valid (e.g. sizes now add up) instead of going stale.
  function refreshVisibleError() {
    var el = $('[data-step-error="' + state.step + '"]');
    if (el && !el.hidden) showError(state.step, validateStep(state.step));
  }
  function minDate() {
    var d = new Date(); d.setDate(d.getDate() + 1);
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }

  function goTo(n, focus) {
    if (n !== 4) sizeNotice = '';
    state.step = Math.max(1, Math.min(TOTAL_STEPS, n));
    state.maxStep = Math.max(state.maxStep, state.step);
    if (state.step === ART_STEP) warmUp();
    persist();
    renderAll();
    if (focus) {
      var panel = $('[data-step-panel="' + state.step + '"]');
      var heading = panel && panel.querySelector('h3');
      if (heading) { heading.setAttribute('tabindex', '-1'); heading.focus({ preventScroll: true }); }
      var top = $('[data-builder-top]').getBoundingClientRect().top + window.scrollY - 20;
      if (window.scrollY > top) window.scrollTo({ top: top, behavior: 'smooth' });
    }
  }

  function renderAll() {
    root.setAttribute('data-current-step', String(state.step));
    renderStepNav();
    renderProducts();
    renderQuantity();
    renderColors();
    renderSizes();
    renderPlacements();
    renderArtwork();
    syncBoundFields();
    renderRush();
    renderSummary();
    if (state.step === TOTAL_STEPS) renderReview();
  }

  /* ---------- Events ---------- */
  // "Price breakdown" <details> (Bulk): keep the customer's open/closed choice when the estimate re-renders.
  // 'toggle' doesn't bubble, so listen in the capture phase.
  root.addEventListener('toggle', function (e) {
    var d = e.target;
    if (d && d.matches && d.matches('details[data-price-breakdown]')) breakdownOpen = d.open;
  }, true);
  root.addEventListener('change', function (e) {
    var t = e.target;
    var focusCustom = false;
    var refocusCat = '';
    if (t.name === 'bulk_product') {
      selectProduct(t.value);
      state.maxStep = Math.min(state.maxStep, 2);
    } else if (t.name === 'bulk_categories') {
      // Families: which categories are on the order (config order). The family total follows the chosen categories.
      var on = {};
      $all('input[name="bulk_categories"]').forEach(function (i) { if (i.checked) on[i.value] = true; });
      state.categories = (familyCats() || []).map(function (c) { return c.id; }).filter(function (id) { return on[id]; });
      state.catsTouched = true;
      syncFamilyQuantity();
      pruneSizes();
      state.maxStep = Math.min(state.maxStep, Math.max(state.step, 2));
      refocusCat = t.value;
    } else if (t.name === 'bulk_colors') {
      var sk = splitKey(t.value);
      if (sk.base === '__other') {
        // Each category has its own "Another color" (e.g. Youth — Requested color: Sage).
        state.otherBy[sk.token] = t.checked;
        if (t.checked && !customsOf(sk.token).length) newCustomColor(sk.token);
        var firstCustom = customsOf(sk.token)[0];
        focusCustom = t.checked && firstCustom && !firstCustom.name ? firstCustom.id : false;
      } else {
        state.colors = state.colors.filter(function (n) { return n !== t.value; });
        if (t.checked) state.colors.push(t.value);
      }
      pruneSizes();
    } else if (t.hasAttribute('data-art-bycolor')) {
      state.artByColor = t.checked;
    } else if (t.name === 'bulk_placement') {
      state.placementId = t.value;
    } else if (t.hasAttribute('data-print-location')) {
      state.printSizes[t.getAttribute('data-print-location')] = t.value;
    } else if (t.hasAttribute('data-art-method')) {
      slotState(t.getAttribute('data-slot')).method = t.value;
    } else if (t.hasAttribute('data-art-same')) {
      state.artSame = t.checked;
    } else if (t.hasAttribute('data-art-later')) {
      slotState(t.getAttribute('data-slot')).later = t.checked;
      persist();
      refreshVisibleError();
      return;
    } else if (t.hasAttribute('data-up-input')) {
      var picked = Array.prototype.slice.call(t.files || []);
      t.value = '';
      if (picked.length) addFiles(t.getAttribute('data-up-input'), picked);
      return;
    } else if (t.hasAttribute('data-bind') && t.type === 'radio') {
      state[t.getAttribute('data-bind')] = t.value;
    } else { return; }
    showError(state.step, '');
    persist();
    renderAll();
    if (focusCustom) { var ci = byAttr('data-custom-color', focusCustom); if (ci) ci.focus({ preventScroll: true }); }
    if (refocusCat) {
      // The category picker is rebuilt when the chosen categories change — keep focus on the box just toggled.
      var cb = $all('input[name="bulk_categories"]').filter(function (i) { return i.value === refocusCat; })[0];
      if (cb) cb.focus({ preventScroll: true });
    }
  });
  // iOS keeps the keyboard open only while the typed-in field keeps focus: restore it if a re-render ever drops it.
  function keepFocus(el) {
    if (el && el !== document.activeElement && el.isConnected && root.contains(el) && typeof el.focus === 'function') {
      try { el.focus({ preventScroll: true }); } catch (err) {}
    }
  }
  root.addEventListener('input', function (e) {
    var t = e.target;
    if (t.hasAttribute('data-quantity')) {
      state.quantity = t.value.trim();
      // Families: the plain total box (hidden; still used with one category) sets that category's quantity.
      if (familyCats()) {
        if (state.categories.length === 1) state.catQty[state.categories[0]] = state.quantity;
        syncFamilyQuantity();
      }
      state.maxStep = Math.min(state.maxStep, Math.max(state.step, 2));
    } else if (t.hasAttribute('data-cat-qty')) {
      state.catQty[t.getAttribute('data-cat-qty')] = t.value.trim();
      syncFamilyQuantity();
      state.maxStep = Math.min(state.maxStep, Math.max(state.step, 2));
    } else if (t.hasAttribute('data-size-input')) {
      // Live: card subtotal, Total assigned, Step 4 estimate and sidebar all update on every keystroke.
      sizesOf(t.getAttribute('data-color-key') || '')[t.getAttribute('data-size-input')] = t.value.trim();
    } else if (t.hasAttribute('data-custom-color')) {
      var cc = state.customColors.filter(function (x) { return x.id === t.getAttribute('data-custom-color'); })[0];
      if (cc) cc.name = t.value;
      renderColors();
    } else if (t.hasAttribute('data-art-color-notes')) {
      state.artColorNotes = t.value;
      persist();
      refreshVisibleError();
      return;
    } else if (t.hasAttribute('data-art-field')) {
      var as = slotState(t.getAttribute('data-slot'));
      var field = t.getAttribute('data-art-field');
      if (field === 'canvaLink') as.canvaLink = t.value; else as.help[field] = t.value;
      persist();
      refreshVisibleError();
      return;
    } else if (t.hasAttribute('data-bind') && t.type !== 'radio') {
      state[t.getAttribute('data-bind')] = t.value;
      if (t.getAttribute('data-bind') === 'needBy') renderRush();
      if (t.getAttribute('data-bind') !== 'address') { persist(); renderSummary(); refreshVisibleError(); return; }
    } else { return; }
    persist();
    refreshVisibleError();
    renderQuantity();
    renderSizes();
    renderPlacements();
    renderSummary();
    syncBoundFields();
    keepFocus(t);
  });
  root.addEventListener('click', function (e) {
    var upBtn = e.target.closest('[data-up-choose],[data-up-remove],[data-up-cancel],[data-up-retry],[data-up-dismiss]');
    if (upBtn) { e.preventDefault(); uploaderAction(upBtn); return; }
    var zone = e.target.closest('[data-drop]');
    if (zone) { openPicker(zone.getAttribute('data-drop')); return; }
    var cardBtn = e.target.closest('[data-ccard-toggle]');
    if (cardBtn) { e.preventDefault(); toggleCard(cardBtn.getAttribute('data-ccard-toggle')); return; }
    var addBtn = e.target.closest('[data-custom-add]');
    if (addBtn) {
      e.preventDefault();
      var addTok = addBtn.getAttribute('data-custom-add') || '';
      if (customsOf(addTok).length < MAX_CUSTOM_COLORS) {
        var added = newCustomColor(addTok);
        state.otherBy[addTok] = true;
        persist();
        renderAll();
        var addedInput = byAttr('data-custom-color', added.id);
        if (addedInput) addedInput.focus();
      }
      return;
    }
    var removeBtn = e.target.closest('[data-custom-remove]');
    if (removeBtn) {
      e.preventDefault();
      var rid = removeBtn.getAttribute('data-custom-remove');
      var gone = state.customColors.filter(function (x) { return x.id === rid; })[0];
      var rtok = gone ? gone.category || '' : '';
      state.customColors = state.customColors.filter(function (x) { return x.id !== rid; });
      delete state.colorSizes[keyFor(rtok, 'custom:' + rid)];
      if (!customsOf(rtok).length) state.otherBy[rtok] = false;
      persist();
      renderAll();
      refreshVisibleError();
      var left = customsOf(rtok)[0];
      var nextInput = left ? byAttr('data-custom-color', left.id) : null;
      if (nextInput) nextInput.focus({ preventScroll: true });
      return;
    }
    var start = e.target.closest('[data-start-product]');
    if (start) {
      e.preventDefault();
      selectProduct(start.getAttribute('data-start-product'));
      state.maxStep = Math.max(state.maxStep, 2);
      goTo(2, false);
      $('[data-builder-top]').scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    var go = e.target.closest('[data-goto]');
    if (go && !go.disabled) {
      var target = Number(go.getAttribute('data-goto'));
      if (target > state.step) {
        for (var i = state.step; i < target; i++) { var m = validateStep(i); if (m) { goTo(i, true); showError(i, m); return; } }
      }
      goTo(target, true);
      return;
    }
    if (e.target.closest('[data-next]')) {
      var msg = validateStep(state.step);
      if (msg) { showError(state.step, msg); return; }
      showError(state.step, '');
      goTo(state.step + 1, true);
      return;
    }
    if (e.target.closest('[data-prev]')) { goTo(state.step - 1, true); }
  });

  // Shopify's contact-form hCaptcha listens for submit events, so Enter in a field must never
  // trigger implicit submission before the Review step. Treat it as Continue instead.
  form.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON') return;
    if (e.target.tagName !== 'INPUT') return;
    e.preventDefault();
    if (state.step === TOTAL_STEPS) return;
    var stepMsg = validateStep(state.step);
    if (stepMsg) { showError(state.step, stepMsg); return; }
    goTo(state.step + 1, true);
  }, true);

  // Drag & drop onto an uploader. A file dropped anywhere else on the Artwork step is ignored
  // (instead of the browser opening the image and leaving the page).
  function dropZone(e) { return e.target && e.target.closest ? e.target.closest('[data-drop]') : null; }
  function hasFiles(e) { return !!(e.dataTransfer && Array.prototype.indexOf.call(e.dataTransfer.types || [], 'Files') !== -1); }
  ['dragenter', 'dragover'].forEach(function (type) {
    root.addEventListener(type, function (e) {
      var z = dropZone(e);
      if (!z || !hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      z.classList.add('is-over');
    });
  });
  root.addEventListener('dragleave', function (e) {
    var z = dropZone(e);
    if (z && !z.contains(e.relatedTarget)) z.classList.remove('is-over');
  });
  root.addEventListener('drop', function (e) {
    var z = dropZone(e);
    if (!z) return;
    e.preventDefault();
    z.classList.remove('is-over');
    addFiles(z.getAttribute('data-drop'), e.dataTransfer && e.dataTransfer.files);
  });
  window.addEventListener('dragover', function (e) {
    if (state.step === ART_STEP && hasFiles(e) && !dropZone(e)) { e.preventDefault(); e.dataTransfer.dropEffect = 'none'; }
  });
  window.addEventListener('drop', function (e) {
    if (state.step === ART_STEP && hasFiles(e) && !dropZone(e)) e.preventDefault();
  });
  window.addEventListener('beforeunload', function (e) {
    if (Object.keys(uploads).some(function (k) { return busyEntries(k).length > 0; })) { e.preventDefault(); e.returnValue = ''; }
  });

  /* ---------- Submission (native Shopify contact form; no payment) ---------- */
  function requestNumber() {
    var d = new Date();
    var stamp = String(d.getFullYear()).slice(2) + String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0');
    var chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    var rand = '';
    for (var i = 0; i < 4; i++) rand += chars.charAt(Math.floor(Math.random() * chars.length));
    return 'CH-BULK-' + stamp + '-' + rand;
  }
  function buildSummaryText(q, number) {
    var prod = q.product;
    var multi = q.colorRows.length > 1;
    var fam = q.family && (q.categoryRows || []).length > 1;   // 2+ family categories (Adult + Youth …), both pages
    var pname = orderProductName(prod);
    var CUSTOM_NOTE = ' (customer-entered color — confirm color and size availability with supplier)';
    var lines = [
      (CUSTOM ? 'CUSTOM ORDER REQUEST (1–' + CUSTOM_MAX + ' pieces) ' : 'BULK ORDER REQUEST ') + number,
      'Pricing version: ' + config.version,
      '',
      'Product: ' + (fam ? pname + ' (' + q.categoryRows.map(function (c) { return c.label; }).join(', ') + ')' : pname),
      'Quantity: ' + (fam ? q.quantity + ' total (' + categoryQtyText(q) + ')' + (CUSTOM ? '' : ' — priced as one ' + q.quantity + '-piece order')
        : q.quantity + (multi ? ' total across ' + q.colorRows.length + ' colors' + (CUSTOM ? '' : ' (priced as one ' + q.quantity + '-piece order)') : '')),
      'Colors: ' + q.colorRows.map(function (row) {
        return (fam ? row.categoryLabel + ' — ' + (row.custom ? 'Requested color: ' : '') : '') + row.name + (row.custom ? ' (customer-entered)' : '');
      }).join(', '),
      '',
      'COLORS & SIZES — ' + pname + ' — ' + q.quantity + ' pieces'
    ];
    if (fam) {
      // Families: category → color → sizes, never flattened: "Adult — Black — 30" then "S: 10 · M: 10 · L: 10".
      q.categoryRows.forEach(function (cr) {
        lines.push(cr.label.toUpperCase() + ' — ' + cr.quantity + ' (' + cr.name + ')');
        cr.colorRows.forEach(function (row) {
          lines.push(cr.label + ' — ' + (row.custom ? 'Requested color: ' : '') + row.name + ' — ' + row.total + (row.custom ? CUSTOM_NOTE : ''));
          lines.push('  ' + (sizeLine(row) || 'No sizes entered'));
        });
      });
    } else {
      // One block per garment color (never flattened): "Black — 50" then "S: 5 · M: 15 · …".
      q.colorRows.forEach(function (row) {
        lines.push(row.name + ' — ' + row.total + (row.custom ? CUSTOM_NOTE : ''));
        lines.push('  ' + (sizeLine(row) || 'No sizes entered'));
      });
    }
    lines.push('', 'Print placement(s): ' + (q.print.placement ? q.print.placement.label : ''));
    if (q.print.auto) Array.prototype.push.apply(lines, autoPrintEmailLines(q));
    else lines.push('Print size: ' + printSizeText(q));
    lines.push('Artwork status: ' + artworkList().map(function (a) { return a.slot.short + ' — ' + (INTERNAL_STATUS[a.status] || 'NOT PROVIDED'); }).join('; '));
    Array.prototype.push.apply(lines, artworkEmailLines(number));
    var rq = rushNow(q);
    lines.push('', !EST ? 'PRICING' : 'ESTIMATE');
    if (!EST) {
      // v11: the 1–9 page shows "From" starting prices (display only), never a quote or estimate.
      lines.push('Custom order (1–' + CUSTOM_MAX + ' pieces) — the customer saw starting prices only, no quote or estimate. Quote it manually and send a Shopify invoice.');
    } else if (q.customQuote) {
      lines.push('Custom Volume Pricing (' + config.custom_quote_threshold + '+ pieces) — no automatic price shown to customer.');
      if (rq) lines.push('Rush service: REQUESTED — need-by is ' + rq.days + ' business day(s) away; manual rush review (no automatic fee at ' + config.custom_quote_threshold + '+ pieces).');
    } else {
      var ef = figures(q);
      if (CUSTOM) lines.push('Custom order (1–' + CUSTOM_MAX + ' pieces) — the customer saw this live estimate at the 1–' + CUSTOM_MAX + ' prices. Confirm the final price and send a Shopify invoice.');
      lines.push('Customer was shown: ' + (ef.average ? 'Average estimated price ' : 'Estimated price ') + fmt(ef.eachCents) + ' each · Estimated order total ' + fmt(ef.totalCents) + ' (before shipping/tax)');
      lines.push('Internal breakdown (not shown to the customer):');
      if (fam) {
        // One tier from the combined family total; each category at its own rate in that tier.
        lines.push('  Base price per piece by category (' + (CUSTOM ? '1–' + CUSTOM_MAX + ' custom order prices' : tierRange(q.tier) + ' tier from the combined ' + q.quantity + ' pieces') + '):');
        q.categoryRows.forEach(function (cr) {
          lines.push('    ' + cr.label + ': ' + fmt(cr.unitCents) + ' × ' + cr.quantity + ' = ' + fmt(cr.baseCents));
        });
        lines.push('    Total base: ' + fmt(q.baseCents));
      } else {
        lines.push('  Base price per piece: ' + fmt(q.unitBaseCents) + ' (' + (CUSTOM ? '1–' + CUSTOM_MAX + ' custom order price' : tierRange(q.tier) + ' tier') + ') × ' + q.quantity + ' = ' + fmt(q.baseCents));
      }
      if (!q.extendedLines.length) {
        lines.push('  Extended-size charges: None');
      } else {
        lines.push('  Extended-size charges by color:');
        q.colorRows.forEach(function (row) {
          lines.push('    ' + (fam ? row.categoryLabel + ' — ' : '') + row.name + ': ' + (row.extendedLines.length
            ? row.extendedLines.map(function (l) { return l.size + ' × ' + l.qty + ' @ ' + fmt(l.eachCents); }).join(', ') + ' = ' + fmt(row.extendedCents) : 'None'));
        });
        lines.push('    Total extended-size charges: ' + fmt(q.extendedCents));
      }
      if (q.autoPrint) {
        var charged = q.autoPrint.rows.filter(function (l) { return l.cents > 0; });
        lines.push('  Print-size charges by garment size: ' + (charged.length ? charged.map(function (l) {
          return l.location + ' ' + (fam && l.categoryLabel ? l.categoryLabel + ' ' : '') + l.size + ' (' + l.label + ') ' + l.qty + ' × ' + fmt(l.eachCents) + ' = ' + fmt(l.cents);
        }).join('; ') + ' — total ' + fmt(q.autoPrint.cents) : 'None'));
      }
      lines.push('  Additional print charges: ' + (q.print.lines.length ? q.print.lines.map(function (l) { return l.label + ' ' + fmt(l.cents); }).join(', ') + ' = ' + fmt(q.print.perItemCents) + '/item × ' + q.quantity + ' = ' + fmt(q.print.perItemCents * q.quantity) : 'None'));
      lines.push('  Rush service fee: ' + (rq && !rq.manual ? fmt(rq.cents) + ' (need-by is ' + rq.days + ' business day(s) away; ' + tierRange(rq.tier) + ' pieces)' : 'None'));
      lines.push('Estimated order total: ' + fmt(ef.totalCents) + (rq && !rq.manual ? ' (includes ' + fmt(rq.cents) + ' rush service fee)' : '') + ' (before shipping/tax)');
    }
    var opt = fulfillmentOption();
    lines.push('', 'CUSTOMER',
      'Name: ' + state.name.trim(),
      'Business/Organization: ' + ((state.organization || '').trim() || '—'),
      'Email: ' + state.email.trim(),
      'Phone: ' + state.phone.trim(),
      'Need-by date: ' + state.needBy + (rq ? ' — RUSH (' + rq.days + ' business day' + (rq.days === 1 ? '' : 's') + ' away)' : ''),
      'Shipping or pickup: ' + (opt ? opt.label : state.fulfillment));
    if (opt && opt.needs_address) lines.push('Shipping address: ' + state.address.trim());
    lines.push('Additional notes: ' + ((state.notes || '').trim() || '—'));
    return lines.join('\n');
  }
  function setHidden(name, value) {
    var box = $('[data-hidden-fields]');
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'contact[' + name + ']';
    input.value = value;
    box.appendChild(input);
  }

  form.addEventListener('submit', function (e) {
    if (state.step !== TOTAL_STEPS) {
      // Enter key in an earlier step acts as Continue, never as an early submission.
      e.preventDefault();
      var stepMsg = validateStep(state.step);
      if (stepMsg) { showError(state.step, stepMsg); return; }
      goTo(state.step + 1, true);
      return;
    }
    var bad = firstInvalidStep();
    if (bad) { e.preventDefault(); goTo(bad, true); showError(bad, validateStep(bad)); return; }
    var q = currentQuote();
    // Uploads that belong to a location/method no longer on the order: stop them, trash their files.
    Object.keys(uploads).forEach(function (k) { (uploads[k] || []).slice().forEach(dropEntry); });
    pruneUnusedFiles();
    var number = ensureRequestNumber();   // same number the uploaded files are filed under in Drive
    persist();
    $('[data-hidden-fields]').innerHTML = '';
    setHidden('name', state.name.trim());
    setHidden('email', state.email.trim());
    setHidden('phone', state.phone.trim());
    setHidden('Request type', CUSTOM ? 'Custom order request (1–' + CUSTOM_MAX + ')' : 'Bulk order request');
    setHidden('Request number', number);
    // Families: the category split, e.g. "Adult 50 · Youth 50" (the body keeps the full category → color → size detail).
    if (q.family && q.categoryRows.length) setHidden('Categories', categoryQtyText(q));
    var hf = figures(q);
    var hr = rushNow(q);
    if (EST) {
      setHidden('Estimated order total', q.customQuote ? 'Custom Volume Pricing' : fmt(hf.totalCents));
      if (!q.customQuote) setHidden('Estimated price each', (hf.average ? 'Average ' : '') + fmt(hf.eachCents));
      if (Engine.rushEnabled(config)) {
        setHidden('Rush service', !hr ? 'No' : hr.manual ? 'Yes — manual rush review (' + config.custom_quote_threshold + '+ pieces)'
          : 'Yes — ' + fmt(hr.cents) + ' fee (' + hr.days + ' business days to need-by)');
      }
    }
    setHidden('body', buildSummaryText(q, number));
    try {
      sessionStorage.setItem(REQUEST_KEY, JSON.stringify({
        number: number, name: state.name.trim(),
        placement: q.print.placement ? q.print.placement.label : '',
        artwork: artworkList().map(function (a) { return { label: a.slot.label, short: a.slot.short, status: a.status }; })
          .concat(colorArtActive() && slotState(COLOR_ART).inspo.length ? [{ label: 'Other color versions', short: 'color versions', status: 'uploaded' }] : []),
        summary: orderProductName(q.product) + ' × ' + q.quantity + (multiCat() ? ' (' + categoryQtyText(q) + ')' : '') + (colorsText() ? ' · ' + colorsText() : '') + ' · ' +
          (!EST ? 'We’ll email your price' : q.customQuote ? 'Custom Volume Pricing' : 'Estimated order total ' + fmt(hf.totalCents) + ' (before shipping & tax)')
      }));
    } catch (err) {}
    var btn = $('[data-submit]');
    if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
  });

  // Read-only preview of the internal request email body (used by the validator; sends nothing).
  window.CarryHopeBulkRequestPreview = function () { var pq = currentQuote(); return pq.product ? buildSummaryText(pq, state.requestNumber || 'CH-BULK-PREVIEW') : ''; };

  /* ---------- Init ---------- */
  renderFulfillment();
  $('[data-need-by]').min = minDate();
  ensureCategories();
  pruneSizes();
  sizeNotice = '';
  if (form.querySelector('[data-form-errors]')) { state.step = TOTAL_STEPS; state.maxStep = TOTAL_STEPS; }
  if (state.step > 1 && firstInvalidStep() && firstInvalidStep() < state.step) state.step = firstInvalidStep();
  if (state.step === ART_STEP) warmUp();
  renderAll();
  root.classList.add('is-ready');
})();
